import { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo, useSyncExternalStore, type MutableRefObject } from 'react'
import { useBrand } from '@papan/brand/react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate as useRouterNavigate, useSearchParams } from 'react-router-dom'
import { Upload, FolderUp, Folder, FolderPlus, Bug, LayoutGrid, MoreVertical, Trash2, Pencil, Search, Wifi, WifiOff, ExternalLink, Download, FolderArchive, Copy, Scissors, Clipboard, ChevronUp, ChevronDown, ArrowUpDown, Loader2, FolderOpen, Users, Info, Share2, Link2, Lock, UsersRound, User, Component, AppWindow as AppWindowIcon, Wrench, Palette, AlertTriangle } from 'lucide-react'
import { useFiles, type FileItem, type FileType, type SortField } from './useFiles'
import { useUploadService } from './useUploadService'
import { useDeleteRunner, folderBeingDeletedText, reservedNameText, engineDbOwnedText, engineDbInvalidText, engineDbBusyText, extractDbInvalidText } from './useDeleteRunner'
import { pasteProgressTotal } from './paste-progress-total'
import { pasteItems, pasteUndoRecord } from '@papan/files/client/paste'
import { replaceClearApiAs } from '@papan/files/client/replace-clear'
import { resolveExtractFileConflicts } from './extractConflictPrompts'
import { pasteToastText } from './pasteToast'
import { useRealtime } from '@papan/realtime/client'
import { useToast, useConfirm, useUndo, useConflict, useOrgMemberNames, useTheme, getThemeClass, useWriteStatus, useAnchoredPosition, FileIcon, fileIconProps, MiniappIcon, miniappIconProps, Tooltip, nameFromEmail, type ConflictResolution, type ConflictOperation, PDAppIcon, useDraftState, useSwapScroll, useWhenShown } from '@papan/core-ui/ui'
import { captureUndoContext, type UndoCapture } from '@papan/core-ui/ui/undoCapture'
import { ManifestProblemBadge } from './ManifestProblemBadge'
import { usePageTitle } from '@papan/brand/react'
import { AccessTooltip, AccessAvatar, nameBreakdown, type AccessTooltipRow } from './AccessSummaryBits'
import { packageIsHallwayShell as isHallwayPackage } from './hallway-package'
import { FileInfoDialog } from './FileInfoDialog'
import { DriveBreadcrumb } from './DriveBreadcrumb'
import { MutedEndings, nameParts } from './nameParts'
import { nameOwnSpace } from './locations'
import { SearchResults } from './home/SearchResultsView'
import { useDebounced } from './home/hooks'
import { usePendingPointerGate, isPendingPointer } from './PendingPointerGate.js'
import { filesApi, inFlightDeletes, type ExtractPartial, type ConflictStrategy, type FolderAccessSummary, type FileListItem, reportListedRowRefused } from '@papan/files/client'
import { fileKeys, loadPermission } from '@papan/files/client/queries'
import { useQueryClient } from '@tanstack/react-query'
import { request, getActiveTenantId, isSessionEndedError, pinnedBatch, isPinnedBatchIdentityLost, type PinnedBatch } from '@papan/api-client'
import type { FolderWorkspaceAccess, StructuralFolderKind } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { useApproveCode } from './useApproveCode'
import { isPointerRow, isChainPointerRow, pointerKindOf, isSharedPointer, canWriteFor, createAccessBurst, changesViewerAccess } from '@papan/types'
import { showsOutboundShareBadge } from './share-badge'
import { TENANT_WRITE_FROZEN_CODE, damagedMessageFrom, storageRefusalText, areExtensionsCompatible, isLiveMountRow, isProjectFolder, isMiniappFolder, presentsAsMiniapp, presentsAsProject, supportsDebugLog, projectFolderName, projectFolderExt, stripProjectFolderExt, stripMiniappFolderExt, stripPointerSuffix, parseMiniappIcon, parseMiniappIconRev, isPdAppContainerName, structuralFolderKind, structuralFolderBlocksShare } from '@papan/types'

import { createDragImage, setActiveDragFolderIds, clearActiveDrag, isCircularMove, useFolderAncestors, offerDragToApps } from '@papan/files/client/dnd'
import { startPapanDrag, isPapanDrag, readPapanDrag, peekPapanDragFolderIds } from '@papan/core-ui/ui/papanDrag'
import { WhoHasAccessDialog } from './WhoHasAccessDialog'
import { ShareInternallyDialog } from './ShareInternallyDialog'
import { MiniappIconEditorDialog } from './MiniappIconEditorDialog'
import { DebugLogViewer } from './DebugLogViewer'
import { useNewProject } from './NewProjectProvider'
import { ShareDialog } from '@papan/org-connections/ui'
import { ChainEscapeDialog, type ChainEscapeInfo } from './ChainEscapeDialog'
import { PointerDownloadDialog, FolderLinkedDownloadDialog, planPointerDownload, routePointerDownload, type PointerRowLike } from './PointerDownloadDialog'
import { shareKindForFile, canShareMiniApp, miniappAncestors, type CrumbLike } from '@papan/share-links/client'
import { ShareLinksDialog, useMiniAppShare } from '@papan/share-links/ui'
import { canManageAccessFor } from './fileActions'
import { confirmCrossTenantPaste, driveLabel, resolveDriveLabel } from './crossTenantPaste'
import { usePartitionGate, moveResultMessage, isMountId, clearReplaceTarget, chainEscapeRecallUpdate, moveAsVersionUndo, existingFromConflicts, moveReplaceStep } from './protectedBranchFlow'
import { moveItemsInto } from './moveItemsInto'
import { moveErrorText } from './moveErrorText'
import { clearSpentCut } from './spentCut'
import { withMoveProgress, withDialogMoveProgress } from './moveProgress'
import { dragMoveUndoParent } from './dragMoveUndo'

/** The one id the "a paste is still running" notice shows under — a repeated press replaces it, never stacks. */
const PASTE_STILL_RUNNING_TOAST_ID = 'drive-paste-still-running'

// Sentinel for root folder in breadcrumb drop targets
const DROP_ROOT = '__ROOT__'


interface ClipboardData {
  items: { id: string; name: string; isFolder: boolean; parentId: string | null }[]
  isCut: boolean
  tenantId?: string
  /**
   * The user's effective org email AT COPY TIME in the source tenant.
   * For cross-tenant paste we must resolve source-tenant permissions
   * with the identity the user was actually using when they selected
   * the files — not the destination tenant's active email, which is
   * what `auth.user.email` defaults to server-side. Multi-email users
   * who are owner via one address and member via another were getting
   * the OWNER's view leaked into the paste destination otherwise.
   */
  orgEmail?: string
  /** (#752) How the SOURCE drive is named, captured at copy time — the
   *  cross-tenant paste confirmation quotes it after the user has switched away. */
  sourceLabel?: string
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '--'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

function formatDate(isoString: string): string {
  if (!isoString) return '--'
  const date = new Date(isoString)
  const now = new Date()
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffSec < 60) return relativeFormatter.format(0, 'second')
  if (diffSec < 3600) return relativeFormatter.format(-Math.floor(diffSec / 60), 'minute')
  if (diffSec < 86400) return relativeFormatter.format(-Math.floor(diffSec / 3600), 'hour')
  if (diffSec < 604800) return relativeFormatter.format(-Math.floor(diffSec / 86400), 'day')

  return date.toLocaleDateString(undefined, {
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getFileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  if (dot === -1 || dot === 0) return ''
  return name.substring(dot + 1).toUpperCase()
}


function SortHeader({ field, label, sortBy, sortDir, toggleSort, className = '' }: {
  field: SortField
  label: string
  sortBy: SortField
  sortDir: 'asc' | 'desc'
  toggleSort: (field: SortField) => void
  className?: string
}) {
  const active = sortBy === field
  return (
    <button
      onClick={() => toggleSort(field)}
      className={`flex items-center gap-1 hover:themed-text cursor-pointer transition-colors ${active ? 'themed-text' : ''} ${className}`}
    >
      <span>{label}</span>
      {active && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
    </button>
  )
}

const FILE_BADGE_STYLES: Partial<Record<FileType, string>> = {
  workpapan: 'file-badge-workspace',
  shared: 'file-badge-shared',
  // Spreadsheets
  csv: 'file-badge-csv',
  tsv: 'file-badge-tsv',
  xlsx: 'file-badge-xlsx',
  xls: 'file-badge-xlsx',
  ods: 'file-badge-xlsx',
  parquet: 'file-badge-parquet',
  // Documents
  docx: 'file-badge-docx',
  doc: 'file-badge-docx',
  odt: 'file-badge-docx',
  rtf: 'file-badge-docx',
  pdf: 'file-badge-pdf',
  // Presentations
  pptx: 'file-badge-pptx',
  ppt: 'file-badge-pptx',
  odp: 'file-badge-pptx',
  // Code / markup
  json: 'file-badge-json',
  md: 'file-badge-md',
  html: 'file-badge-html',
  xml: 'file-badge-muted',
  yaml: 'file-badge-muted',
  code: 'file-badge-code',
  // Text
  txt: 'file-badge-muted',
  log: 'file-badge-muted',
  pmeta: 'file-badge-muted',
  ppn: 'file-badge-ppn',
  // Images
  png: 'file-badge-image',
  jpg: 'file-badge-image',
  gif: 'file-badge-image',
  webp: 'file-badge-image',
  svg: 'file-badge-image',
  // Video
  mp4: 'file-badge-video',
  webm: 'file-badge-video',
  mov: 'file-badge-video',
  // Audio — one colour per format (shared warm palette, see index.css)
  mp3: 'file-badge-mp3',
  wav: 'file-badge-wav',
  m4a: 'file-badge-m4a',
  aac: 'file-badge-aac',
  flac: 'file-badge-flac',
  aiff: 'file-badge-aiff',
  opus: 'file-badge-opus',
  // Archive
  zip: 'file-badge-zip',
  // Database
  db: 'file-badge-db',
  // Special
  sqlparz: 'file-badge-parquet',
}

function FileTypeBadge({ type, name }: { type: FileType; name?: string }) {
  const cls = FILE_BADGE_STYLES[type]
  if (!cls || type === 'folder') return null

  // The project badge follows the folder's actual extension — `.project`
  // (canonical) or the `.workpapan` / `.workspace` aliases — rather than the
  // internal `workpapan` type tag, so a `Foo.project` folder reads "PROJECT"
  // and a legacy `Foo.workpapan` folder reads "WORKPAPAN".
  const label = type === 'workpapan'
    ? (projectFolderExt(name ?? '')?.slice(1).toUpperCase() ?? 'PROJECT')
    : type === 'code'
      // Unified `code` type → show the file's real extension (TS / PY / SQL …).
      ? (getFileExtension(name ?? '') || 'CODE')
      : type.toUpperCase()

  return (
    <span className={`text-xs px-1.5 py-0.5 rounded ${cls}`}>
      {label}
    </span>
  )
}

// Access-summary presentation (AccessTooltip / nameBreakdown / AccessAvatar) is
// shared with the Projects / MiniApps card access line — one source in
// ./AccessSummaryBits. Imported at the top of this file.

/**
 * The Access column cell — the per-row companion to the breadcrumb access bar.
 * EMPTY for the normal case (a row everyone can see, no narrowing / exposure);
 * it only speaks up when a row is RESTRICTED (a muted lock + who can get in)
 * or EXPOSED (🔗 share links / ⌁ outside orgs). Same shape vocabulary as the
 * bar, in miniature. Opens Who-has-access when the viewer can manage the row.
 *
 * Compact ladder: the grantee avatars drop below T1 (a "+N" absorbs them);
 * the lock + chips stay. The whole column hides below T3 (parent carries
 * `drive-hide-t3`), where the name-cell lock badge takes over.
 */
function AccessCell({ file, isOrg, onManage, t }: {
  file: FileItem
  isOrg?: boolean
  onManage?: (file: FileItem) => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  // Same resolution as the project cards: the member list first, the email's
  // local-part only as a fallback. Without it one face read a real
  // name while every grantee beside it read a guess ("Kok Kok, Kok2").
  const { data: memberNames } = useOrgMemberNames()
  const nameFor = (email: string) => memberNames?.get(email.trim().toLowerCase()) || nameFromEmail(email)
  const summary = file.accessSummary
  const floor = file.floor // 'member' | 'admin' | undefined
  const isAdmin = floor === 'admin'
  const isMember = floor === 'member'
  const links = summary?.links ?? 0
  const outsiders = summary?.outsiders ?? 0
  const people = summary?.people ?? []
  const peopleCount = summary?.peopleCount ?? 0
  const teams = summary?.teams ?? []
  const teamCount = summary?.teamCount ?? 0

  // Empty for normal (Viewer+). The column speaks up when the floor is above
  // viewer (Member+ / Limited) or the row is exposed (links / outsiders).
  const special = !!floor || links > 0 || outsiders > 0
  if (!isOrg || !special) return null

  // Admin-floor is the "locked" state — the top team leads, grantee avatars
  // follow (space permitting) and fold into a +N count when narrow. Only
  // principals that HOLD access appear — never the row's creator, who holds
  // nothing by having created it (#1037);
  // no named grantees → a "Limited" label. Member-floor shows "Member+".
  // Not "Admin+" (#787: the row floor folds the owner-only case into 'admin',
  // where drive admins are cut too) and not "Restricted" (the name of the
  // Restricted FLAG — a folder is also locked by role grants set to No access
  // without it). No role claim; the tooltip points at the list.
  const hasGrantees = teamCount > 0 || peopleCount > 0
  const shown = isAdmin ? people.slice(0, teams.length > 0 ? 1 : 2) : []
  const teamsBeyond = Math.max(0, teamCount - (teams.length > 0 ? 1 : 0))
  const wideExtra = teamsBeyond + Math.max(0, peopleCount - shown.length)
  const narrowExtra = teamsBeyond + peopleCount

  // Tooltip = a plain who-has-access breakdown (icon-prefixed lines).
  const tipRows: AccessTooltipRow[] = []
  if (teamCount > 0) tipRows.push({ icon: <UsersRound size={12} className="text-blue-300" />, text: nameBreakdown(teams.map(x => x.name), teamCount, 'teams', t) })
  if (peopleCount > 0) tipRows.push({ icon: <User size={12} className="text-zinc-300" />, text: nameBreakdown(people.map(nameFromEmail), peopleCount, 'people', t) })
  if (links > 0) tipRows.push({ icon: <Link2 size={12} className="text-sky-300" />, text: t('driveView.access.links', { count: links }) })
  if (outsiders > 0) tipRows.push({ icon: <Share2 size={12} className="text-purple-300" />, text: t('driveView.access.outsiders', { count: outsiders }) })
  if (tipRows.length === 0 && floor) tipRows.push({ icon: floor === 'admin' ? <Lock size={12} className="text-zinc-300" /> : <User size={12} className="text-zinc-300" />, text: t(floor === 'admin' ? 'driveView.access.limitedTip' : 'driveView.access.memberPlus') })

  const inner = (
    <>
      {isAdmin && <Lock size={12} className="themed-text-muted shrink-0" />}
      {/* Member+ is the lowest-priority signal — no icon. It hides FIRST when
          the list compacts, but ONLY when it's sharing the cell with other
          items (links / outsiders); if it's the sole content, keep it. */}
      {isMember && <span className={`font-medium ${(links > 0 || outsiders > 0) ? 'drive-hide-t1' : ''}`}>{t('driveView.access.memberPlus')}</span>}
      {isAdmin && !hasGrantees && <span className="font-medium">{t('driveView.access.limited')}</span>}
      {/* Team badge leads the admin-floor facepile — blue pill + UsersRound. */}
      {isAdmin && teams.length > 0 && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ws-team-badge min-w-0 shrink">
          <UsersRound size={10} className="shrink-0" />
          <span className="truncate max-w-[3.5rem]">{teams[0].name}</span>
        </span>
      )}
      {/* Grantee avatars follow the team, wide only. */}
      {shown.length > 0 && (
        <span className="drive-hide-t1 inline-flex items-center">
          {shown.map((email, i) => (
            <AccessAvatar key={email} seed={email} label={nameFor(email)} overlap={i > 0} sm />
          ))}
        </span>
      )}
      {wideExtra > 0 && (
        <span className="drive-hide-t1 items-center text-[9px] font-medium px-1 py-0.5 rounded-full themed-bg-tertiary themed-text-muted">+{wideExtra}</span>
      )}
      {narrowExtra > 0 && (
        <span className="drive-show-t1 items-center text-[9px] font-medium px-1 py-0.5 rounded-full themed-bg-tertiary themed-text-muted">+{narrowExtra}</span>
      )}
      {links > 0 && (
        <span className="ws-access-link inline-flex items-center gap-0.5 shrink-0">
          <Link2 size={11} />{links}
        </span>
      )}
      {outsiders > 0 && (
        <span className="ws-access-outsider inline-flex items-center gap-0.5 shrink-0">
          <Share2 size={11} />{outsiders}
        </span>
      )}
    </>
  )

  return (
    <Tooltip content={<AccessTooltip rows={tipRows} />} always className="inline-flex max-w-full">
      {onManage ? (
        <button
          type="button"
          data-testid="access-cell"
          onClick={(e) => { e.stopPropagation(); onManage(file) }}
          onDoubleClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 -mr-1 hover:themed-bg-tertiary transition-colors cursor-pointer text-[11px] themed-text-muted"
        >
          {inner}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1 px-1 py-0.5 -mr-1 text-[11px] themed-text-muted">{inner}</span>
      )}
    </Tooltip>
  )
}

/**
 * The mini-app row glyph — a 24px squircle rendered by the shared
 * `MiniappIcon` from the row's stored spec (or its name-derived default).
 * Slightly larger than the 20px FileIcon glyphs: a filled tile reads
 * smaller than a line glyph at equal px, and the size gap reinforces the
 * "this is an app" contrast. The custom-icon tile + APP chip stay RESERVED
 * for mini-apps (DESIGN_DOC §10.3); regular projects keep the purple
 * project FileIcon.
 */
function MiniappTile({ id, name, metadataJson }: { id: string; name: string; metadataJson?: string | null }) {
  const spec = useMemo(() => parseMiniappIcon(metadataJson), [metadataJson])
  const rev = useMemo(() => parseMiniappIconRev(metadataJson), [metadataJson])
  return <MiniappIcon spec={spec} name={name} size={24} refId={id} rev={rev} className="shrink-0" />
}

/**
 * Sort dropdown at the list header's right end — the home of sort fields
 * whose column no longer exists (Type, dropped in the 2026-07-10 redesign)
 * and the future home of density/grouping. Visible columns keep their
 * click-to-sort headers; this menu is the superset.
 */
function SortMenu({ sortBy, sortDir, toggleSort, t }: {
  sortBy: SortField
  sortDir: 'asc' | 'desc'
  toggleSort: (field: SortField) => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [open])
  const fields: Array<{ f: SortField; label: string }> = [
    { f: 'name', label: t('driveView.header.name') },
    { f: 'modified', label: t('driveView.header.dateModified') },
    { f: 'size', label: t('driveView.header.size') },
    { f: 'type', label: t('driveView.header.type') },
  ]
  return (
    <div ref={ref} className="relative w-10 flex justify-end">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded themed-text-muted hover:themed-text transition-colors cursor-pointer"
        title={t('driveView.sort.title')}
        data-testid="drive-sort-menu"
      >
        <ArrowUpDown size={13} />
      </button>
      {open && (
        <div className="absolute right-0 top-6 ws-menu z-20 min-w-[160px] normal-case tracking-normal">
          {fields.map(({ f, label }) => (
            <button
              key={f}
              type="button"
              onClick={() => toggleSort(f)}
              className="ws-menu-item"
            >
              <span className="flex-1 text-left">{label}</span>
              {sortBy === f && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The access summary — its own borderless line under the breadcrumb (the
 * header block owns the single border), so a long path never crowds it.
 * Answers "who can get into THIS folder" at a glance: the access FLOOR,
 * escalating to a lock + grantees only when members are shut out:
 *
 *   Viewer+   everyone can view (the normal shape) — quiet, no lock
 *   Member+   viewer excluded, member+ can — quiet, no lock
 *   🔒 Dev Team & 2 more teams  (+ member avatars) — members locked out
 *   🔒 Limited  role-only fallback when there are no explicit grants
 *
 * Plus "exposed" chips (share links / shared out). The chip itself opens
 * Who-has-access — no separate button.
 */
function AccessBar({ folderId, isOrg, onManage, t }: {
  folderId: string | null
  isOrg?: boolean
  onManage: () => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  const [summary, setSummary] = useState<FolderAccessSummary | null>(null)
  // Same name resolution as AccessCell above — this bar names the same people.
  const { data: memberNames } = useOrgMemberNames()
  const nameFor = (email: string) => memberNames?.get(email.trim().toLowerCase()) || nameFromEmail(email)
  const { onOrgEvent, onPermissionChanged } = useRealtime()
  const whenShown = useWhenShown()
  useEffect(() => {
    if (!isOrg || !folderId) { setSummary(null); return }
    let live = true
    const load = () => filesApi.getFolderAccessSummary(folderId)
      .then(s => { if (live) setSummary(s) })
      .catch(() => { if (live) setSummary(null) })
    // At most one reload per interval (`createAccessBurst`): a single change fires BOTH signals
    // (a grant emits org:access_changed AND permission:changed), and an admin's run of team edits
    // is one change per click, in every member's tab (gnexis 2026-10-21).
    // A hidden drive deck re-reads once when shown (#1259). Not skipped for a change naming another
    // member: this bar lists PEOPLE (a member added to a team changes who has access here).
    const burst = createAccessBurst(() => whenShown(() => { void load() }))
    const scheduleLoad = () => burst.push()
    load()
    // Auto-refresh on permission changes. onOrgEvent covers grant / role /
    // team / restricted; onPermissionChanged is the consolidated signal that
    // ALSO catches share-link + outbound-share changes (the link / outsider
    // chips) — those fire only permission:changed, never org:access_changed.
    const offOrg = onOrgEvent((event) => {
      if (event.type === 'org:access_changed' || event.type === 'org:role_changed') scheduleLoad()
    })
    const offPerm = onPermissionChanged(() => scheduleLoad())
    return () => { live = false; burst.cancel(); whenShown.cancel(); offOrg(); offPerm() }
  }, [folderId, isOrg, onOrgEvent, onPermissionChanged, whenShown])

  if (!isOrg || !summary || summary.scope !== 'org') return null
  const { floor, locked, teams, teamCount, users, userCount, links, outbound } = summary

  // Headline (per the cascade). A muted floor label when open; when members
  // are locked out, the granted TEAM renders as a badge (team icon + name, so
  // it reads as a team, not free text), followed by an "& N more" hint.
  let headline: React.ReactNode
  if (!locked) {
    headline = <span className="font-medium">{floor === 'viewer' ? t('driveView.access.viewerPlus') : t('driveView.access.memberPlus')}</span>
  } else if (teams.length > 0) {
    // The bar has a full line — show up to TWO team badges (teams lead) before
    // folding the rest into "& N more teams".
    const showTeams = teams.slice(0, 2)
    const remaining = teamCount - showTeams.length
    headline = (
      <span className="inline-flex items-center gap-1 min-w-0">
        {showTeams.map((tm) => (
          <span key={tm.name} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ws-team-badge min-w-0">
            <UsersRound size={10} className="shrink-0" />
            <span className="truncate max-w-[9rem]">{tm.name}</span>
          </span>
        ))}
        {remaining > 0 && <span className="opacity-70 shrink-0">{t('driveView.access.moreTeams', { count: remaining })}</span>}
      </span>
    )
  } else if (users.length > 0) {
    headline = <span className="font-medium">{t('driveView.access.peopleCount', { count: userCount })}</span>
  } else {
    headline = <span className="font-medium">{t('driveView.access.limited')}</span>
  }

  // Tooltip = a plain who-has-access breakdown (one icon-prefixed line per
  // principal kind). Falls back to the floor label when nobody's named.
  const tipRows: AccessTooltipRow[] = []
  if (teams.length > 0) tipRows.push({ icon: <UsersRound size={12} className="text-blue-300" />, text: nameBreakdown(teams.map(x => x.name), teamCount, 'teams', t) })
  if (userCount > 0) tipRows.push({ icon: <User size={12} className="text-zinc-300" />, text: nameBreakdown(users.map(nameFromEmail), userCount, 'people', t) })
  if (links > 0) tipRows.push({ icon: <Link2 size={12} className="text-sky-300" />, text: t('driveView.access.links', { count: links }) })
  if (outbound > 0) tipRows.push({ icon: <Share2 size={12} className="text-purple-300" />, text: t('driveView.access.outsiders', { count: outbound }) })
  if (tipRows.length === 0) tipRows.push(
    locked
      ? { icon: <Lock size={12} className="text-zinc-300" />, text: t('driveView.access.limitedTip') }
      : { icon: <Component size={12} className="text-zinc-300" />, text: floor === 'viewer' ? t('driveView.access.viewerPlus') : t('driveView.access.memberPlus') })

  // Own line under the breadcrumb, indented past the path, tucked up close to
  // it. Muted throughout — the lock icon (not colour) signals "restricted";
  // the team badge carries identity. A compact self-sized chip, not a bar.
  return (
    <div className="pl-6 pr-4 pb-2 -mt-2">
    <Tooltip content={<AccessTooltip rows={tipRows} />} always className="inline-flex">
    <button
      type="button"
      data-testid="folder-access-bar"
      onClick={onManage}
      className="shrink-0 inline-flex items-center gap-1.5 h-6 px-1.5 rounded-md text-[11px] themed-text-muted hover:themed-bg-tertiary transition-colors"
    >
      {/* Component for the open floor states (Viewer+/Member+); the lock is
          the restricted signal. */}
      {locked
        ? <Lock size={12} className="shrink-0" />
        : <Component size={12} className="shrink-0" />}
      {headline}
      {/* Member avatars — locked state only (someone specific has access). */}
      {locked && users.length > 0 && (
        <span className="inline-flex items-center shrink-0">
          {users.map((email, i) => (
            <AccessAvatar key={email} seed={email} label={nameFor(email)} overlap={i > 0} sm />
          ))}
          {userCount > users.length && (
            <span className="text-[9px] font-medium px-1 py-0.5 rounded-full themed-bg-tertiary themed-text-muted ml-0.5">+{userCount - users.length}</span>
          )}
        </span>
      )}
      {/* Exposed-state chips — the safety-relevant signals. */}
      {links > 0 && (
        <span className="ws-access-link ws-access-chip inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full shrink-0">
          <Link2 size={10} />{links}
        </span>
      )}
      {outbound > 0 && (
        <span className="ws-access-outsider ws-access-chip inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full shrink-0">
          <Share2 size={10} />{t('driveView.access.outsiders', { count: outbound })}
        </span>
      )}
    </button>
    </Tooltip>
    </div>
  )
}

interface FileContextMenuProps {
  file: FileItem
  onClose: () => void
  onOpenInNewTab: (file: FileItem) => void
  onOpenWorkspace?: (file: FileItem) => void
  onOpenWorkspaceInNewTab?: (file: FileItem) => void
  onOpenOrgWorkspace?: (file: FileItem) => void
  onExplore?: (file: FileItem) => void
  onExploreInNewTab?: (file: FileItem) => void
  /** "Open as App" — run a .miniapp folder / .html file as a top-level shell window. */
  onOpenAsApp?: (file: FileItem) => void
  onDownload: (file: FileItem) => void
  onExtract?: (file: FileItem, toSubfolder: boolean) => void
  onCopy: () => void
  onCut: () => void
  onWhoHasAccess?: (file: FileItem) => void
  /** "Share internally" — grant a role / team / member of THIS drive access
   *  (the inward counterpart to Share to another Drive / Share link). */
  onShareInternally?: (file: FileItem) => void
  /** "Share to another Drive" — opens ShareDialog for cross-org `.shared` mounting. */
  onShare?: (file: FileItem) => void
  /**
   * "Share link" — opens the unified ShareLinksDialog. Mode (App vs
   * Public) + live/snapshot are picked inside the dialog (Decisions 5 + 2).
   */
  onShareLink?: (file: FileItem) => void
  /**
   * "Share PDApp" — for an HTML file inside a `.miniapp` folder, shares the
   * CONTAINING mini-app (opens ShareLinksDialog on the folder). Receives the
   * `.miniapp` ancestor list (top-level → nearest); 1 ⇒ share directly, 2+ ⇒
   * the caller shows a picker. Shown only when `folderBreadcrumbs` yields a
   * `.miniapp` ancestor for an HTML file.
   */
  onShareMiniApp?: (ancestors: CrumbLike[]) => void
  /** The current folder's breadcrumb path — a file row's `.miniapp` ancestors
   *  are `miniappAncestors(folderBreadcrumbs)` (its parent IS the current folder). */
  folderBreadcrumbs?: CrumbLike[]
  onRename: (file: FileItem) => void
  isOrg?: boolean
  wsAccess?: { individual: FolderWorkspaceAccess; organization: FolderWorkspaceAccess } | null
  onShowInfo?: (file: FileItem) => void
  /** "Debug log" — opens the debug-log viewer for a `.miniapp` folder, a loose
   *  HTML file, or a runnable script (.py/.sh). Gated by `supportsDebugLog`. */
  onDebugLog?: (file: FileItem) => void
  /** "Edit icon" — opens the mini-app icon editor. Gated to `.miniapp` folders. */
  onEditIcon?: (file: FileItem) => void
  onDelete: (file: FileItem) => void
  canWrite?: boolean
  canCopy?: boolean
  urlPrefix?: string
  t: (key: string, options?: Record<string, unknown>) => string
}

function FileContextMenu({ file, onClose, onOpenInNewTab, onOpenWorkspace, onOpenWorkspaceInNewTab, onOpenOrgWorkspace, onExplore, onExploreInNewTab, onOpenAsApp, onDownload, onExtract, onCopy, onCut, onWhoHasAccess, onShareInternally, onShare, onShareLink, onShareMiniApp, folderBreadcrumbs, onRename, onDelete, onShowInfo, onDebugLog, onEditIcon, isOrg, wsAccess, canWrite = true, canCopy = true, urlPrefix = '/~', t }: FileContextMenuProps) {
  // "someone else's <product> Drive" in the cross-tenant share tooltips below.
  const { productName } = useBrand()
  const isZip = !file.isFolder && file.name.toLowerCase().endsWith('.zip')
  // A `.shared`/`.chain` pointer (presents as a folder): offer a second download
  // — its metadata JSON — alongside the content zip (onDownload).
  const isPointer = isPointerRow(file)
  // Runnable as a top-level app window: a mini-app folder or a loose HTML page.
  // (#327) `presentsAsMiniapp` so a `.shared` mount of someone else's app gets the
  // APP verbs (Open as app / Browse files) instead of a plain-folder menu — the row
  // already wears the APP badge, so a bare-name test made the menu contradict it.
  // The branch condition is `isLiveMountRow`, not `fileType === 'shared'`: a
  // pointer renamed off its suffix is DEMOTED to a plain file but keeps
  // `file_type`, so a kind-only test kept routing it down the pointer arm. For a
  // demoted `report.html` that meant losing the "Open as App" verb it should now
  // have as an ordinary HTML file — the arm asks `presentsAsMiniapp`, which
  // (correctly) says no, and the `.html` fallback never got a chance to say yes.
  const isAppOpenable = file.isFolder || isLiveMountRow(file.name, file.fileType)
    ? file.type !== 'workpapan' && presentsAsMiniapp(file.name, file.fileType, file.isFolder)
    : /\.html?$/i.test(file.name)
  // A package folder reached only as a HALLWAY (#1036) has no project / app to open — it
  // browses as the plain folder the drive shows (Browse files stays).
  const packageShellOnly = file.isFolder && file.effectivePermission === 'hidden'
  // (#327) A `.miniapp` MOUNT can be run and browsed, but not EDITED: the editor is a
  // workspace rooted at the app's own folder, and a mount has no such folder of its
  // own. Gate the authoring verbs on mount-ness, not on write permission.
  const isPackageMount = isPointer && presentsAsMiniapp(file.name, file.fileType, file.isFolder)
  // (#327 Track B) A `.project` MOUNT opens as a workspace rooted at the mount. The
  // native branch below keys on `type === 'workpapan'`, which a pointer row never is.
  const isProjectMount = isPointer && presentsAsProject(file.name, file.fileType, file.isFolder)
  return (
    <>
      {onOpenAsApp && isAppOpenable && !packageShellOnly && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onOpenAsApp(file)
            onClose()
          }}
          className="ws-menu-item"
        >
          <AppWindowIcon size={14} className="text-purple-500" />
          {t('driveView.menu.openAsApp')}
        </button>
      )}
      {/* Edit the mini-app in a workspace (files + code editor). Read-only for
          viewers → "View source code". Distinct from "Open as app" (chromeless
          run) — this is the workshop. */}
      {isAppOpenable && file.isFolder && !isPackageMount && onOpenWorkspace && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onOpenWorkspace(file)
            onClose()
          }}
          className="ws-menu-item"
        >
          <Wrench size={14} className="text-fuchsia-500" />
          {canWrite ? t('driveView.menu.editMiniapp', { defaultValue: 'Edit PDApp' }) : t('driveView.menu.viewSource', { defaultValue: 'View source code' })}
        </button>
      )}
      {/* Edit the mini-app's custom icon (text/emoji on a squircle). Miniapp
          folders only — not workpapan workspaces or loose HTML. Write-gated:
          setMiniappIcon is server-enforced, so hide it from read-only viewers.
          Same per-row permission check (with folder-level fallback) as Debug log. */}
      {onEditIcon && file.isFolder && file.type !== 'workpapan' && isMiniappFolder(file.name)
        && (file.effectivePermission ? ['readwrite', 'admin', 'owner'].includes(file.effectivePermission) : canWrite) && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClose()
            onEditIcon(file)
          }}
          className="ws-menu-item"
        >
          <Palette size={14} className="text-pink-500" />
          {t('driveView.menu.editIcon', { defaultValue: 'Edit icon' })}
        </button>
      )}
      {/* Mini-app package escape hatch — the folder view behind an explicit
          verb ("Show Package Contents" semantics): double-click now opens the
          app, so Browse files is how you reach the underlying folder. */}
      {isAppOpenable && file.isFolder && onExplore && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onExplore(file)
            onClose()
          }}
          className="ws-menu-item"
        >
          <FolderOpen size={14} />
          {t('driveView.menu.browseFiles')}
        </button>
      )}
      {(file.type === 'workpapan' || isProjectMount) && onOpenWorkspace && (
        <>
          {!packageShellOnly && <button
            onClick={(e) => {
              e.stopPropagation()
              onOpenWorkspace(file)
              onClose()
            }}
            className="ws-menu-item"
          >
            <LayoutGrid size={14} className="text-purple-500" />
            Open Project
          </button>}
          {/* (#327) Not for a project MOUNT: this opens `/workspace/<file.id>` directly,
              and a pointer id is not a workspace folder id — the wrapper is minted
              server-side by `open-folder`, so only the in-place verb above can reach it. */}
          {!isProjectMount && !packageShellOnly && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onOpenWorkspaceInNewTab?.(file)
                onClose()
              }}
              className="ws-menu-item"
            >
              <LayoutGrid size={14} className="text-purple-500" />
              Open Project in New Tab
            </button>
          )}
          <div className="ws-menu-sep" />
          <button
            onClick={(e) => {
              e.stopPropagation()
              onExplore?.(file)
              onClose()
            }}
            className="ws-menu-item"
          >
            <FolderOpen size={14} />
            {t('driveView.menu.browseFiles')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onExploreInNewTab?.(file)
              onClose()
            }}
            className="ws-menu-item"
          >
            <ExternalLink size={14} />
            {t('driveView.menu.browseFilesNewTab')}
          </button>
        </>
      )}
      {/* "Start AI Project" (wrap a plain folder in a linked project) is
          distinct from "Edit PDApp" above — a .miniapp opens as its own
          workspace, so it must NOT also offer the linked-wrapper flow. */}
      {/* A chain that Needs approval doesn't open — not as a project either (D17 / I114). */}
      {file.isFolder && file.type !== 'workpapan' && !presentsAsMiniapp(file.name, file.fileType, file.isFolder) && !isPackageMount && onOpenWorkspace && !isPendingPointer(file) && (() => {
        const ind = wsAccess?.individual
        const org = wsAccess?.organization
        const canReadInd = ind && ind.permission !== 'hidden' && ind.permission !== 'none'
        const canEditInd = ind && ['admin', 'readwrite'].includes(ind.permission)
        // Start: folder is visible to user (hidden is fine — children filter by own perms). Only block 'none'.
        const canStartInd = ind && ind.permission !== 'none'
        const canReadOrg = org && org.permission !== 'hidden' && org.permission !== 'none'
        const canEditOrg = org && ['admin', 'readwrite'].includes(org.permission)
        const canStartOrg = org && org.permission !== 'none'
        const indLabel = ind?.exists ? (canReadInd ? (canEditInd ? 'Open' : 'View') : null) : (canStartInd ? 'Start' : null)
        const orgLabel = org?.exists ? (canReadOrg ? (canEditOrg ? 'Open' : 'View') : null) : (canStartOrg ? 'Start' : null)
        const loaded = !!wsAccess
        const indDisabled = loaded && !indLabel
        const orgDisabled = loaded && !orgLabel
        return <>
          <button
            disabled={indDisabled}
            onClick={(e) => { e.stopPropagation(); onOpenWorkspace(file); onClose() }}
            className={`ws-menu-item ${indDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <LayoutGrid size={14} className={indDisabled ? 'opacity-40' : isOrg ? 'text-blue-500' : 'text-purple-500'} />
            {`${indLabel || 'Start'} AI Project${isOrg ? ' (My Space)' : ''}`}
          </button>
          {isOrg && (
            <button
              disabled={orgDisabled}
              onClick={(e) => { e.stopPropagation(); onOpenOrgWorkspace?.(file); onClose() }}
              className={`ws-menu-item ${orgDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <LayoutGrid size={14} className={orgDisabled ? 'opacity-40' : 'text-purple-500'} />
              {`${orgLabel || 'Start'} AI Project (Organization)`}
            </button>
          )}
        </>
      })()}
      {file.isFolder && file.type !== 'workpapan' && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onOpenInNewTab(file)
            onClose()
          }}
          className="ws-menu-item"
        >
          <ExternalLink size={14} />
          {t('driveView.menu.openInNewTab')}
        </button>
      )}
      {!file.isFolder && file.type !== 'workpapan' && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            // A pending chain goes through the gate (its dialog), never straight to its target.
            if (isPendingPointer(file)) onOpenInNewTab(file)
            else window.open(`${urlPrefix}/file/${file.id}`, '_blank')
            onClose()
          }}
          className="ws-menu-item"
        >
          <ExternalLink size={14} />
          Open in New Tab
        </button>
      )}
      {canCopy && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onDownload(file)
            onClose()
          }}
          className="ws-menu-item"
        >
          <Download size={14} />
          {/* A pointer gets the bare "Download" label, NOT "Download as ZIP":
              it can yield either the source contents (a zip) or the pointer
              JSON, and `handleDownload` opens a dialog to settle which. This
              used to be TWO peer items — "Download as ZIP" + "Download
              metadata" — which read as two flavours of one action. */}
          {file.isFolder && !isPointer ? t('driveView.menu.downloadAsZip') : t('driveView.menu.download')}
        </button>
      )}
      {isZip && onExtract && canWrite && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onExtract(file, false)
              onClose()
            }}
            className="ws-menu-item"
          >
            <FolderArchive size={14} />
            {t('driveView.menu.extractHere')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onExtract(file, true)
              onClose()
            }}
            className="ws-menu-item"
          >
            <FolderArchive size={14} />
            {t('driveView.menu.extractToFolder', { name: file.name.replace(/\.zip$/i, '') })}
          </button>
        </>
      )}
      {(canCopy || canWrite) && <div className="border-t themed-border-secondary my-1" />}
      {canCopy && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onCopy()
            onClose()
          }}
          className="ws-menu-item"
        >
          <Copy size={14} />
          {t('driveView.menu.copy')}
        </button>
      )}
      {canWrite && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onCut()
            onClose()
          }}
          className="ws-menu-item"
        >
          <Scissors size={14} />
          {t('driveView.menu.cut')}
        </button>
      )}
      {(onShare || onShareLink || onWhoHasAccess || onShareInternally) && (
        <>
          <div className="border-t themed-border-secondary my-1" />
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider themed-text-muted">
            Share
          </div>
          {onWhoHasAccess && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onWhoHasAccess(file)
                onClose()
              }}
              className="ws-menu-item"
            >
              <Users size={14} />
              {t('driveView.menu.whoHasAccess')}
            </button>
          )}
          {onShareInternally && (
            <Tooltip content="Give a role, team, or member of this drive access." always followCursor>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onShareInternally(file)
                  onClose()
                }}
                className="ws-menu-item"
              >
                <UsersRound size={14} />
                {t('driveView.shareInternally.action', { defaultValue: 'Share internally' })}
              </button>
            </Tooltip>
          )}
          {onShare && (
            <Tooltip
              content={file.reshareBlocked
                ? 'The owner shared this with you without permission to share it onward.'
                : `Drop this into someone else's ${productName} Drive as a shared ${file.isFolder ? 'folder' : 'file'}.`}
              always followCursor
            >
              {/* (#327) Disabled, not hidden: the verb still belongs on an inbound
                  mount, and a greyed control with a reason reads better than a
                  vanished one. Previously this stayed live and the mint 403'd —
                  the UI offered an action that could never succeed. */}
              <button
                type="button"
                disabled={file.reshareBlocked}
                onClick={(e) => {
                  e.stopPropagation()
                  if (file.reshareBlocked) return
                  onShare(file)
                  onClose()
                }}
                className={`ws-menu-item${file.reshareBlocked ? ' opacity-40 cursor-not-allowed' : ''}`}
              >
                <Share2 size={14} />
                Share to another Drive
              </button>
            </Tooltip>
          )}
          {onShareLink && (
            <Tooltip content="Create a URL with custom permissions, allowed emails, limits like expiry, and analytics." always followCursor>
              <button
                type="button"
                disabled={file.reshareBlocked}
                onClick={(e) => {
                  e.stopPropagation()
                  if (file.reshareBlocked) return
                  onShareLink(file)
                  onClose()
                }}
                className={`ws-menu-item${file.reshareBlocked ? ' opacity-40 cursor-not-allowed' : ''}`}
              >
                <Share2 size={14} />
                {t('driveView.menu.shareLink')}
              </button>
            </Tooltip>
          )}
          {/* "Share PDApp" — this HTML file lives inside a `.miniapp`; share the
              CONTAINING app, not just the file. Gated to HTML-file-in-.miniapp via
              canShareMiniApp; the handler picks the folder (or a picker if nested). */}
          {onShareMiniApp && folderBreadcrumbs && canShareMiniApp(file, folderBreadcrumbs) && (
            <Tooltip content="Share the PDApp this file belongs to (not just this file)." always followCursor>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onShareMiniApp(miniappAncestors(folderBreadcrumbs))
                  onClose()
                }}
                className="ws-menu-item"
              >
                <PDAppIcon size={14} className="text-pink-500" />
                {t('driveView.menu.shareMiniApp')}
              </button>
            </Tooltip>
          )}
        </>
      )}
      {canWrite && <>
      <div className="border-t themed-border-secondary my-1" />
      {/* A revoked/broken `.shared` ("No longer available") is still a normal row in
          the user's OWN drive — rename/move/copy/delete it like any folder; only OPEN
          (dead pointer) + Share/manage/Download (no grant left) stay blocked, and it's
          greyed. A dead `.chain` stays fully inert (Delete only) — that's deliberate
          and guarded by e2e/chain-primitive.spec.ts. */}
      {!file.chainBroken && (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onRename(file)
          onClose()
        }}
        className="ws-menu-item"
      >
        <Pencil size={14} />
        {t('driveView.menu.rename')}
      </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete(file)
          onClose()
        }}
        className="ws-menu-item ws-menu-item-danger"
      >
        <Trash2 size={14} />
        {t('driveView.menu.delete')}
      </button>
      </>}
      {/* Debug log — mini-app folders + loose HTML/.py/.sh files, WRITE access only
          (the log is author/editor material; the API also enforces this).
          `effectivePermission` can be absent on a realtime-patched row (it can't
          ride the broadcast payload), so fall back to the folder-level `canWrite`
          rather than hiding the option until a refetch. */}
      {onDebugLog && supportsDebugLog(file.name, file.isFolder)
        && (file.effectivePermission ? ['readwrite', 'admin', 'owner'].includes(file.effectivePermission) : canWrite) && (
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); onDebugLog(file) }}
          className="ws-menu-item"
        >
          <Bug size={14} />
          {t('driveView.menu.debugLog', { defaultValue: 'Debug log' })}
        </button>
      )}
      {/* Info item */}
      <div className="border-t themed-border-secondary my-1" />
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); onShowInfo?.(file) }}
        className="ws-menu-item"
      >
        <Info size={14} />
        {file.isFolder ? 'Folder Info' : 'File Info'}
      </button>
    </>
  )
}

function EmptyAreaContextMenu({
  hasClipboard,
  onClose,
  onOpenWorkspace,
  onOpenOrgWorkspace,
  onNewFolder,
  onNewProject,
  onChainHere,
  onPaste,
  onDownload,
  onOpenInNewTab,
  onWhoHasAccess,
  onShareInternally,
  onShare,
  onShareLink,
  reshareBlocked,
  isOrg,
  canWrite,
  canCopy,
  wsAccess,
  onShowInfo,
  t,
}: {
  hasClipboard: boolean
  onClose: () => void
  onOpenWorkspace: () => void
  onOpenOrgWorkspace?: () => void
  onNewFolder: () => void
  /** Contextual — provided only inside the Projects container. */
  onNewProject?: () => void
  /** "Chain here…" — pointer creation moved off the toolbar (peer of Paste). */
  onChainHere?: () => void
  onPaste: () => void
  onDownload?: () => void
  onOpenInNewTab?: () => void
  onWhoHasAccess?: () => void
  /** "Share internally" on the CURRENT folder (background menu). */
  onShareInternally?: () => void
  onShare?: () => void
  onShareLink?: () => void
  /** (#327) The CURRENT folder is inside a mount granted without reshare
   *  rights — disable both share verbs above rather than hide them. */
  reshareBlocked?: boolean
  isAppEligible?: boolean
  isOrg?: boolean
  canWrite?: boolean
  canCopy?: boolean
  wsAccess?: { individual: FolderWorkspaceAccess; organization: FolderWorkspaceAccess } | null
  onShowInfo?: () => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  // Same cross-tenant share tooltip as `FileContextMenu`, on the background
  // menu. Both say the product's name, so both have to read it.
  const { productName } = useBrand()
  return (
    <>
      {(() => {
        const ind = wsAccess?.individual
        const org = wsAccess?.organization
        const canReadInd = ind && ind.permission !== 'hidden' && ind.permission !== 'none'
        const canEditInd = ind && ['admin', 'readwrite'].includes(ind.permission)
        // Start: folder is visible to user (hidden is fine — children filter by own perms). Only block 'none'.
        const canStartInd = ind && ind.permission !== 'none'
        const canReadOrg = org && org.permission !== 'hidden' && org.permission !== 'none'
        const canEditOrg = org && ['admin', 'readwrite'].includes(org.permission)
        const canStartOrg = org && org.permission !== 'none'
        const indLabel = ind?.exists ? (canReadInd ? (canEditInd ? 'Open' : 'View') : null) : (canStartInd ? 'Start' : null)
        const orgLabel = org?.exists ? (canReadOrg ? (canEditOrg ? 'Open' : 'View') : null) : (canStartOrg ? 'Start' : null)
        const loaded = !!wsAccess
        const indDisabled = loaded && !indLabel
        const orgDisabled = loaded && !orgLabel
        return <>
          <button
            disabled={indDisabled}
            onClick={(e) => { e.stopPropagation(); onOpenWorkspace(); onClose() }}
            className={`ws-menu-item ${indDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <LayoutGrid size={14} className={indDisabled ? 'opacity-40' : isOrg ? 'text-blue-500' : 'text-purple-500'} />
            {`${indLabel || 'Start'} AI Project${isOrg ? ' (My Space)' : ''}`}
          </button>
          {isOrg && (
            <button
              disabled={orgDisabled}
              onClick={(e) => { e.stopPropagation(); onOpenOrgWorkspace?.(); onClose() }}
              className={`ws-menu-item ${orgDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <LayoutGrid size={14} className={orgDisabled ? 'opacity-40' : 'text-purple-500'} />
              {`${orgLabel || 'Start'} AI Project (Organization)`}
            </button>
          )}
        </>
      })()}
      <div className="border-t themed-border-secondary my-1" />
      <button
        disabled={canWrite === false}
        onClick={(e) => {
          e.stopPropagation()
          onNewFolder()
          onClose()
        }}
        className={`ws-menu-item ${canWrite === false ? 'opacity-40 cursor-not-allowed' : ''}`}
      >
        <FolderPlus size={14} className={canWrite === false ? 'opacity-40' : ''} />
        {t('driveView.actions.newFolder')}
      </button>
      {onNewProject && (
        <button
          disabled={canWrite === false}
          onClick={(e) => {
            e.stopPropagation()
            onNewProject()
            onClose()
          }}
          className={`ws-menu-item ${canWrite === false ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          <LayoutGrid size={14} className={canWrite === false ? 'opacity-40' : 'text-purple-500'} />
          {t('driveView.menu.newProject')}
        </button>
      )}
      {canWrite !== false && (hasClipboard || onChainHere) && (
        <>
          <div className="border-t themed-border-secondary my-1" />
          {hasClipboard && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onPaste()
                onClose()
              }}
              className="ws-menu-item"
            >
              <Clipboard size={14} />
              {t('driveView.menu.paste')}
            </button>
          )}
          {onChainHere && (
            <button
              data-testid="drive-action-chain"
              onClick={(e) => {
                e.stopPropagation()
                onChainHere()
                onClose()
              }}
              className="ws-menu-item"
            >
              <Link2 size={14} className="text-sky-400" />
              {t('driveView.menu.chainHere')}
            </button>
          )}
        </>
      )}
      {(onDownload || onOpenInNewTab) && (
        <>
          <div className="border-t themed-border-secondary my-1" />
          {onOpenInNewTab && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenInNewTab(); onClose() }}
              className="ws-menu-item"
            >
              <ExternalLink size={14} />
              {t('driveView.menu.openInNewTab')}
            </button>
          )}
          {canCopy !== false && onDownload && (
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(); onClose() }}
              className="ws-menu-item"
            >
              <Download size={14} />
              {t('driveView.menu.downloadAsZip')}
            </button>
          )}
        </>
      )}
      {/* (#755) Same gate as the row menu. Share internally arrives only on
          org drives; gating the section on it (with Who-has-access, which was
          org-gated too) dropped every share verb on a personal drive. */}
      {(onShare || onShareLink || onWhoHasAccess || onShareInternally) && (
        <>
          <div className="border-t themed-border-secondary my-1" />
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider themed-text-muted">
            Share
          </div>
          {onWhoHasAccess && (
            <button
              onClick={(e) => { e.stopPropagation(); onWhoHasAccess(); onClose() }}
              className="ws-menu-item"
            >
              <Users size={14} />
              {t('driveView.menu.whoHasAccess')}
            </button>
          )}
          {onShareInternally && (
            <Tooltip content="Give a role, team, or member of this drive access." always followCursor>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onShareInternally(); onClose() }}
                className="ws-menu-item"
              >
                <UsersRound size={14} />
                {t('driveView.shareInternally.action', { defaultValue: 'Share internally' })}
              </button>
            </Tooltip>
          )}
          {/* (#327) Standing inside a mount the owner shared without reshare
              rights, both share verbs are disabled — same treatment as the
              row menu, reached from the other direction. */}
          {onShare && (
            <Tooltip
              content={reshareBlocked
                ? 'The owner shared this with you without permission to share it onward.'
                : `Drop this into someone else's ${productName} Drive as a shared folder.`}
              always followCursor
            >
              <button
                type="button"
                disabled={reshareBlocked}
                onClick={(e) => { e.stopPropagation(); if (reshareBlocked) return; onShare(); onClose() }}
                className={`ws-menu-item${reshareBlocked ? ' opacity-40 cursor-not-allowed' : ''}`}
              >
                <Share2 size={14} />
                Share to another Drive
              </button>
            </Tooltip>
          )}
          {onShareLink && (
            <Tooltip
              content={reshareBlocked
                ? 'The owner shared this with you without permission to share it onward.'
                : 'Create a URL with custom permissions, allowed emails, limits like expiry, and analytics.'}
              always followCursor
            >
              <button
                type="button"
                disabled={reshareBlocked}
                onClick={(e) => { e.stopPropagation(); if (reshareBlocked) return; onShareLink(); onClose() }}
                className={`ws-menu-item${reshareBlocked ? ' opacity-40 cursor-not-allowed' : ''}`}
              >
                <Share2 size={14} />
                {t('driveView.menu.shareLink')}
              </button>
            </Tooltip>
          )}
        </>
      )}
      {/* Folder info */}
      <div className="border-t themed-border-secondary my-1" />
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); onShowInfo?.() }}
        className="ws-menu-item"
      >
        <Info size={14} />
        Folder Info
      </button>
    </>
  )
}

interface FileRowProps {
  file: FileItem
  /** A delete for this row is in flight — dim it and take it out of reach. */
  deleting?: boolean
  selected: boolean
  selectedFiles: FileItem[]
  onSelect: (file: FileItem, e: React.MouseEvent) => void
  onOpen: (file: FileItem) => void
  onOpenInNewTab: (file: FileItem) => void
  // Jump from a `.chain` row to its real target (the "→ <target>" label).
  onJumpToTarget?: (targetId: string, targetIsFolder: boolean) => void
  /** D10: approve a "Needs approval" `.chain` (only offered when the viewer can open and share its target). */
  onApprovePointer?: (file: FileItem) => void
  /** Owner 2026-10-27: approve a `.py` / `.sql` the app (or a visitor) wrote, so it runs as the app. */
  onApproveCode?: (file: FileItem) => void
  onOpenWorkspace?: (file: FileItem) => void
  onOpenWorkspaceInNewTab?: (file: FileItem) => void
  onOpenOrgWorkspace?: (file: FileItem) => void
  onExplore?: (file: FileItem) => void
  onExploreInNewTab?: (file: FileItem) => void
  onOpenAsApp?: (file: FileItem) => void
  isOrg?: boolean
  onCopy: () => void
  onCut: () => void
  onWhoHasAccess?: (file: FileItem) => void
  /** Grant a role / team / member of THIS drive access (internal counterpart
   *  to the outward "Share to another Drive" / "Share link"). */
  onShareInternally?: (file: FileItem) => void
  onShare?: (file: FileItem) => void
  onShareLink?: (file: FileItem) => void
  onShareMiniApp?: (ancestors: CrumbLike[]) => void
  folderBreadcrumbs?: CrumbLike[]
  /** The row is one of the drive's structural folders (#889) — drawn with its sidebar icon. */
  structural?: StructuralFolderKind | null
  onDownload: (file: FileItem) => void
  onExtract?: (file: FileItem, toSubfolder: boolean) => void
  onDelete: (file: FileItem) => void
  onShowInfo?: (file: FileItem) => void
  onDebugLog?: (file: FileItem) => void
  onEditIcon?: (file: FileItem) => void
  onRename: (file: FileItem) => void
  onRenameSubmit: (file: FileItem, newName: string) => void
  onRenameCancel: () => void
  isRenaming: boolean
  isDropTarget: boolean
  isForbidden: boolean
  isDragging: boolean
  isCut: boolean
  onDragStart: (e: React.DragEvent, file: FileItem) => void
  onDragOver: (e: React.DragEvent, file: FileItem) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, file: FileItem) => void
  onContextMenu: (file: FileItem, e: React.MouseEvent) => void
  canWrite?: boolean
  canCopy?: boolean
  wsAccess?: { individual: FolderWorkspaceAccess; organization: FolderWorkspaceAccess } | null
  tenantId: string
  t: (key: string, options?: Record<string, unknown>) => string
}

function FileRow({
  file,
  deleting,
  selected,
  selectedFiles,
  onSelect,
  onOpen,
  onOpenInNewTab,
  onJumpToTarget,
  onApprovePointer,
  onApproveCode,
  onOpenWorkspace,
  onOpenWorkspaceInNewTab,
  onOpenOrgWorkspace,
  onExplore,
  onExploreInNewTab,
  onOpenAsApp,
  isOrg,
  onCopy,
  onCut,
  onWhoHasAccess,
  onShareInternally,
  onShare,
  onShareLink,
  onShareMiniApp,
  folderBreadcrumbs,
  structural,
  onDownload,
  onExtract,
  onDelete,
  onShowInfo,
  onDebugLog,
  onEditIcon,
  onRename,
  onRenameSubmit,
  onRenameCancel,
  isRenaming,
  isDropTarget,
  isForbidden,
  isDragging,
  isCut,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onContextMenu,
  canWrite,
  canCopy,
  wsAccess,
  tenantId,
  t,
}: FileRowProps) {
  const rowUrlPrefix = tenantId.startsWith('p_') ? '/~' : `/t/${tenantId}`
  // Project / mini-app identity: both are "apps that live in a folder" —
  // the row shows the clean display name (the .project/.miniapp extension
  // is runtime meta, not something the user should parse) and the type
  // badge carries the identity instead. Rename still edits the full name.
  const isProject = file.type === 'workpapan'
  const isMiniapp = file.isFolder && file.type !== 'workpapan' && isMiniappFolder(file.name)
  // Show the FULL name, its endings muted, never hidden (see ./nameParts).
  const { base: nameBase, ext: nameExt, ext2: nameExt2 } = nameParts({ name: file.name, isFolder: file.isFolder, fileType: file.fileType, isProject })
  const [showMenu, setShowMenu] = useState(false)
  // Where the ⋯ dropdown is pinned, in viewport coordinates. `null` until the
  // button is clicked; set from that button's rect.
  //
  // PORTALED AND FIXED, for the reason the right-click menu below already
  // documents: the file list scrolls (`overflow-auto`), and an absolutely
  // positioned child of a scrolling ancestor is CLIPPED BY IT. This menu used
  // to be exactly that, flipping between `top-8` and `bottom-8` on the row —
  // so on a short window an upward flip put its first items above the list's
  // top edge, where they were invisible AND unreachable, because scrolling
  // moves the row and the menu together.
  //
  // Keeping it in the row and merely capping its height does NOT fix that: the
  // clipping bound is the LIST's box, not the viewport's, and a menu can sit
  // entirely inside the window while half of it is cut off by the container it
  // lives in. Escaping to `<body>` is the fix; clamping to the viewport after
  // measuring is what keeps it whole once it is out.
  // Position comes from `useAnchoredPosition` (@papan/core-ui): it follows the
  // ⋯ button through scroll and resize, and dismisses when the button scrolls
  // out of the list. Both halves matter here — a `fixed` menu that neither
  // follows nor closes stays pinned to the viewport while its row travels, so
  // it ends up beside a DIFFERENT file while still acting on the original one.
  const menuBtnRef = useRef<HTMLButtonElement>(null)
  // Portaled content leaves the ThemedArea, so the theme class has to travel
  // with it or the menu renders with no CSS variables defined at all.
  const { getTheme: getRowTheme } = useTheme()
  const rowMenuThemeClass = getThemeClass(getRowTheme('drive'))
  const [localWsAccess, setLocalWsAccess] = useState<typeof wsAccess>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // Follow the ⋯ button; close once it has scrolled out of the list. The flip,
  // the viewport clamp and the guarded writes all live in the hook — this used
  // to be a hand-rolled clamp here whose check and write disagreed below
  // `maxTop < 8`, an infinite `setState` loop held off only by the `max-h`
  // below happening to equal its gap doubled.
  // `exitTransitionMs` matches `.anchor-exit`'s 160ms in index.css — the menu
  // plays out rather than blinking away, so a close the user did not ask for
  // does not read as a glitch.
  const { pos: menuPos, exiting: menuExiting, origin: menuOrigin } = useAnchoredPosition({
    open: showMenu,
    anchorRef: menuBtnRef,
    floatingRef: menuRef,
    onDismiss: useCallback(() => setShowMenu(false), []),
    align: 'end',
    exitTransitionMs: 160,
  })
  const [renameValue, setRenameValue] = useState(file.name)
  // Fetch workspace access when three-dot menu opens on a folder
  useEffect(() => {
    if (!showMenu) { setLocalWsAccess(null); return }
    if (file.isFolder && file.type !== 'workpapan') {
      request<{ individual: FolderWorkspaceAccess; organization: FolderWorkspaceAccess }>(`/api/workspaces/folder-access/${file.id}`).then(setLocalWsAccess).catch(() => {})
    }
  }, [showMenu, file.id, file.isFolder, file.type])
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Focus and select the name (without extension) when entering rename mode
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      setRenameValue(file.name)
      renameInputRef.current.focus()
      const dotIndex = file.isFolder ? -1 : file.name.lastIndexOf('.')
      if (dotIndex > 0) {
        renameInputRef.current.setSelectionRange(0, dotIndex)
      } else {
        renameInputRef.current.select()
      }
    }
  }, [isRenaming, file.name, file.isFolder])

  // Close menu when clicking outside
  useEffect(() => {
    if (!showMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('click', handleClickOutside, true)
    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [showMenu])

  const handleDragStart = (e: React.DragEvent) => {
    const filesToDrag = selected ? selectedFiles : [file]

    // Alt (Option) held at the START = the hidden "drag as link" gesture (#750, owner
    // decision C): one file also carries its /file/<id> link, for chat or a new tab.
    // Otherwise names only — whatever this is dropped on can read it.
    startPapanDrag(e.dataTransfer, {
      tenantId,
      files: filesToDrag.map(f => ({ id: f.id, name: f.name, isFolder: f.isFolder })),
    }, { link: e.altKey, names: true })
    // …and to an app it is dropped on, as content (#605).
    offerDragToApps(e.dataTransfer, filesToDrag.map(f => f.id))

    // Custom drag image
    const dragEl = createDragImage(filesToDrag)
    document.body.appendChild(dragEl)
    e.dataTransfer.setDragImage(dragEl, 0, 0)
    requestAnimationFrame(() => document.body.removeChild(dragEl))

    onDragStart(e, file)
  }

  return (
    <div
      data-file-id={file.id}
      draggable={!isRenaming && !file.chainBroken}
      onDragStart={handleDragStart}
      onDragOver={(e) => file.isFolder && onDragOver(e, file)}
      onDragLeave={onDragLeave}
      onDrop={(e) => file.isFolder && onDrop(e, file)}
      onClick={(e) => !isRenaming && onSelect(file, e)}
      onDoubleClick={() => !isRenaming && onOpen(file)}
      onContextMenu={(e) => onContextMenu(file, e)}
      // `deleting` DIMS THE ROW AND TAKES IT OUT OF REACH (#732/#764).
      //
      // Two jobs. The screen changes the instant the user confirms, instead of
      // sitting untouched for the 93-126s a large folder takes — the progress
      // toast says work is happening, this says WHICH row it is happening to.
      //
      // And `pointer-events-none` is the half that matters for #764: with the
      // row still live, the natural response to a motionless screen is to
      // delete it again, and a second delete landing inside the first one's
      // window is exactly what banked two bin entries on stagetest2. The
      // server is idempotent about that now; this stops it being asked.
      //
      // The row is NOT dimmed as a whole. A uniformly faded row reads as
      // "disabled" with no reason given, which is its own confusion. Instead
      // each part says one thing: the icon goes grey (this item is going away),
      // the name shimmers (work is in progress, the same sweep chat uses for a
      // running tool), and a "Deleting…" label with a spinner names the work.
      aria-busy={deleting || undefined}
      className={`h-12 flex items-center px-4 border-b themed-row-border cursor-pointer transition-colors group select-none ${
        selected ? 'bg-blue-600/20 hover:bg-blue-600/30' : 'themed-row-hover'
      } ${isForbidden ? 'bg-red-600/20 border-red-500/50 border-2' : isDropTarget ? 'bg-green-600/30 border-green-500 border-2' : ''} ${
        isDragging || isCut ? 'opacity-50' : ''
      } ${file.shareBroken ? 'opacity-50' : ''} ${deleting ? 'pointer-events-none' : ''}`}
      title={file.shareBroken ? (file.shareNoAccess ? "No access — you don't have a grant for this shared folder" : 'No longer available — access was revoked') : undefined}
    >
      <div className="flex-1 flex items-center gap-3 min-w-0 pointer-events-none">
        {/* Share2 corner badge on owner-side SHARING folders — only
            when the server flagged the row with `hasOutboundShare`
            (i.e. there's at least one active share_grants row pointing
            to an outsider). Recipient `.shared` rows get the Wifi
            overlay via FileIcon's own .shared registry entry; this
            branch is for folders THIS tenant owns and has shared out. */}
        {/* A `.chain` keeps its own link sub-icon even when it inherits the
            SHARING flag inside a shared subtree — the chain identity wins for
            the icon; the SHARING badge still renders in the row. */}
        <span className={`inline-flex shrink-0 transition-[filter,opacity] ${deleting ? 'grayscale opacity-40' : ''}`}>
        {(() => {
          // Mini-app rows get the small vivid gradient tile (the reserved
          // "this is an app" signal — DESIGN_DOC §10.3); everything else
          // keeps FileIcon. The outbound-share corner badge wraps either.
          const iconEl = isMiniapp ? (
            <MiniappTile id={file.id} name={nameBase} metadataJson={file.metadataJson} />
          ) : (
            <FileIcon
              // The ONE row→icon mapping (name/type/true isFolder/fileType, and
              // the D17 grey for a `.chain` that still needs approval). The true
              // isFolder lets FileIcon's `.shared` branch pick the folder icon
              // for a real folder-target share.
              {...fileIconProps(file)}
              structural={structural}
              size={20}
              // Custom icon for an APP-MOUNT root (`X.miniapp.shared`): the
              // Phase-1c lift resolves these to the 'miniapp-folder' key, and
              // the listing merges the SOURCE's miniappIcon into metadataJson —
              // this spread lets FileIcon render the real icon (share overlay
              // preserved). Safe on every other row (doc on miniappIconProps).
              {...miniappIconProps(file)}
            />
          )
          return showsOutboundShareBadge(file) ? (
            <span className="relative inline-flex items-center justify-center shrink-0">
              {iconEl}
              <span
                className="absolute -bottom-[3px] -right-[4px] inline-flex items-center justify-center pointer-events-none file-chip-sharing rounded-full p-0.5"
                aria-hidden
              >
                {/* Inherited-only: fade the glyph, NOT the chip — the chip
                    overlays the file icon, so a translucent bg would let the
                    icon bleed through. */}
                <Share2 size={12} strokeWidth={2.5} className={file.outboundShareInherited ? 'opacity-35' : undefined} />
              </span>
            </span>
          ) : iconEl
        })()}
        </span>
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                const trimmed = renameValue.trim()
                if (trimmed && trimmed !== file.name) {
                  onRenameSubmit(file, trimmed)
                } else {
                  onRenameCancel()
                }
              }
              if (e.key === 'Escape') onRenameCancel()
            }}
            onBlur={() => {
              const trimmed = renameValue.trim()
              if (trimmed && trimmed !== file.name) {
                onRenameSubmit(file, trimmed)
              } else {
                onRenameCancel()
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent themed-text outline-none border themed-border rounded px-1 py-0.5 text-sm pointer-events-auto"
          />
        ) : (
          <>
            {/* pointer-events-auto so the full-name title tooltip fires on
                hover. Drag from the filename starts the row's HTML5 drag like
                anywhere else on the row (the old press-to-select-text mode
                here used to swallow the gesture and block dnd). */}
            <span
              className={`truncate pointer-events-auto ${deleting ? 'working-shimmer' : 'themed-text'}`}
              title={file.name}
            ><MutedEndings parts={{ base: nameBase, ext: nameExt, ext2: nameExt2 }} /></span>
            {/* Badge precedence:
                  1. revoked / broken share → "No longer available" pill (alone).
                  2. otherwise → the per-extension `FileTypeBadge` (PDF/TXT/…),
                     AND — for content inside a cross-tenant share (server set
                     `sharedFromName`) — the green SOURCE-ORG pill (e.g. "ORG").
                     The mount ARTIFACT itself is the exception: a `.shared` row
                     gets ONLY the org pill (its "SHARED" type badge is suppressed
                     — the org pill already says it's shared), and a `.chain` uses
                     the "→ target" label below. CHILD items inside the share keep
                     their type badge + the org pill, so the file TYPE stays visible
                     at a glance. FileTypeBadge is null for folders / unknown
                     extensions. ("Shared by …" lives on hover.)
                  Package-target pointers are the one carve-out from the
                     suppression: a folder-target `.shared`/`.chain` whose INNER
                     name is a package wears the same identity chip a native row
                     does (PROJECT badge / APP chip) — recognition only, the
                     open behavior is unchanged (#327 owns launch-through-mount). */}
            {deleting
              // Replaces the type/share badges rather than joining them: while
              // the row is going away, what it is being done to matters more
              // than what it is. Without this label a grey icon and a
              // shimmering name are two effects with no stated cause.
              ? <span data-testid="row-deleting" className="text-[10px] px-1.5 py-0.5 rounded-full themed-bg-tertiary themed-text-muted shrink-0 whitespace-nowrap inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" aria-hidden />{t('driveView.row.deleting')}</span>
              : (file.shareBroken || file.chainBroken)
              ? <span className="text-[10px] px-1.5 py-0.5 rounded-full themed-bg-tertiary themed-text-muted shrink-0 whitespace-nowrap">{file.shareNoAccess ? 'No access' : 'No longer available'}</span>
              : file.alreadyShared
              ? <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap" style={{ backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24' }} title="A share for this folder already exists in your drive — this is a conflicting copy">Conflict</span>
              : (
                <>
                  {!isPointerRow(file)
                    ? <FileTypeBadge type={file.type} name={file.name} />
                    : file.isFolder && isProjectFolder(stripPointerSuffix(file.name))
                      ? <FileTypeBadge type="workpapan" name={stripPointerSuffix(file.name)} />
                      : null}
                  {(isMiniapp || (file.isFolder && isPointerRow(file) && isMiniappFolder(stripPointerSuffix(file.name)))) && (
                    // Same APP chip as ProjectCard — one signal for one concept
                    // (the old "MINIAPP" pill predates the card language).
                    <span className="text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded file-badge-app shrink-0 whitespace-nowrap">APP</span>
                  )}
                  {file.sharedFromName && (
                    <span className="text-xs px-1.5 py-0.5 rounded shrink-0 whitespace-nowrap" style={{ backgroundColor: 'rgba(52, 211, 153, 0.2)', color: '#34d399' }} title={`Shared by ${file.sharedFromName}`}>{file.sharedFromName}</span>
                  )}
                </>
              )}
            {/* D118 (owner 2026-10-20): this app's databases.json (or this file) can't be read — its databases are held
                back. Shown only to whoever can edit it (the server sends it only then); the reason is in the tooltip, and
                a click opens the file to fix it. */}
            {file.manifestProblem && (
              <ManifestProblemBadge problem={file.manifestProblem} {...(onJumpToTarget ? { onOpenFile: (id: string) => onJumpToTarget(id, false) } : {})} />
            )}
            {/* Points-to badge for a healthy `.chain` row — a sky pill that
                follows the chain subicon's sky-300 link glyph, styled like the
                ORG/SHARING pills. Shows the Link2 icon + the source-folder name,
                and (when clickable) jumps to the real target (navigate-into for
                a folder, viewer for a file). Rendered BEFORE the SHARING pill so
                a chain's identity reads first. */}
            {/* D17: the target is shown only once approved — before that, only in the Approve dialog. */}
            {isChainPointerRow(file) && file.chainTargetName && !file.chainBroken && !file.chainNeedsApproval && (
              <Tooltip
                content={t('driveView.chain.jumpTo', { name: file.chainTargetName })}
                always
                className="shrink-0 inline-flex max-w-[14rem] pointer-events-auto"
              >
                {onJumpToTarget ? (
                  <button
                    type="button"
                    data-testid="chain-points-to"
                    onClick={(e) => { e.stopPropagation(); onJumpToTarget(file.chainTargetId!, !!file.chainTargetIsFolder) }}
                    className="text-xs px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-1 max-w-full hover:brightness-110"
                    style={{ backgroundColor: 'rgba(125, 211, 252, 0.18)', color: '#7dd3fc' }}
                  >
                    <Link2 size={12} strokeWidth={2.5} className="shrink-0" />
                    <span className="truncate">{file.chainTargetName}</span>
                  </button>
                ) : (
                  <span
                    className="text-xs px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-1 max-w-full"
                    style={{ backgroundColor: 'rgba(125, 211, 252, 0.18)', color: '#7dd3fc' }}
                  >
                    <Link2 size={12} strokeWidth={2.5} className="shrink-0" />
                    <span className="truncate">{file.chainTargetName}</span>
                  </span>
                )}
              </Tooltip>
            )}
            {/* D10 — "Needs approval": share links and apps don't follow this chain until
                someone who can open and share its target approves it (they become its author). */}
            {isChainPointerRow(file) && file.chainNeedsApproval && !file.chainBroken && (
              <Tooltip
                content={t('driveView.chain.needsApprovalHint')}
                always
                className="shrink-0 inline-flex pointer-events-auto"
              >
                {file.chainCanApprove && onApprovePointer ? (
                  <button
                    type="button"
                    data-testid="chain-approve"
                    onClick={(e) => { e.stopPropagation(); onApprovePointer(file) }}
                    className="text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap inline-flex items-center gap-1 bg-amber-500 text-black shadow-sm hover:bg-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} className="shrink-0" />
                    {t('driveView.chain.approve')}
                  </button>
                ) : (
                  <span
                    data-testid="chain-needs-approval"
                    className="text-xs px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-1"
                    style={{ backgroundColor: 'rgba(251, 191, 36, 0.18)', color: '#fbbf24' }}
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} className="shrink-0" />
                    {t('driveView.chain.needsApproval')}
                  </span>
                )}
              </Tooltip>
            )}
            {/* Owner 2026-10-27 — "Needs approval": a `.py` / `.sql` the app (or a visitor) wrote doesn't run as the app
                until someone who can edit the app approves it (POST /:id/code/approve). */}
            {file.isFolder && file.codeApprovalBelow && (
              <Tooltip content={t('driveView.code.folderHasPending')} always className="shrink-0 inline-flex pointer-events-auto">
                <span data-testid="code-pending-below" aria-label={t('driveView.code.folderHasPending')} className="inline-flex" style={{ color: '#fbbf24' }}>
                  <AlertTriangle size={13} strokeWidth={2.5} className="shrink-0" />
                </span>
              </Tooltip>
            )}
            {file.codeNeedsApproval && (
              <Tooltip content={t('driveView.code.needsApprovalHint')} always className="shrink-0 inline-flex pointer-events-auto">
                {file.codeCanApprove && onApproveCode ? (
                  <button
                    type="button"
                    data-testid="code-approve"
                    onClick={(e) => { e.stopPropagation(); onApproveCode(file) }}
                    className="text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap inline-flex items-center gap-1 bg-amber-500 text-black shadow-sm hover:bg-amber-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} className="shrink-0" />
                    {t('driveView.code.approve')}
                  </button>
                ) : (
                  <span
                    data-testid="code-needs-approval"
                    className="text-xs px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-1"
                    style={{ backgroundColor: 'rgba(251, 191, 36, 0.18)', color: '#fbbf24' }}
                  >
                    <AlertTriangle size={12} strokeWidth={2.5} className="shrink-0" />
                    {t('driveView.code.needsApproval')}
                  </span>
                )}
              </Tooltip>
            )}
            {/* SHARING pill — owner view of a folder that has at least one
                active outbound share_grants row (or inherits one inside a
                shared subtree). Server sets `hasOutboundShare`. Rendered AFTER
                the chain badge so a chain's identity reads first. */}
            {file.hasOutboundShare && (
              // Inherited-only rows (flag from a shared ancestor, no grant of
              // their own → no entry in Who-has-access) render dimmed.
              <span
                className={`text-xs px-1.5 py-0.5 rounded file-badge-shared shrink-0 whitespace-nowrap ${file.outboundShareInherited ? 'pointer-events-auto' : ''}`}
                title={file.outboundShareInherited ? 'Shared via a parent folder — this item has no share of its own' : undefined}
              >
                {/* Fade the label only — the pill bg keeps its normal alpha. */}
                <span className={file.outboundShareInherited ? 'opacity-35' : undefined}>SHARING</span>
              </span>
            )}
            {/* T3 fallback — when the Access column is gone (narrow list), an
                admin-floor row keeps a small lock in the name cell so the
                "locked down" signal survives. Normal rows stay quiet. */}
            {isOrg && file.floor === 'admin' && (
              <span className="drive-show-t3 shrink-0" title={t('driveView.access.limitedTip')}>
                <Lock size={12} className="themed-text-muted" />
              </span>
            )}
          </>
        )}
      </div>
      {/* Explicit open-vs-browse affordances on PACKAGE rows (projects and
          mini-apps) — the answer to "does double-click expand it or launch
          it?": it launches (packages behave like apps, macOS-bundle
          semantics), and BOTH verbs are visible on hover so nobody has to
          guess. Hover affordances drop at T1 (Drive's "if dimensions
          permit" rule); double-click + context menu keep both verbs
          reachable at every width. */}
      {(isProject || isMiniapp) && !isRenaming && !file.shareBroken && !file.chainBroken && (
        <div className="drive-hide-t1 flex items-center gap-1 mr-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {(isProject ? onOpenWorkspace : onOpenAsApp) && !(file.isFolder && file.effectivePermission === 'hidden') && (
            <button
              onClick={(e) => { e.stopPropagation(); (isProject ? onOpenWorkspace! : onOpenAsApp!)(file) }}
              onDoubleClick={(e) => e.stopPropagation()}
              className="text-xs px-2.5 py-1 rounded-md themed-btn-primary flex items-center gap-1.5 pointer-events-auto"
              data-testid={isProject ? 'project-row-open' : 'app-row-open'}
            >
              {isProject ? <LayoutGrid size={12} /> : <AppWindowIcon size={12} />}
              {t('driveView.project.open')}
            </button>
          )}
          {onExplore && (
            <button
              onClick={(e) => { e.stopPropagation(); onExplore(file) }}
              onDoubleClick={(e) => e.stopPropagation()}
              title={t('driveView.menu.browseFiles')}
              className="p-1.5 rounded-md themed-text-muted hover:themed-icon-active hover:themed-bg-tertiary transition-colors pointer-events-auto"
              data-testid={isProject ? 'project-row-browse' : 'app-row-browse'}
            >
              <FolderOpen size={14} />
            </button>
          )}
        </div>
      )}
      {/* Hover quick actions on ordinary rows — Drive-style, T0 only, ghosts.
          The ⋮ menu stays the permanent path (hover is never the only way). */}
      {!isProject && !isMiniapp && !isRenaming && !file.shareBroken && !file.chainBroken && (onShareLink || onWhoHasAccess) && (
        <div className="drive-hide-t1 flex items-center gap-0.5 mr-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {onShareLink && (
            <button
              onClick={(e) => { e.stopPropagation(); onShareLink(file) }}
              onDoubleClick={(e) => e.stopPropagation()}
              title={t('driveView.menu.shareLink')}
              className="p-1.5 rounded-md themed-text-muted hover:themed-icon-active hover:themed-bg-tertiary transition-colors pointer-events-auto"
            >
              <Share2 size={13} />
            </button>
          )}
          {onWhoHasAccess && (
            <button
              onClick={(e) => { e.stopPropagation(); onWhoHasAccess(file) }}
              onDoubleClick={(e) => e.stopPropagation()}
              title={t('driveView.menu.whoHasAccess')}
              className="p-1.5 rounded-md themed-text-muted hover:themed-icon-active hover:themed-bg-tertiary transition-colors pointer-events-auto"
            >
              <Users size={13} />
            </button>
          )}
        </div>
      )}
      {/* Access column — restricted/exposed indicator; empty for normal rows;
          hides at T3 (name-cell lock badge takes over). */}
      <div className="w-32 drive-hide-t3 flex items-center justify-end shrink-0">
        <AccessCell
          file={file}
          isOrg={isOrg}
          onManage={onWhoHasAccess}
          t={t}
        />
      </div>
      <div className="w-28 text-xs themed-text-muted text-right shrink-0 pointer-events-none drive-hide-t4" title={file.updatedAt ? new Date(file.updatedAt).toLocaleString() : undefined}>
        {formatDate(file.updatedAt)}
      </div>
      <div className="w-20 text-xs themed-text-muted text-right shrink-0 pointer-events-none drive-hide-t2">
        {formatFileSize(file.size)}
      </div>
      <div className="w-10 flex justify-end relative">
        <button
          ref={menuBtnRef}
          onClick={(e) => {
            e.stopPropagation()
            setShowMenu(!showMenu)
          }}
          className="p-1 themed-text-muted hover:themed-text-secondary opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto"
          data-testid="file-row-menu"
        >
          <MoreVertical size={16} />
        </button>
        {showMenu && createPortal((
          <div
            ref={menuRef}
            data-testid="file-context-menu"
            // `max-h` is against the VIEWPORT, which is now the only thing that
            // can cut this menu off — matching the right-click menu's bound.
            className={`${rowMenuThemeClass} ws-menu fixed z-[100] min-w-[160px] max-h-[calc(100vh-16px)] overflow-y-auto${menuExiting ? ' anchor-exit' : ''}`}
            // Rendered BEFORE it is positioned, deliberately: the hook measures
            // this element, so gating the render on `menuPos` would mean it
            // never mounts, never measures, and never opens. Hidden rather than
            // unmounted for the one layout pass that takes — `useLayoutEffect`
            // resolves it before paint, so nothing flashes at 0,0.
            style={menuPos
              ? {
                  top: menuPos.top,
                  left: menuPos.left,
                  // Collapse toward the ⋯ button on an anchor-exit, not toward
                  // the menu's own middle.
                  ...(menuOrigin ? { transformOrigin: `${menuOrigin.x}px ${menuOrigin.y}px` } : {}),
                }
              : { top: 0, left: 0, visibility: 'hidden' }}
          >
            <FileContextMenu
              file={file}
              onClose={() => setShowMenu(false)}
              onOpenInNewTab={onOpenInNewTab}
              onOpenWorkspace={onOpenWorkspace}
              onOpenWorkspaceInNewTab={onOpenWorkspaceInNewTab}
              onOpenOrgWorkspace={onOpenOrgWorkspace}
              onExplore={onExplore}
              onExploreInNewTab={onExploreInNewTab}
              onOpenAsApp={onOpenAsApp}
              onDownload={onDownload}
              onExtract={onExtract}
              onCopy={onCopy}
              onCut={onCut}
              onWhoHasAccess={onWhoHasAccess}
              onShareInternally={onShareInternally}
              onShare={onShare}
              onShareLink={onShareLink}
              onShareMiniApp={onShareMiniApp}
              folderBreadcrumbs={folderBreadcrumbs}
              onRename={onRename}
              onShowInfo={onShowInfo}
              onDebugLog={onDebugLog}
              onEditIcon={onEditIcon}
              isOrg={isOrg}
              wsAccess={localWsAccess || wsAccess}
              canWrite={canWrite}
              canCopy={canCopy}
              urlPrefix={rowUrlPrefix}
              onDelete={onDelete}
              t={t}
            />
          </div>
        ), document.body)}
      </div>
    </div>
  )
}

/** Read open workspace folder IDs from localStorage for priority ordering */
function getOpenWorkspaceFolderIds(): string[] {
  try {
    const raw = localStorage.getItem('papan-open-workspaces')
    if (!raw) return []
    return (JSON.parse(raw).workspaces || []).map((w: { id: string }) => w.id)
  } catch { return [] }
}

interface DriveViewProps {
  /** Is the drive deck on screen? The shell keeps it MOUNTED when hidden, so
   *  without this a backgrounded drive would keep naming the browser tab. */
  deckActive?: boolean
  /** Resolved tenant ID — must come from the app's tenant context */
  tenantId?: string
  /** Whether the active tenant is an organization */
  isOrg?: boolean
  /** Current user's role in this tenant (e.g. 'owner', 'admin', 'member') */
  userRole?: string
  /** Active org email identity (for permission reactivity on email switch) */
  orgEmail?: string
  /**
   * Display name of the active tenant — the org name for an org, or the
   * user's name for a personal tenant. Used to personalize the drive header
   * ("{{tenant}}'s Drive"). When empty/undefined the header falls back to the
   * generic localized root-folder name.
   */
  tenantName?: string
  onRefreshRef?: MutableRefObject<(() => void) | null>
  /** Called after a file/folder is deleted (for workspace list refresh etc.) */
  onAfterDelete?: () => void
  /**
   * Select the mini-app editor face before it mounts (threaded from App.tsx,
   * which owns @papan/collab — avoids a files → collab dependency edge). Used so
   * "Edit app" opens the Settings face by default.
   */
  onRequestMiniappView?: (folderId: string, view: 'run' | 'settings') => void
  /**
   * File-or-folder picker dialog (passed in to avoid a files → workspace
   * dependency edge — same pattern KeybookView uses). When omitted, the
   * "Chain here" action is hidden (tests / headless environments where
   * the picker isn't mounted).
   */
  FilePickerDialog?: React.ComponentType<{
    tenantId: string
    onSelect: (id: string, isFolder: boolean) => void
    onClose: () => void
    title?: string
  }>
}

export function DriveView({ tenantId: tenantIdProp, isOrg, userRole, orgEmail, tenantName, onRefreshRef, onAfterDelete, onRequestMiniappView, FilePickerDialog, deckActive = true }: DriveViewProps) {
  const { t } = useTranslation('files')
  const toast = useToast()
  const confirm = useConfirm()
  const undo = useUndo()
  // This deck IS the drive's chrome, and it owns the file operations Ctrl+Z
  // reverses (recordDelete / recordMove / recordRename below). Claiming the
  // region when it becomes visible is what takes the keystroke back from a
  // workspace deck that is still mounted behind it — those stay mounted with
  // display:none, so their editor never stops thinking it is active.
  const { setUndoRegion } = undo
  // #801 — the preview-before-confirm gate and the response-aware undo recorders.
  const partitionGate = usePartitionGate()
  const recordDeleteUndo = useCallback((at: UndoCapture, file: { id: string; name: string }, result: Parameters<typeof undo.recordDeleteResult>[3]) => {
    // A split inside a mount binned SOURCE-tenant items — this drive's undo can't restore them.
    if (!result.binId && isMountId(file.id)) return
    undo.recordDeleteResult(at, file.id, file.name, result)
  }, [undo])
  const recordMoveUndo = useCallback((
    at: UndoCapture,
    item: { id: string; name: string }, originalParentId: string | null,
    result: { copied?: boolean; split?: { replay?: boolean; destFolderId?: string; moved: string[] } },
  ) => {
    // #753: a move out of a received `.shared` is a COPY — nothing to move back. A split
    // through a mount moved SOURCE-tenant ids, which don't resolve here.
    if (result.copied || (result.split && isMountId(item.id))) return
    undo.recordMoveResult(at, item.id, originalParentId, item.name, result)
  }, [undo])
  // #801 — preview a folder move — out of a received `.shared` too: since #982 that is a real move (what can't be
  // removed stays there as a shell), not a #753 copy. A COPY is never previewed (`moveItemsInto` skips the gate).
  const gateMove = useCallback(async (item: { id: string; name: string; isFolder: boolean }, destId: string | null, batch?: PinnedBatch) => {
    if (!item.isFolder) return { go: true, confirmed: false } as const
    // The destination rides along, so a move the server will refuse is shown as refused
    // up front (L4 round 6, M-17c) — never a confirm that fails.
    // #1316 review round 2: a pinned batch's preview is sent as its starter, and asks nothing once its session ended
    if (!batch) return partitionGate.gate(item, 'move', () => filesApi.protectedBranches(item.id, 'move', destId))
    return partitionGate.gate(item, 'move', () => filesApi.protectedBranches(item.id, 'move', destId, batch.opts()), { ended: batch.ended })
  }, [partitionGate])
  const newProject = useNewProject()
  const resolveConflict = useConflict()
  // A cross-tenant paste holding pointers stops to ask keep-links / copy-contents (the
  // pointer dialog, copy wording; the drive keeps links by default).
  const [linkedCopyAsk, setLinkedCopyAsk] = useState<{ label: string; resolve: (c: 'files' | 'contents' | null) => void } | null>(null)
  const askPointers = useCallback((label: string) => new Promise<'files' | 'contents' | null>((resolve) => setLinkedCopyAsk({ label, resolve })), [])
  // A FOLDER out of a received `.shared` into this drive lands as a copy across tenants
  // (#753 Copy → Paste keeps the source; #982 a move then bins it in its owning org):
  // the same question for the pointers inside. Resolves
  // true (contents), false (keep links / nothing to ask), or null (cancelled).
  const askForkPointers = useCallback(async (item: { id: string; name: string; isFolder: boolean }, destId: string | null, batch?: PinnedBatch): Promise<boolean | null> => {
    if (!item.isFolder || !item.id.startsWith('s_') || (destId ?? '').startsWith('s_')) return false
    // #1316: a probe dropped at a session end CANCELS (null) — never "no pointers" (that would go on to copy / move as
    // the next session). A pinned batch's probe (review round 2) is sent as its starter and never dropped. Any other
    // failure stays fail-soft.
    let any: boolean
    try { any = await filesApi.hasLinkedPointers([item.id], batch?.opts()) } catch (err) {
      if (isSessionEndedError(err) || isPinnedBatchIdentityLost(err)) return null
      any = false
    }
    if (!any) return false
    // #1316 review round 2: the question is never asked in the session after the batch's own — this item is skipped
    if (batch?.ended()) return null
    const choice = await askPointers(item.name)
    return choice === null ? null : choice === 'contents'
  }, [askPointers])
  // The context menu portals to <body> (see below) to escape the .drive-cq
  // container's containing block + scroll overflow — so it needs the drive
  // theme class re-applied, same as any out-of-ThemedArea portal.
  const { getTheme } = useTheme()
  const contextMenuThemeClass = getThemeClass(getTheme('drive'))
  const queryClient = useQueryClient()
  const { tenant: tenantParam, folderId: urlFolderId } = useParams<{ tenant: string; folderId?: string }>()
  const routerNavigate = useRouterNavigate()
  // Use prop from app-level tenant context; fall back to URL param for backwards compat
  const tenant = tenantIdProp || tenantParam || ''
  const urlPrefix = tenantParam ? `/t/${tenantParam}` : '/~'

  // Drive-root labels. The header/breadcrumb root is personalized with the
  // active tenant's name ("Alice's Drive" / "Acme Corp's Drive"); until the
  // name is loaded it falls back to the generic localized root name.
  // `rootFolderName` is the stable generic name used wherever the root is
  // referenced as a folder *object* (zip-extract destination, access-dialog
  // root labels, "open workspace" at root) — a possessive title doesn't read
  // right when it flows into other strings or becomes an entity name.
  const rootFolderName = t('driveView.rootFolder')
  const driveTitle = tenantName?.trim()
    ? t('driveView.title', { tenant: tenantName.trim() })
    : rootFolderName
  // `driveTitle` is the LOADING-SENSITIVE half of the pair above: `tenantName`
  // arrives with App's `my-tenants` fetch, so it is undefined for a window after
  // the drive is already interactive (the file listing and the tenant list are
  // independent requests, and the listing routinely wins).
  //
  // Every OTHER reader re-renders, so it self-corrects. A `confirm()` message is
  // the exception — it is a ReactNode built once and handed to the provider, so
  // whatever the label was at that instant is frozen into the open dialog for
  // good. Read it through this ref AFTER any await instead: the handler that
  // needs it awaits a network round trip first, which is the same window
  // `my-tenants` lands in. Measured on stagetest 2026-08-13 — `my-tenants`
  // resolved 30ms after the rename fired and ~1.3s before the preview it was
  // waiting on came back, so the dialog quoted "My Drive" while the header two
  // inches above it already read "<Org>'s Drive".
  const driveTitleRef = useRef(driveTitle)
  driveTitleRef.current = driveTitle

  const {
    files, currentFolderId, loading, loadingMore, error,
    hasMore, total, sortBy, sortDir, parentPermission, parentReshareBlocked, parentNeedsApproval,
    navigate, refresh, refreshFolder, loadMore, toggleSort,
    moveFile, createFolder,
  } = useFiles(urlFolderId || null)
  // #897 — the list keeps its place across a phone/desktop layout swap (the
  // phone's Drive screen uses the same key and the same file-id rows).
  const fileListRef = useRef<HTMLDivElement>(null)

  // The person's Delete (#781): inline for small, a followed job for big.
  // Undo needs the bin entry in THIS tenant's bin — a delete through a
  // received share lands in the owner's bin, so it gets none.
  const { deleteItems } = useDeleteRunner({
    onDeleted: (target, outcome, at) => {
      // #801: a whole delete, or the branches a split binned (the folder stays).
      if (at && !outcome.deletedInSource) recordDeleteUndo(at, target, outcome)
    },
    onSettled: () => {
      refresh()
      onAfterDelete?.()
    },
  })


  // Effective folder ID — currentFolderId or papan root
  const effectiveFolderId = currentFolderId || (tenant ? `papan_${tenant}` : null)
  // Display path for a dialog target: the current breadcrumbs, then the item —
  // unless the item IS the folder you are standing in (the background menu's
  // target), whose name is already the last breadcrumb.
  // The folder you are standing in, as a dialog target from the background
  // menu. Carries the listing's permission for it (the capped grant inside a
  // received `.shared`), so Share / Who-has-access cap and gate exactly as the
  // same folder's ROW would — a bare { id, name } read as uncapped and fell back
  // to the drive-wide manage gate (full review A).
  const currentFolderItem = (): FileItem => {
    const name = breadcrumbs[breadcrumbs.length - 1]?.name || driveTitle
    return {
      id: effectiveFolderId!,
      name,
      isFolder: true,
      ...(parentPermission ? { effectivePermission: parentPermission } : {}),
      // Standing AT a received mount's root (its own native row, not an `s_…`
      // child): it is that `.shared` row, so Share caps at the reshare clamp
      // (readcopy) exactly like the row does — not at the mount's permission.
      ...(isSharedPointer(name) && !effectiveFolderId!.startsWith('s_') ? { fileType: 'shared' } : {}),
    } as FileItem
  }
  const folderPathFor = (item: { id: string; name: string }) => {
    const names = breadcrumbs.map(b => b.name)
    if (item.id !== effectiveFolderId) names.push(item.name)
    return `/papan/${names.join('/')}`
  }

  // Pre-fetch ancestor chain for current folder — used by centralized circular move check
  const currentFolderAncestors = useFolderAncestors(currentFolderId)
  const { status: realtimeStatus, onOperationEvent, onOrgEvent, onPermissionChanged } = useRealtime()
  // Refresh the folder the upload LANDED in, not the folder currently open.
  // These differ when the user navigates into a new folder mid-upload: the
  // completion must not yank the open folder's view back to the drop target
  // ("Loading… / 0 items" wedge, #180). refreshFolder reloads the visible list
  // only when the target IS the open folder, else just invalidates its cache.
  const upload = useUploadService({ onComplete: (targetFolderId) => refreshFolder(targetFolderId) })

  // Breadcrumbs from API
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([])
  // The listed folder's path of names below the drive root — what tells a row it is one
  // of the drive's structural folders (#889: `structuralFolderKind`). Null while the
  // crumbs still describe the PREVIOUS folder, so a row never borrows another's place.
  // (The own-space crumb reads "My Space", but that position is judged without its name.)
  const listedPath = useMemo<string[] | null>(() => {
    if (!currentFolderId) return []
    return breadcrumbs[breadcrumbs.length - 1]?.id === currentFolderId ? breadcrumbs.map((b) => b.name) : null
  }, [breadcrumbs, currentFolderId])
  // A row that is one of the drive's own folders (null while the crumbs are stale). The
  // PDApps container is never shared AS A WHOLE — no share link, no org share (owner
  // ruling 2026-10-02; the server refuses both) — so its row offers neither; the drive's
  // other own folders can be shared (their "allow sharing" is off by default).
  const rowStructural = useCallback(
    (file: { isFolder: boolean; name: string }) => (file.isFolder && listedPath ? structuralFolderKind([...listedPath, file.name]) : null),
    [listedPath],
  )

  // Sync URL folder ID to hook when route param changes
  const prevUrlFolderIdRef = useRef(urlFolderId)
  useEffect(() => {
    if (urlFolderId !== prevUrlFolderIdRef.current) {
      prevUrlFolderIdRef.current = urlFolderId
      navigate(urlFolderId || null)
    }
  }, [urlFolderId, navigate])

  // Load breadcrumbs when folder changes — redirect to root if folder doesn't exist
  useEffect(() => {
    // A response for a folder we already left is dropped: the effect re-runs on
    // every navigation AND on dependency churn right after one (orgEmail, t), so
    // two requests can be in flight and the slower — an OLDER folder's — would
    // otherwise land last and replace the current trail (e2e trace 2026-10-11:
    // the chain's late crumbs overwrote `inner`'s), or bounce a live folder as dead.
    let stale = false
    if (currentFolderId) {
      // Reaching a folder whose row is gone (empty crumbs) or whose
      // breadcrumb endpoint 403/404s means some list cache served us a
      // dead id — typically a row that was deleted while another tab held
      // the list within the 30s staleTime, or while WS was disconnected
      // and the file:deleted event was dropped. Flush every list cache
      // before bouncing so the next folder visit refetches fresh, and
      // surface a toast (silent bounces look like a logout / permissions
      // bug). The server intentionally conflates "not found" and "not
      // authorized" for privacy, so the .then(empty) and .catch paths
      // recover identically.
      const recoverFromDeadId = () => {
        queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list'] })
        // #1264: and the other lists that can show it (search, recent, projects, the Shared page)
        reportListedRowRefused(currentFolderId)
        toast.info(t('driveView.toast.folderGone'))
        routerNavigate(`${urlPrefix}/folder`, { replace: true })
      }
      filesApi.getBreadcrumbs(currentFolderId).then(crumbs => {
        if (stale) return
        if (crumbs.length === 0 && urlFolderId) {
          // Root/papan folder also returns empty crumbs, but urlFolderId
          // is undefined for root — so an explicit id with empty crumbs
          // is a dead id, not the root. A locked "No access" folder is NOT
          // empty here: the breadcrumbs endpoint returns its path as a visible
          // shell (server fix), so the user keeps the trail + back-navigation
          // and lands on the empty locked view without bouncing.
          recoverFromDeadId()
        } else {
          // The viewer's own `Persons/<email>` folder reads "My Space", as the
          // sidebar calls it. Renamed HERE, at the one place crumbs enter the
          // view, so the header, the scope chip, the paste target and the tab
          // title all agree. The rule is shared with every other surface that
          // shows a location (`./locations`).
          setBreadcrumbs(nameOwnSpace(crumbs, { viewerEmail: orgEmail, mySpaceLabel: t('driveHome.nav.mySpace') }))
        }
      }).catch(() => { if (!stale) recoverFromDeadId() })
    } else {
      setBreadcrumbs([])
    }
    return () => { stale = true }
  }, [currentFolderId, urlFolderId, urlPrefix, routerNavigate, queryClient, toast, orgEmail, t])

  // Navigate to a folder (update URL, hook syncs via effect)
  const navigateToFolder = useCallback((targetFolderId: string | null) => {
    if (targetFolderId) {
      routerNavigate(`${urlPrefix}/folder/${targetFolderId}`)
    } else {
      routerNavigate(`${urlPrefix}/folder`)
    }
  }, [urlPrefix, routerNavigate])

  // Expose refresh to parent
  useEffect(() => {
    if (onRefreshRef) onRefreshRef.current = refresh
    return () => { if (onRefreshRef) onRefreshRef.current = null }
  }, [onRefreshRef, refresh])

  // File-event subscription lives in useFiles (Phase A.2). It patches the
  // TanStack cache + local accumulator directly when the event carries the
  // row, falling back to debounced refresh() when it can't.

  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [showUploadMenu, setShowUploadMenu] = useState(false)
  const uploadMenuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!showUploadMenu) return
    const onDocClick = (e: MouseEvent) => {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(e.target as Node)) {
        setShowUploadMenu(false)
      }
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [showUploadMenu])
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false)
  const [isPickingChainTarget, setIsPickingChainTarget] = useState(false)
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  /**
   * Rows whose delete is in flight (#732). Set the moment the dialog is
   * confirmed and cleared on the outcome, so the screen answers the click
   * immediately rather than after the 93-126s a large folder takes.
   *
   * NOT an optimistic removal. A delete can time out on the client while the
   * server carries on, so "gone" is a claim this screen cannot make — and if
   * it made it wrongly, putting the row back would be the second surprise.
   * Dimmed says what is true: something is happening to this row.
   *
   * Kept in a per-tab store, not this view's state (#781): navigating away
   * and back while a big folder's request is still out must not drop the
   * row's "Deleting…" before the server's marker can say it.
   */
  const deletingIds = useSyncExternalStore(inFlightDeletes.subscribe, inFlightDeletes.get)
  /** Add/remove ids from the in-flight set without clobbering a concurrent run. */
  const markDeleting = inFlightDeletes.mark
  const lastSelectedRef = useRef<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const revealFileId = searchParams.get('reveal')

  // ─── In-folder search mode ─────────────────────────────────────────
  // The search icon (where Refresh used to live) flips the breadcrumb row
  // into a search box scoped to the current folder (`rootId`). The listing
  // is replaced in place by the shared <SearchResults> core; a chip in the
  // count line widens the scope to the whole drive. Auto-refresh made the
  // manual Refresh button redundant, so this reclaims its slot.
  const [searchOpen, setSearchOpen] = useState(false)
  // Not while searching: the results aren't the folder's rows.
  useSwapScroll(searchOpen ? null : `drive:${urlFolderId || 'root'}`, fileListRef, { items: '[data-file-id]', fraction: false })
  const [searchInput, setSearchInput] = useState('')
  const [searchScoped, setSearchScoped] = useState(true)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debouncedSearch = useDebounced(searchInput.trim(), 300)

  // Clean display name for the current folder (package extensions stripped) —
  // used in the scope chip and the input placeholder.
  const rawFolderName = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : driveTitle
  const currentFolderName = isMiniappFolder(rawFolderName)
    ? stripMiniappFolderExt(rawFolderName)
    : isProjectFolder(rawFolderName)
    ? stripProjectFolderExt(rawFolderName)
    : rawFolderName

  // The folder you are in names the tab. `currentFolderName` and not the raw
  // one: the drive strips package extensions everywhere else it shows a name,
  // and a tab reading "Sales.pdapp" would be the only place it leaks.
  useEffect(() => {
    if (deckActive) setUndoRegion('chrome')
  }, [deckActive, setUndoRegion])

  usePageTitle(currentFolderName, deckActive)

  const openSearch = useCallback(() => {
    setSearchScoped(true)
    setSearchOpen(true)
    requestAnimationFrame(() => searchInputRef.current?.focus())
  }, [])
  const closeSearch = useCallback(() => {
    setSearchOpen(false)
    setSearchInput('')
  }, [])
  const openSearchResult = useCallback((f: FileListItem) => {
    closeSearch()
    // A chain is REVEALED beside its siblings, never entered from search: search rows don't carry
    // `chainNeedsApproval`, and the row's own open runs the D17 gate (I114).
    if (f.isFolder && !isChainPointerRow(f)) routerNavigate(`${urlPrefix}/folder/${f.id}`)
    else routerNavigate(f.parentId ? `${urlPrefix}/folder/${f.parentId}?reveal=${f.id}` : `${urlPrefix}/folder?reveal=${f.id}`)
  }, [closeSearch, routerNavigate, urlPrefix])
  // Leaving the folder (breadcrumb nav, reveal handoff) exits search mode.
  useEffect(() => { setSearchOpen(false); setSearchInput('') }, [currentFolderId])

  // Context menu state
  // The right-click menu belongs at the POINTER but is anchored to the ROW
  // under it: the row is the thing that scrolls away, so it decides when the
  // menu follows and when it exits. `offX/offY` are the click point expressed
  // INSIDE the anchor, so the menu rides along as the row moves.
  //
  // A right-click on empty space anchors to the scroll container instead. That
  // is not a fallback — the menu's target there is the FOLDER, which cannot
  // scroll out from under itself, so a container anchor is the honest one: it
  // never exits and the menu stays put.
  const [contextMenu, setContextMenu] = useState<
    { x: number; y: number; file?: FileItem; anchorEl: HTMLElement; offX: number; offY: number } | null
  >(null)
  const ctxAnchorRef = useRef<HTMLElement | null>(null)
  ctxAnchorRef.current = contextMenu?.anchorEl ?? null
  const [wsAccess, setWsAccess] = useState<{ individual: FolderWorkspaceAccess; organization: FolderWorkspaceAccess } | null>(null)
  const [infoFile, setInfoFile] = useState<FileItem | null>(null)
  const [debugLogFile, setDebugLogFile] = useState<FileItem | null>(null)
  const [iconEditorFile, setIconEditorFile] = useState<FileItem | null>(null)

  // Fetch workspace access when context menu opens on a folder or empty space
  useEffect(() => {
    if (!contextMenu) { setWsAccess(null); return }
    const wsFid = contextMenu.file?.isFolder ? contextMenu.file.id : effectiveFolderId
    if (wsFid) request<{ individual: FolderWorkspaceAccess; organization: FolderWorkspaceAccess }>(`/api/workspaces/folder-access/${wsFid}`).then(setWsAccess).catch(() => {})
  }, [contextMenu, effectiveFolderId])
  const contextMenuRef = useRef<HTMLDivElement>(null)

  // Access dialog state
  const [whoHasAccessFile, setWhoHasAccessFile] = useState<FileItem | null>(null)
  // #897 — an open sharing dialog is kept across a phone/desktop layout swap
  // (with its unsaved changes, held inside it). Share internally has no phone
  // host: it comes back only if the window widens again within the store's
  // claim window (`DRAFT_CLAIM_WINDOW_MS`).
  const [shareInternallyFile, setShareInternallyFile, { clear: clearShareInternally }] = useDraftState<FileItem | null>(tenant ? `drive:${tenant}:shareInternally` : null, null)
  // Bumped when Who-has-access (opened from the Share dialog) closes, so the
  // still-open Share dialog re-fetches recipient permission + re-sharing state.
  const [shareReloadKey, setShareReloadKey] = useState(0)
  const [shareFile, setShareFile] = useState<FileItem | null>(null)
  // Sharing a `.chain` is blocked (the server rejects it too): a share must
  // bind to the real source, not a deletable same-tenant pointer. Intercept the
  // row-level Share gesture so the user gets a clear nudge to share the target
  // folder, instead of a dialog that would fail.
  const handleShareFile = (file: FileItem) => {
    // By KIND: the server's share gates refuse a chain-kind row whatever its name (I110).
    if (pointerKindOf(file) === 'chain') {
      toast.error("Chains can’t be shared — open the folder it points to and share that instead.")
      return
    }
    setShareFile(file)
  }
  // Reverse-share (step 7): dragging own content onto a cross-tenant `.shared`
  // opens the Share dialog PRE-TARGETED at the owner org, operating on the
  // dragged item's source content. Populated from the move PUT's `reverseShare`
  // preview (the move itself is rejected server-side — no orphan/byte-fork).
  const [reverseShare, setReverseShare] = useState<{
    sourceFileId: string; sourceName: string; sourceIsFolder: boolean
    recipientTenantId: string; recipientOrgName: string
  } | null>(null)
  // Chain-escape (owner-side): moving a `.chain` (or a folder containing one)
  // into a cross-tenant shared folder where the chain's target is OUTSIDE the
  // share. The move PUT returns the escape preview (move not yet applied); the
  // dialog lets the owner share the target separately, move anyway, or cancel.
  const [chainEscape, setChainEscape] = useState<{
    fileId: string; targetFolderId: string | null; escape: ChainEscapeInfo
    /** #801: the previewed set the first call carried — the re-call sends it again. */
    expectRemovable?: string[]
    /** For the move undo: the item's name and original parent. `originalParentId`
     *  undefined = unknown (a drag), so only a SPLIT is recorded. */
    name: string
    originalParentId?: string | null
  } | null>(null)
  // #801: what a chain-escape re-call's answer means — a split / replay says so, and the
  // undo follows the answer (a drag, with no known original parent, records a split only).
  const afterChainEscapeMove = useCallback((
    at: UndoCapture,
    m: { fileId: string; name: string; originalParentId?: string | null },
    r: Awaited<ReturnType<typeof filesApi.moveFile>>,
  ) => {
    const msg = moveResultMessage(t, r)
    if (msg) toast.info(msg)
    if (m.originalParentId !== undefined) recordMoveUndo(at, { id: m.fileId, name: m.name }, m.originalParentId, r)
    else if (r.split) recordMoveUndo(at, { id: m.fileId, name: m.name }, null, r)
  }, [t, toast, recordMoveUndo])
  // The selection awaiting its contents-vs-share-file choice. A single live
  // pointer from the row menu, or a bulk selection containing one.
  const [pointerDownload, setPointerDownload] = useState<FileItem[] | null>(null)
  // A folder whose linked pointers have been pre-flighted, awaiting the choice.
  // Only the FOLDER is carried. The pre-flight's `pointers` and `truncated`
  // rode along until the one-zip change, and neither has been read since: the
  // dialog stopped listing them, and the confirm stopped being one download per
  // pointer. State nothing reads is state that later reads as a guarantee.
  const [linkedDownload, setLinkedDownload] = useState<{
    folder: FileItem
    /** Every id to download — one folder, or a whole selection of them. */
    fileIds: string[]
    /** Announced name when the selection is not a single folder. */
    label: string | null
  } | null>(null)
  // "Share the source folders too" opens the Share dialog pre-loaded with the
  // chain targets + the recipient (the move already applied).
  const [shareTarget, setShareTarget] = useState<{
    primaryId: string; primaryName: string; primaryIsFolder: boolean
    alsoShare: Array<{ id: string; name: string; isFolder: boolean }>
    recipientTenantId: string
  } | null>(null)
  // Re-fetch files when role changes (permissions may have changed)
  const prevRoleRef = useRef(userRole)
  useEffect(() => {
    if (prevRoleRef.current !== userRole) {
      prevRoleRef.current = userRole
      refresh()
    }
  }, [userRole, refresh])

  // In personal context the user is the sole member of their own personal
  // tenant — they own everything in it, so role-gate is unconditionally
  // satisfied. In org context the standard role check applies. (Cross-tenant
  // .shared rows are governed by the resolver, not this menu-visibility flag.)
  const canManageAccessByRole = isOrg
    ? (userRole === 'owner' || userRole === 'admin')
    : true
  // `currentFolderPerm` is the folder's STORED permission row for this user
  // (via `/permission`), distinct from `parentPermission` from the listing
  // which is server-elevated for hallway folders. It exists for exactly one
  // consumer below: `canManageAccess`, which must read the un-elevated stored
  // perm — a member sitting in a hallway folder shouldn't see "Manage Access"
  // just because the listing elevated their view to 'readcopy'. Copy/Cut
  // gates moved to `parentPermission` (see the block right below) precisely
  // because they want the elevated value. Don't collapse the two: they
  // diverge intentionally in hallway folders.
  const [currentFolderPerm, setCurrentFolderPerm] = useState<string | null>(null)
  const refreshFolderPerm = useCallback(() => {
    if (!effectiveFolderId || !isOrg) { setCurrentFolderPerm(null); return }
    // `/permission`, not `/count` (getFileStats): only the permission is read
    // here, and `/count` now walks the subtree whenever part of it is unreadable
    // to the caller — paying that on every folder navigation for nothing.
    // Equivalent for this consumer: a hallway folder 403s here (→ null) where
    // `/count` answered 'none'; a Hidden folder answers 'hidden' where `/count`
    // presents 'readcopy' — neither is admin/owner.
    // Read with every other permission asked at that moment (`loadPermission`, #1259). Only a
    // refusal clears it — a 429 / network failure keeps the last answer (#1260).
    const folder = effectiveFolderId
    loadPermission(folder)
      .then(setCurrentFolderPerm)
      .catch((error: unknown) => {
        const status = (error as { status?: number } | null)?.status
        if (status === 403 || status === 404) setCurrentFolderPerm(null)
      })
  }, [effectiveFolderId, isOrg, orgEmail])
  useEffect(() => { refreshFolderPerm() }, [refreshFolderPerm])
  // Re-check folder permission on role/access changes in real time — at most once per interval
  // (`createAccessBurst`; a folder change re-checks by itself, above, so dropping a pending one with
  // the old folder loses nothing).
  // #1259: skipped for a change naming another member; a hidden drive deck re-checks once when shown.
  const whenShownPerm = useWhenShown()
  useEffect(() => {
    const burst = createAccessBurst(() => whenShownPerm(() => refreshFolderPerm()))
    const off = onOrgEvent((event) => {
      if ((event.type === 'org:access_changed' || event.type === 'org:role_changed') && changesViewerAccess(event)) burst.push()
    })
    // the deferred reaction closes over THIS folder: dropped with it
    return () => { off(); burst.cancel(); whenShownPerm.cancel() }
  }, [onOrgEvent, refreshFolderPerm, whenShownPerm])
  // Auto-refresh the LISTING (and thus the Access column's floor / grantee /
  // link / outsider data AND the per-row SHARING badge / `hasOutboundShare`)
  // on permission + share-link changes — same signals the breadcrumb access
  // bar uses. onOrgEvent covers grant / role / team / restricted;
  // onPermissionChanged also catches share-link mint/revoke (which fire ONLY
  // permission:changed). Debounced so a change firing both events refetches
  // the current folder once.
  //
  // NOT gated on isOrg: the SHARING badge renders on personal drives too, and
  // share-link mint/revoke on a personal file publishes permission:changed to
  // the personal tenant. Gating this on isOrg left the personal-drive badge
  // stale until manual refresh (issue #227). The org-only Access column/bar
  // self-gate on isOrg in their own render, so a personal-drive refresh here
  // only re-runs the listing fetch the badge needs.
  // At most once per interval (`createAccessBurst`), not once per change: an admin's run of team
  // edits reaches every member's tab (gnexis 2026-10-21).
  // The burst lives as long as the FOLDER does: `refresh` changes with the sort too, and a re-run
  // there would drop a pending refresh while the user is still in the folder — it runs the latest
  // `refresh` through a ref instead (set in a layout effect, never during render).
  const refreshRef = useRef(refresh)
  useLayoutEffect(() => { refreshRef.current = refresh })
  // A hidden drive deck re-reads once when shown (#1259). Not skipped for a change naming another
  // member: the listing carries the Access column (who has access).
  const whenShownList = useWhenShown()
  useEffect(() => {
    const burst = createAccessBurst(() => whenShownList(() => refreshRef.current()))
    const offOrg = onOrgEvent((event) => {
      if (event.type === 'org:access_changed' || event.type === 'org:role_changed') burst.push()
    })
    const offPerm = onPermissionChanged(() => burst.push())
    return () => {
      offOrg(); offPerm()
      // Left the folder (or unmounted) with its refresh still pending: mark its lists stale, or
      // coming back within their staleTime serves the pre-change listing — a change that arrives
      // only as permission:changed (a link minted / revoked) has nothing else marking it. One
      // waiting for a hidden deck counts as pending too.
      const deferred = whenShownList.pending()
      whenShownList.cancel()
      if (burst.cancel() || deferred) void queryClient.invalidateQueries({ queryKey: [...fileKeys.all, 'list', currentFolderId ?? null], refetchType: 'none' })
    }
  }, [onOrgEvent, onPermissionChanged, queryClient, currentFolderId, whenShownList])
  const canManageAccess = canManageAccessByRole || (currentFolderPerm === 'admin' || currentFolderPerm === 'owner')
  // Per-row gate for context menus targeting an item in the listing. The
  // folder-level `canManageAccess` is wrong here — a user with admin on
  // `test/` standing in its parent would otherwise see "Who has access..."
  // hidden when right-clicking `test/`, even though the menu targets the
  // row's folder, not the parent. Falls back to the folder-level gate when
  // the row was built from a realtime event (no inlined effectivePermission).
  const rowCanManageAccess = (file: FileItem) =>
    // A broken/revoked `.shared` is a dead pointer — there's no access left to
    // manage. Hide the manage actions (Who-has-access, Share) no matter WHY the
    // viewer would otherwise qualify.
    !file.shareBroken && (
      // Gate on the server-resolved per-row permission (= manage_folder_permission)
      // — NO role check. The owner floor lives in the resolution itself: an owner
      // now resolves to `owner` on every row (getPermissionOverrides owner floor),
      // so they qualify here; an admin restricted on a folder correctly does not;
      // a folder-admin member manages a child they're admin on but not one they
      // hold a read-only/hidden grant on. Realtime-event rows lack the inlined
      // permission → fall back to the folder-level gate.
      file.effectivePermission !== undefined
        ? canManageAccessFor(file.effectivePermission)
        : (canManageAccessByRole || canManageAccess)
    )
  // "Share to another Drive" is a SHARING action, NOT access-management. It is
  // gated SERVER-side on the sharing capability (the member/role allow-sharing
  // flag + the tenant external-share wall + Gate A on re-shares), so a plain
  // member with readwrite + allow-sharing can share — same as the empty-area
  // menu. The only client gate is hiding it for a broken `.shared` (a dead
  // pointer with nothing to share). Do NOT re-couple this to rowCanManageAccess
  // (owner/admin only) — a 2026-06 merge did, which hid Share from members.
  const rowCanShare = (file: FileItem) => !file.shareBroken
  /**
   * Share link… — structural folders cannot be shared whole, and a DEAD pointer has
   * nothing to hand out.
   *
   * The pointer half was missing, and the three checks around here each look like
   * they cover it and do not: `rowCanShare` above gates "Share to another Drive";
   * the `!shareBroken && !chainBroken` test further up is on the HOVER quick-action;
   * and the "A dead `.chain` stays fully inert (Delete only)" comment sits on the
   * rename/move/copy block. So the ⋮ menu offered Share link… on a revoked `.shared`
   * and on a `.chain` whose target is gone, the dialog opened (its `POST /default`
   * answers 201), and the mint then failed with a hard `403 no_file_access`
   * (measured against the real route) — #327's "the UI offered an action that could
   * never succeed", in the one place that issue did not reach.
   *
   * One predicate, not the condition written twice: the branch this follows spent
   * three review rounds on a flag copied into one call site and not the other.
   *
   * Not applied to the CURRENT FOLDER's Share link… below. `currentFolderItem()` is
   * synthesised from the breadcrumbs and carries neither flag, so there is nothing
   * to read; standing inside a dead or reshare-blocked mount is the separate
   * `parentReshareBlocked` layer.
   */
  const rowCanShareLink = (file: FileItem) =>
    !structuralFolderBlocksShare(rowStructural(file)) && !file.shareBroken && !file.chainBroken
  // Permission-based capabilities: the folder's resolved permission (`parentPermission`).
  //
  // The folder gate reads `parentPermission` from the listing rather than the
  // stored permission from getFileStats. They diverge for hallway folders:
  // a member with grants only on descendants sees the stored perm as 'hidden'
  // but the server elevates parentPermission to 'readcopy' (so the user can
  // open the folder AND copy the descendants they have grants on). Reading
  // the stored perm here disables copy/cut/paste inside hallway folders
  // even though the server's /copy + /download endpoints accept it.
  // The freeze is tenant state, read once by the shell's provider so the drive and
  // the chat cannot disagree about it while one is being lifted.
  const { uploadsPaused, refresh: refreshWriteStatus } = useWriteStatus()
  // #872 (owner, option A): the folder's RESOLVED permission alone decides — an explicit Edit
  // grant lets a Viewer or Guest write, and no role caps it (the server never did). The listing
  // reports the drive root's resolved permission too, so a role check is not needed to hide
  // New Folder at a root the caller can't write.
  // Unknown (still loading, or the listing failed) allows nothing — the role used to cover that gap.
  const folderCanWrite = canWriteFor(parentPermission)
  const folderCanCopy = !!parentPermission && ['admin', 'readwrite', 'readcopy', 'owner'].includes(parentPermission)
  /**
   * Manual billing's write freeze (§12). A SECOND axis on top of the folder
   * permission, and it composes by AND with it — but it is not a
   * permission: it says "not right now", not "not you".
   *
   * `canCreate` is deliberately separate from `canWrite`. The freeze blocks NEW
   * items only; renaming, moving, editing and deleting all still work, and folding
   * it into `canWrite` would disable half the drive for a customer who has lost
   * nothing but the ability to add. That would be the strict-direction failure
   * decision 5 exists to avoid, reproduced in the UI.
   */
  const canWrite = folderCanWrite
  const canCreate = canWrite && !uploadsPaused
  const canCopy = folderCanCopy

  // Drag state
  const [draggedIds, setDraggedIds] = useState<Set<string>>(new Set())
  const [dropTargetId, setDropTargetId] = useState<string | null>(null)
  const [dropTargetCrumb, setDropTargetCrumb] = useState<string | null>(null)
  const [showDropOverlay, setShowDropOverlay] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const [forbiddenDropId, setForbiddenDropId] = useState<string | null>(null)
  const dragCounterRef = useRef(0)

  // Clipboard state (persisted in localStorage for cross-tab sync)
  const CLIPBOARD_KEY = 'papan-clipboard'
  // (#752) A COPY clipboard survives a tenant switch — that is how a
  // cross-tenant copy is reached (copy in A → switch to B → paste). A CUT from
  // another tenant is not pasteable (moves never cross tenants), so it reads as
  // empty here rather than offering a Paste that can only refuse.
  const readClipboard = useCallback((): ClipboardData | null => {
    try {
      const stored = localStorage.getItem(CLIPBOARD_KEY)
      if (!stored) return null
      const parsed = JSON.parse(stored)
      // Validate shape — reject stale/malformed data
      if (!parsed || !Array.isArray(parsed.items)) return null
      if (parsed.isCut && parsed.tenantId && parsed.tenantId !== tenant) return null
      return parsed
    } catch {
      return null
    }
  }, [tenant])
  const [clipboard, setClipboardState] = useState<ClipboardData | null>(readClipboard)

  const setClipboard = useCallback((value: ClipboardData | null) => {
    setClipboardState(value)
    if (value) {
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(value))
    } else {
      localStorage.removeItem(CLIPBOARD_KEY)
    }
  }, [])
  // #1316 review round 2: a cut is spent once its paste ran — cleared whether or not the paste's session ended (it
  // finished as its starter; re-pasting would move the items again), but only while the clipboard still holds it
  const consumeCut = useCallback((cut: ClipboardData) => {
    if (clearSpentCut(cut)) setClipboardState(null)
  }, [])

  // Sync clipboard across tabs (`storage`), and re-read on focus and on a
  // tenant change: a same-tab writer (WorkspaceExplorer, another deck) fires no
  // `storage` event, and a switch changes which cut clipboard is pasteable.
  useEffect(() => {
    const sync = () => setClipboardState(readClipboard())
    const handleStorageChange = (e: StorageEvent) => { if (e.key === CLIPBOARD_KEY) sync() }
    sync()
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('focus', sync)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('focus', sync)
    }
  }, [readClipboard])

  // Reveal file: select and scroll to it when ?reveal= param is present
  useEffect(() => {
    if (!revealFileId || loading || files.length === 0) return
    const target = files.find(f => f.id === revealFileId)
    if (target) {
      setSelectedIds(new Set([revealFileId]))
      lastSelectedRef.current = revealFileId
      // Clear the param without re-navigation
      setSearchParams(prev => { prev.delete('reveal'); return prev }, { replace: true })
      // Scroll to the element after render
      requestAnimationFrame(() => {
        document.querySelector(`[data-file-id="${revealFileId}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      })
    }
  }, [revealFileId, loading, files]) // eslint-disable-line react-hooks/exhaustive-deps

  // Get selected files
  const selectedFiles = files.filter(f => selectedIds.has(f.id))

  // Clear selection when navigating
  const handleNavigate = useCallback((targetFolderId: string | null) => {
    setSelectedIds(new Set())
    lastSelectedRef.current = null
    navigateToFolder(targetFolderId)
  }, [navigateToFolder])

  const handleSelect = useCallback((file: FileItem, e: React.MouseEvent) => {
    const fileIndex = files.findIndex(f => f.id === file.id)

    if (e.shiftKey && lastSelectedRef.current) {
      const lastIndex = files.findIndex(f => f.id === lastSelectedRef.current)
      if (lastIndex !== -1) {
        const start = Math.min(fileIndex, lastIndex)
        const end = Math.max(fileIndex, lastIndex)
        const rangeIds = files.slice(start, end + 1).map(f => f.id)
        setSelectedIds(new Set(rangeIds))
      }
    } else if (e.metaKey || e.ctrlKey) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(file.id)) {
          next.delete(file.id)
        } else {
          next.add(file.id)
        }
        return next
      })
      lastSelectedRef.current = file.id
    } else {
      setSelectedIds(new Set([file.id]))
      lastSelectedRef.current = file.id
    }
  }, [files])

  // Context menu handler
  const handleContextMenu = useCallback((file: FileItem, e: React.MouseEvent) => {
    e.preventDefault()
    if (!selectedIds.has(file.id)) {
      setSelectedIds(new Set([file.id]))
      lastSelectedRef.current = file.id
    }
    const el = e.currentTarget as HTMLElement
    const r = el.getBoundingClientRect()
    setContextMenu({ x: e.clientX, y: e.clientY, file, anchorEl: el, offX: e.clientX - r.left, offY: e.clientY - r.top })
  }, [selectedIds])

  // ppn.gg share-link management. Both menu actions (App / public link)
  // open the unified ShareLinksDialog where the user sees existing links
  // for this file/folder, manages them (revoke / expiry / max-views), and
  // mints new ones. The dialog handles the mint+copy tail internally.
  // Shared with the phone drive (#897, same key and shape): a layout swap
  // reopens the same file's dialog there. (See the mount check below.)
  const [shareLinksDialog, setShareLinksDialog, { clear: clearShareLinksDialog }] = useDraftState<{
    file: FileItem
  } | null>(tenant ? `drive:${tenant}:shareLinks` : null, null)

  // Decisions 5 + 2: one entry-point for the unified dialog. The dialog
  // surfaces a Mode (App vs Public) + Live/Snapshot picker internally and
  // shows every existing link for the file regardless of kind.
  // #897 — this deck stays mounted (hidden) on other pages, and its dialogs are
  // portaled: one a layout swap restored into a deck that mounts HIDDEN would
  // float over whatever page is on screen. Dropped instead. (A dialog the user
  // opened here and left open stays as it was — only a restore is dropped.)
  useEffect(() => {
    if (deckActive) return
    clearShareLinksDialog()
    clearShareInternally()
    // Mount-time only: a later hide must not drop a dialog the user opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleShareLink = useCallback(
    (file: FileItem) => setShareLinksDialog({ file }),
    [],
  )

  // "Share PDApp": share the `.miniapp` folder that a right-clicked HTML file
  // lives inside (single ancestor → straight to the share dialog on the folder;
  // nested → a picker first). Owns its own dialog/picker via `.node` below.
  const miniAppShare = useMiniAppShare()

  // Clipboard handlers
  const handleCopy = useCallback(() => {
    if (selectedFiles.length === 0) return
    if (!canCopy) {
      toast.error('You need Read-only permission to copy files')
      return
    }
    // A revoked/broken `.shared` is a real row — copy/move it like a normal folder.
    // A dead `.chain` stays inert (excluded) — guarded by e2e/chain-primitive.spec.ts.
    const items = selectedFiles.filter(f => !f.chainBroken)
    if (items.length === 0) return
    setClipboard({
      items: items.map(f => ({ id: f.id, name: f.name, isFolder: f.isFolder, parentId: f.parentId })),
      isCut: false,
      tenantId: tenant,
      // Capture the active org email so cross-tenant paste resolves
      // source-tenant permissions as the identity the user was viewing
      // with, not as their session-default primary email.
      orgEmail,
      sourceLabel: driveLabel(t, isOrg, tenantName),
    })
  }, [selectedFiles, setClipboard, tenant, canCopy, toast, orgEmail, t, isOrg, tenantName])

  const handleCut = useCallback(() => {
    if (selectedFiles.length === 0) return
    if (!canWrite) {
      toast.error('You need Read & Write permission to cut files')
      return
    }
    // A revoked/broken `.shared` is a real row — moving it is a local reorganize.
    // A dead `.chain` stays inert (excluded) — guarded by e2e/chain-primitive.spec.ts.
    const items = selectedFiles.filter(f => !f.chainBroken)
    if (items.length === 0) return
    setClipboard({
      items: items.map(f => ({ id: f.id, name: f.name, isFolder: f.isFolder, parentId: f.parentId })),
      isCut: true,
      tenantId: tenant,
      orgEmail,
    })
  }, [selectedFiles, setClipboard, tenant, canWrite, toast, orgEmail])

  // Helper: move files to a target folder with conflict handling
  const moveFilesToFolder = useCallback(async (
    filesToMove: { id: string; name: string; isFolder: boolean }[],
    targetFolderId: string | null
  ) => {
    if (filesToMove.length === 0) return
    // A drag-and-drop move is undoable like Cut → Paste: the drag carries no parent, so it comes from the listing the
    // item was dragged out of (manual QA of #1167). An item the listing doesn't hold: only a split is undoable.
    const recordDragMoveUndo = (at: UndoCapture, file: { id: string; name: string }, r: Parameters<typeof recordMoveUndo>[3] & { movedAcrossOrgs?: unknown }) => {
      const from = dragMoveUndoParent(files, file.id)
      if (from !== undefined) recordMoveUndo(at, file, from, r)
      else if (r.split || r.movedAcrossOrgs) recordMoveUndo(at, file, null, r)
    }

    // #1316 review round 2 (owner, 2026-10-28): the move the person started FINISHES as the identity that started it —
    // every request below is sent as it (`batch.opts()`), a tenant / identity switch mid-batch neither stops it nor sends
    // the rest as the next session; what it SHOWS (toasts, progress, a question, an undo record) stops with its session.
    const batch = pinnedBatch()
    const at = captureUndoContext()   // D134: the batch's identity — every item's requests are sent as it
    const shown = () => !batch.ended()

    // Check conflicts
    const conflicts = await filesApi.checkConflicts(
      filesToMove.map(f => ({ name: f.name, parentId: targetFolderId })),
      batch.opts(),
    )
    const conflictNames = new Set(conflicts.map(c => c.name))
    // The EXISTING item at each conflicting name, as the server found it — a folder not open here, or
    // another org's file through its `.shared`, is in no listing this view holds.
    const conflictAt = existingFromConflicts(conflicts)

    // Resolve conflicts if any
    let applyToAll: ConflictResolution | null = null
    const resolutions = new Map<string, ConflictStrategy>()

    for (const file of filesToMove) {
      if (conflictNames.has(file.name)) {
        // never a question in the session after the batch's own: the conflicting item is left where it is
        if (!shown()) { resolutions.set(file.id, 'skip'); continue }
        if (applyToAll) {
          resolutions.set(file.id, applyToAll)
        } else {
          const result = await resolveConflict({
            fileName: file.name,
            sourcePath: file.name,
            destPath: file.name,
            isFolder: file.isFolder,
            operation: 'move' as ConflictOperation,
          })
          resolutions.set(file.id, result.resolution)
          if (result.applyToAll) applyToAll = result.resolution
        }
      }
    }

    // Execute moves
    const movedNames: string[] = []
    let shareLinkAdvisoryTotal = 0
    const pinnedApi = { replaceByMove: (dest: string, src: string) => filesApi.replaceByMove(dest, src, batch.opts()) }
    const pinnedClearApi = replaceClearApiAs(() => batch.opts())
    for (const file of filesToMove) {
      const strategy = resolutions.get(file.id)
      if (strategy === 'skip') continue

      try {
        // Ask BEFORE anything is changed: cancelling must leave the destination untouched.
        const forkExpand = await askForkPointers(file, targetFolderId, batch)
        if (forkExpand === null) continue
        // #801: then the partition preview (after the pointer dialog, before the
        // destination is touched by a replace).
        const pg = await gateMove(file, targetFolderId, batch)
        if (!pg.go) continue
        const expect = pg.confirmed ? { expectRemovable: pg.expectRemovable } : {}
        if (strategy === 'replace') {
          const existing = files.find(f => f.name === file.name && f.parentId === targetFolderId) ?? conflictAt.get(file.name)
          // Replace (owner, 2026-10-06): a file onto a file is a new VERSION of the existing file
          // (it keeps its id, history and links); the moved file then goes to the Bin.
          // A destination in ANOTHER organisation takes no Replace: nothing is touched there and the
          // ordinary move below answers (the reverse-share prompt), as on main (`moveReplaceStep`).
          const step = await moveReplaceStep(pinnedApi, file, existing)
          if (step.kind === 'versioned') {
            // ONE undo step for the whole move: restore the destination's previous version AND bring the
            // moved file back from the Bin (a fork out of a `.shared` left it where it was).
            const plan = moveAsVersionUndo(step.r, file)
            if (plan?.kind === 'version') undo.recordAddVersion(at, plan.fileId, plan.previousVersionId, file.name, plan.restoreSource, plan.versionId)
            else if (plan?.kind === 'delete') recordDeleteUndo(at, file, plan.deleted)
            movedNames.push(file.name)
            continue
          }
          // A folder / pointer on either side has no versions: bin the conflicting item, then move.
          if (existing && step.kind === 'binThenMove') {
            // #801: never a half-replaced folder — one holding anything the caller
            // can't delete is left alone and THIS item's move is aborted. A delete
            // that split anyway (a race) is recorded so its bin items can be undone.
            const cleared = await clearReplaceTarget(existing, pinnedClearApi)
            if (!cleared.cleared) {
              if (cleared.partial) recordDeleteUndo(at, existing, cleared.partial)
              if (shown()) toast.error(t('protectedBranches.replaceAborted', { name: file.name }))
              continue
            }
          }
        }
        const result = await withMoveProgress(toast, t('driveView.toast.moving', { name: file.name }), (hooks) => filesApi.moveFile(file.id, { parentId: targetFolderId, ...(forkExpand ? { expandPointers: true } : {}), ...expect }, { ...hooks, ...batch.opts() }), batch.ended)
        if (result?.crossTenant && result.reverseShare) {
          // Reverse-share gesture (step 7): the destination is a cross-tenant
          // `.shared` — this isn't a move, it's an intent to share the dragged
          // content BACK to the owning org. Open the Share dialog pre-targeted
          // at that org (operating on the dragged item's source content). One
          // dialog at a time, so STOP here: a multi-select drag opens it for the
          // FIRST such item and leaves the rest to re-drag — vs. `continue`,
          // which would let a later item overwrite the dialog and silently move
          // the trailing ones.
          if (shown()) setReverseShare(result.reverseShare)
          break
        }
        if (result?.chainEscape && result.escape) {
          // Destination is outbound-shared and this chain's target escapes the
          // share — prompt before landing a greyed/inert link. One dialog at a
          // time; STOP here so a multi-select drag opens it for the FIRST
          // escaping item and doesn't silently move the trailing ones.
          if (shown()) setChainEscape({ fileId: file.id, targetFolderId, escape: result.escape, name: file.name, ...expect })
          break
        }
        if (result?.accessChange) {
          if (!shown()) continue   // a question / refusal for the session that ended: this item stays where it is
          if (result.needsConfirmation) {
            // User has admin on both — ask to confirm
            const diffText = (result.diff ?? []).map((d: any) =>
              `${d.label ?? d.targetId}: ${d.fromLabel ?? d.fromPerm ?? 'none'} → ${d.toLabel ?? d.toPerm ?? 'none'}`
            ).join('\n')
            const confirmed = await confirm({
              title: 'Access will change',
              message: `Moving "${file.name}" changes permissions:\n\n${diffText}`,
              type: 'info',
              confirmText: 'Move anyway',
            })
            if (confirmed) {
              const confirmedResult = await withMoveProgress(toast, t('driveView.toast.moving', { name: file.name }), (hooks) => filesApi.moveFile(file.id, { parentId: targetFolderId, confirmedAccessChange: true, ...expect }, { ...hooks, ...batch.opts() }), batch.ended)
              const splitMsg = moveResultMessage(t, confirmedResult)
              if (splitMsg) { if (shown()) toast.info(splitMsg) }
              else movedNames.push(file.name)
              recordDragMoveUndo(at, file, confirmedResult)
              if (confirmedResult?.shareLinkAdvisory) shareLinkAdvisoryTotal += confirmedResult.shareLinkAdvisory.count
            }
          } else {
            // User lacks admin — blocked
            toast.error(result.error || 'Moving this changes access. You need Manage & Edit on both folders.')
          }
          continue
        }
        // #801: a split (or its replay) says what moved and what stayed behind.
        const splitMsg = moveResultMessage(t, result)
        if (splitMsg) { if (shown()) toast.info(splitMsg) }
        else movedNames.push(file.name)
        recordDragMoveUndo(at, file, result)
        if (result?.shareLinkAdvisory) shareLinkAdvisoryTotal += result.shareLinkAdvisory.count
      } catch (err: any) {
        if (shown()) toast.error(moveErrorText(err, t, file.name))
      }
    }

    // #1316 review round 2: the batch finished as its starter — nothing of it is shown in the session after its own
    if (!shown()) return

    if (movedNames.length > 0) {
      toast.success(movedNames.length > 1
        ? t('driveView.toast.moved', { name: movedNames[0], count: movedNames.length - 1 })
        : t('driveView.toast.movedOne', { name: movedNames[0] }))
    }

    // Decision 9 / Phase 29: non-blocking advisory when the moved files
    // (or their descendants) are referenced by active share-links. The
    // link continues to work — Decision 9's id-stable grant model means
    // moves don't break links — but the owner often doesn't realize
    // they just relocated something that's actively shared.
    if (shareLinkAdvisoryTotal > 0) {
      toast.info(`${shareLinkAdvisoryTotal} active share-link${shareLinkAdvisoryTotal === 1 ? '' : 's'} point at moved content. Assets still load from the originally-picked root.`)
    }

    setSelectedIds(new Set())
    refresh()
  }, [files, moveFile, refresh, toast, t, resolveConflict, confirm, askForkPointers, gateMove, recordMoveUndo, recordDeleteUndo, undo])

  const isPastingRef = useRef(false)
  const handlePaste = useCallback(async () => {
    if (!clipboard) return
    // A paste while the last one still runs (a big folder's move takes minutes, #1167 S4) is not queued — say so.
    if (isPastingRef.current) {
      // Under ONE id: pressing again replaces the notice instead of stacking another.
      toast.info(t('driveView.toast.pasteStillRunning'), undefined, undefined, { id: PASTE_STILL_RUNNING_TOAST_ID })
      return
    }
    isPastingRef.current = true

    const { items, isCut, tenantId: clipboardTenantId } = clipboard
    const isCrossTenant = !!clipboardTenantId && clipboardTenantId !== tenant
    // #1316 review round 2 (owner, 2026-10-28): the paste the person started FINISHES as the identity that started it —
    // every request below is sent as it (`batch.opts()`): a tenant / identity switch mid-paste neither stops it nor
    // sends the rest as the next session. What it SHOWS (toasts, progress, a question, an undo record) stops with its
    // session — the drive view it belongs to is gone then.
    const batch = pinnedBatch()
    const at = captureUndoContext()   // D134: the paste's identity — every request is sent as it
    const shown = () => !batch.ended()

    try {
      // `.shared` content carries a synthetic mount id (`s_<mount>_<src>`; native
      // ids never contain `_`). It belongs to ANOTHER tenant, reachable via a
      // GRANT — not membership — so the membership-based cross-tenant copy below
      // can't resolve it (the clipboard's tenantId is the viewing tenant, not the
      // owner, so `isCrossTenant` is even false). Copying such an item out to your
      // own drive is exactly what the DRAG does: a cross-tenant move the server
      // resolves to a COPY (original preserved). Route it through that same move
      // endpoint, which resolves the mount + grant.
      // (#752) A mount id resolves only in the tenant that RECEIVED the share —
      // copied there and pasted into another tenant it cannot be found. Refuse
      // plainly before either path below tries it.
      if (isCrossTenant && items.some(i => i.id.startsWith('s_'))) {
        toast.error(t('driveView.crossTenantPaste.mountRefused'))
        isPastingRef.current = false
        return
      }
      if (items.length > 0 && items.every(i => i.id.startsWith('s_'))) {
        const destFolderId = effectiveFolderId ?? currentFolderId
        // Copy → Paste COPIES (the source stays); Cut → Paste MOVES — out of the mount that removes
        // the source in its owning tenant, gated by permission (owner, 2026-10-06; a read-only one
        // is refused, never silently copied) — and a #801 move can split. Each folder is previewed
        // like the drag (`gateMove`); the toast follows the server's answer.
        await moveItemsInto(items, destFolderId, { t, toast, undo, copy: !isCut, gate: item => gateMove(item, destFolderId, batch), batch })
        // A cut is spent once pasted: the moved items left the share (#982), so a second paste
        // would only fail against the gone sources — as the plain cut-paste below clears it too.
        // Spent whatever the session is now (#1316 review round 2); only the display stops with it.
        if (isCut) consumeCut(clipboard)
        if (shown()) {
          setSelectedIds(new Set())
          refresh()
        }
        isPastingRef.current = false
        return
      }

      // Cross-tenant: only copy supported (no cut/move)
      if (isCrossTenant) {
        if (isCut) {
          toast.error(t('driveView.toast.crossTenantMove'))
          isPastingRef.current = false
          return
        }
        // (#752) Bytes are about to cross an org boundary — ask first, naming both drives.
        const from = clipboard.sourceLabel ?? await resolveDriveLabel(t, clipboardTenantId, undefined, undefined)
        const to = await resolveDriveLabel(t, tenant, isOrg, tenantName)
        // The label fetch above can outlive a tenant / identity switch (this deck is keyed by the session and unmounts) —
        // never ASK in the session after the paste's own: nothing was confirmed, nothing is pasted
        if (!shown() || getActiveTenantId() !== tenant) {
          isPastingRef.current = false
          return
        }
        const ok = await confirmCrossTenantPaste(confirm, t, { count: items.length, from, to })
        // confirmed: the paste finishes as its starter (#1316 review round 2), whatever the session is by now
        if (!ok) {
          isPastingRef.current = false
          return
        }
        try {
          // Go through pasteItems so the user gets the same conflict
          // dialog as a same-tenant paste. The cross-tenant routing +
          // sourceOrgEmail hint are passed via options. Without this
          // dance the server silently auto-renamed conflicting items
          // (getUniqueName → "Folder (1)") instead of asking.
          const pasted = await pasteItems(items, effectiveFolderId, 'copy', resolveConflict, {
            crossTenant: { sourceTenantId: clipboardTenantId, sourceOrgEmail: clipboard.orgEmail },
            askPointers,
            batch,
          })
          if (!shown()) { isPastingRef.current = false; return }
          const { errors } = pasted
          // Files a Replace wrote a new version into count as pasted too (reported apart from `copied`).
          const summary = pasteToastText(t, pasted.copied.map((x) => x.name), (pasted.versioned ?? []).map((x) => x.name))
          if (summary) {
            toast.success(summary)
            const rec = pasteUndoRecord(pasted)
            if (rec) undo.recordCopy(at, rec.copiedFileIds, rec.versioned)
          }
          // Same per-source error surface as same-tenant copy. The server's
          // per-source try/catch previously swallowed these into copied:[],
          // so a hallway folder that hit a name collision at the destination
          // would have silently produced nothing.
          if (errors.length > 0) {
            const itemById = new Map(items.map(i => [i.id, i.name]))
            const describe = (e: { sourceId: string; message: string }) =>
              `${itemById.get(e.sourceId) ?? e.sourceId}: ${e.message}`
            toast.error(errors.length > 1
              ? `${errors.length} items failed to copy — ${describe(errors[0])} (+${errors.length - 1} more)`
              : `Copy failed — ${describe(errors[0])}`)
          }
        } catch (err: any) {
          if (shown()) toast.error(err?.message || 'Cross-tenant copy failed')
        }
        if (shown()) {
          setSelectedIds(new Set())
          refresh()
        }
        isPastingRef.current = false
        return
      }

      const destFolderId = effectiveFolderId ?? currentFolderId

      if (isCut) {
        // Check conflicts for move
        const conflicts = await filesApi.checkConflicts(
          items.map(item => ({ name: item.name, parentId: destFolderId })),
          batch.opts(),
        )
        const conflictNames = new Set(conflicts.map(c => c.name))
    // The EXISTING item at each conflicting name, as the server found it — a folder not open here, or
    // another org's file through its `.shared`, is in no listing this view holds.
    const conflictAt = existingFromConflicts(conflicts)

        let applyToAll: ConflictResolution | null = null
        const resolutions = new Map<string, ConflictStrategy>()

        for (const item of items) {
          if (conflictNames.has(item.name)) {
            // never a question in the session after the batch's own: the conflicting item is left where it is
            if (!shown()) { resolutions.set(item.id, 'skip'); continue }
            if (applyToAll) {
              resolutions.set(item.id, applyToAll)
            } else {
              const result = await resolveConflict({
                fileName: item.name,
                sourcePath: item.name,
                destPath: item.name,
                isFolder: item.isFolder,
                operation: 'move',
              })
              resolutions.set(item.id, result.resolution)
              if (result.applyToAll) applyToAll = result.resolution
            }
          }
        }

        const movedNames: string[] = []
        let shareLinkAdvisoryTotal = 0
        // Track whether a reverse-share / chain-escape guard broke the loop, so a
        // multi-select cut-paste doesn't silently drop (via the clipboard wipe
        // below) the items after the one that opened the dialog.
        let brokeOnGuard = false
        const pinnedApi = { replaceByMove: (dest: string, src: string) => filesApi.replaceByMove(dest, src, batch.opts()) }
        const pinnedClearApi = replaceClearApiAs(() => batch.opts())
        for (const item of items) {
          const strategy = resolutions.get(item.id)
          if (strategy === 'skip') continue
          if (item.parentId === destFolderId && !strategy) continue

          try {
            // Ask BEFORE anything is changed: cancelling must leave the destination untouched.
            const forkExpand = await askForkPointers(item, destFolderId, batch)
            if (forkExpand === null) continue
            // #801: then the partition preview (after the pointer dialog, before the
            // destination is touched by a replace).
            const pg = await gateMove(item, destFolderId, batch)
            if (!pg.go) continue
            const expect = pg.confirmed ? { expectRemovable: pg.expectRemovable } : {}
            if (strategy === 'replace') {
              const conflicting = files.find(f => f.name === item.name && f.parentId === destFolderId) ?? conflictAt.get(item.name) ?? files.find(f => f.name === item.name)
              // Replace (owner, 2026-10-06): a file onto a file is a new VERSION of the existing
              // file (it keeps its id, history and links); the moved file then goes to the Bin.
              // A destination in ANOTHER organisation takes no Replace: nothing is touched there and the
              // ordinary move below answers (the reverse-share prompt), as on main (`moveReplaceStep`).
              const step = await moveReplaceStep(pinnedApi, item, conflicting)
              if (step.kind === 'versioned') {
                // ONE undo step for the whole move: restore the destination's previous version AND bring the
                // moved file back from the Bin (a fork out of a `.shared` left it where it was).
                const plan = moveAsVersionUndo(step.r, item)
                if (plan?.kind === 'version') undo.recordAddVersion(at, plan.fileId, plan.previousVersionId, item.name, plan.restoreSource, plan.versionId)
                else if (plan?.kind === 'delete') recordDeleteUndo(at, item, plan.deleted)
                movedNames.push(item.name)
                continue
              }
              // A folder / pointer on either side has no versions: bin the conflicting item, then move.
              if (conflicting && step.kind === 'binThenMove') {
                // #801: never a half-replaced folder — one holding anything the caller
                // can't delete is left alone and THIS item's move is aborted. A delete
                // that split anyway (a race) is recorded so its bin items can be undone.
                const cleared = await clearReplaceTarget(conflicting, pinnedClearApi)
                if (!cleared.cleared) {
                  if (cleared.partial) recordDeleteUndo(at, conflicting, cleared.partial)
                  if (shown()) toast.error(t('protectedBranches.replaceAborted', { name: item.name }))
                  continue
                }
              }
            }
            const result = await withMoveProgress(toast, t('driveView.toast.moving', { name: item.name }), (hooks) => filesApi.moveFile(item.id, { parentId: destFolderId, ...(forkExpand ? { expandPointers: true } : {}), ...expect }, { ...hooks, ...batch.opts() }), batch.ended)
            if (result?.crossTenant && result.reverseShare) {
              // Pasting into a cross-tenant `.shared` is a reverse-share intent,
              // not a move (the server returned 409, nothing reparented). Open the
              // Share dialog pre-targeted at the owner org. Parity with the drag
              // path — without this the paste was a silent dead gesture. One
              // dialog at a time → stop for the first such item.
              if (shown()) setReverseShare(result.reverseShare)
              brokeOnGuard = true
              break
            }
            if (result?.chainEscape && result.escape) {
              if (shown()) setChainEscape({ fileId: item.id, targetFolderId: destFolderId, escape: result.escape, name: item.name, originalParentId: item.parentId, ...expect })
              brokeOnGuard = true
              break
            }
            if (result?.accessChange) {
              if (!shown()) continue   // a question / refusal for the session that ended: this item stays where it is
              if (result.needsConfirmation) {
                const diffText = (result.diff ?? []).map(d => {
                  const label = d.targetType === 'group' ? `Team '${d.targetId}'` : d.targetType === 'role' ? `Role '${d.targetId}'` : d.targetId
                  return `${label}: ${d.fromPerm ?? 'none'} → ${d.toPerm ?? 'none'}`
                }).join('\n')
                const confirmed = await confirm({
                  title: 'Access will change',
                  message: `Moving "${item.name}" changes permissions:\n\n${diffText}`,
                  type: 'info',
                  confirmText: 'Move anyway',
                })
                if (confirmed) {
                  const confirmedResult = await withMoveProgress(toast, t('driveView.toast.moving', { name: item.name }), (hooks) => filesApi.moveFile(item.id, { parentId: destFolderId, confirmedAccessChange: true, ...expect }, { ...hooks, ...batch.opts() }), batch.ended)
                  recordMoveUndo(at, item, item.parentId, confirmedResult)
                  const splitMsg = moveResultMessage(t, confirmedResult)
                  if (splitMsg) { if (shown()) toast.info(splitMsg) }
                  else movedNames.push(item.name)
                  if (confirmedResult?.shareLinkAdvisory) shareLinkAdvisoryTotal += confirmedResult.shareLinkAdvisory.count
                }
              } else {
                toast.error(result.error || 'Moving this changes access. You need Manage & Edit on both folders.')
              }
              continue
            }
            // #801: a split records a split undo, a replay none (and says "nothing left").
            recordMoveUndo(at, item, item.parentId, result)
            const splitMsg = moveResultMessage(t, result)
            if (splitMsg) { if (shown()) toast.info(splitMsg) }
            else movedNames.push(item.name)
            if (result?.shareLinkAdvisory) shareLinkAdvisoryTotal += result.shareLinkAdvisory.count
          } catch (err: any) {
            if (shown()) toast.error(moveErrorText(err, t, item.name))
          }
        }
        // A guard (reverse-share / chain-escape) that broke the loop leaves the
        // trailing items un-moved. Clear the clipboard — re-pasting it would
        // re-process the already-moved items against their stale parentId (a
        // "replace" on the just-moved file would delete it). Tell the user to
        // re-cut the rest instead. #1316 review round 2: cleared whether or not the
        // paste's session ended — the batch finished as its starter, so the cut is spent.
        consumeCut(clipboard)
        // the batch finished as its starter — nothing of it is SHOWN in the session after its own
        if (!shown()) { isPastingRef.current = false; return }
        if (brokeOnGuard && items.length > 1) {
          toast.info('Move interrupted — the remaining item(s) weren\'t moved. Re-cut them to retry.')
        }

        if (movedNames.length > 0) {
          toast.success(movedNames.length > 1
            ? t('driveView.toast.moved', { name: movedNames[0], count: movedNames.length - 1 })
            : t('driveView.toast.movedOne', { name: movedNames[0] }))
        }

        // Decision 9 / Phase 29: non-blocking share-link advisory.
        if (shareLinkAdvisoryTotal > 0) {
          toast.info(`${shareLinkAdvisoryTotal} active share-link${shareLinkAdvisoryTotal === 1 ? '' : 's'} point at moved content. Assets still load from the originally-picked root.`)
        }
      } else {
        const operationId = `copy-${Date.now()}-${Math.random().toString(36).slice(2)}`

        // Progress total — counted a few at a time on a short server budget, so
        // the copy never waits long on it (paste-progress-total.ts). A total that
        // is only a lower bound shows as INDETERMINATE (null), never "600 / 55".
        const counted = await pasteProgressTotal(items, (id, opts) => filesApi.countChildren(id, { ...opts, ...batch.opts() }))
        const totalItems = counted.truncated ? null : counted.total

        // #1316 review round 2: opened only in the paste's own session (it is cleared at its end)
        const progressCtrl = shown() ? toast.progress(`Copying ${items[0].name}...`, totalItems) : { update: () => {}, complete: () => {}, fail: () => {} }
        const seenIds = new Set<string>()

        const unsubscribe = onOperationEvent((event) => {
          if (event.operationId !== operationId) return
          if (event.stage === 'completed' && event.fileId && !seenIds.has(event.fileId)) {
            seenIds.add(event.fileId)
            if (shown()) progressCtrl.update(seenIds.size, `Copying ${event.fileName}`)
          }
        })

        try {
          const pasted = await pasteItems(items, destFolderId, 'copy', resolveConflict, { operationId, batch })
          const { copied, errors } = pasted
          unsubscribe()
          if (!shown()) { isPastingRef.current = false; return }
          // Files a Replace wrote a new version into count as pasted too (reported apart from `copied`).
          const landed = copied.length + (pasted.versioned?.length ?? 0)
          if (landed > 0) {
            // ONE undo step for the batch: deletes what it created, restores what its Replace overwrote.
            const rec = pasteUndoRecord(pasted)
            if (rec) undo.recordCopy(at, rec.copiedFileIds, rec.versioned)
            progressCtrl.complete(pasteToastText(t, copied.map((x) => x.name), (pasted.versioned ?? []).map((x) => x.name)) ?? '')
          } else {
            progressCtrl.complete('Nothing to copy')
          }
          // Surface per-source failures separately so a partial-success copy
          // doesn't disappear into the toast above. The server's per-source
          // try/catch previously swallowed these into copied:[] silently.
          // The error message includes the source filename so a user with
          // multiple selections can tell *which* item failed; the server's
          // payload only has sourceId so we resolve it from the clipboard
          // items we sent.
          if (errors.length > 0) {
            const itemById = new Map(items.map(i => [i.id, i.name]))
            const describe = (e: { sourceId: string; message: string }) =>
              `${itemById.get(e.sourceId) ?? e.sourceId}: ${e.message}`
            toast.error(errors.length > 1
              ? `${errors.length} items failed to copy — ${describe(errors[0])} (+${errors.length - 1} more)`
              : `Copy failed — ${describe(errors[0])}`)
          }
        } catch (err) {
          unsubscribe()
          if (shown()) progressCtrl.fail?.(engineDbOwnedText(err, t) ?? engineDbInvalidText(err, t) ?? 'Copy failed')
          throw err
        }
      }

      if (shown()) {
        setSelectedIds(new Set())
        refresh()
      }
    } catch (err) {
      console.error('[Paste] Error:', err)
      // #1316 review round 2: a pinned paste's failure after its session ended is shown to nobody
      if (!shown()) return
      // A storage refusal already carries a sentence naming what frees space —
      // the Bin, old versions, duplicates. Replacing it with "Failed to paste"
      // tells the user nothing they can act on, and paste is the single most
      // likely way to meet the cap (it is one click and costs the user
      // nothing). Verified in the browser: before this, a refused paste
      // surfaced only as the generic toast while the actionable text sat in
      // the console.
      //
      toast.error(storageRefusalText(err) ?? folderBeingDeletedText(err, t) ?? reservedNameText(err, t) ?? engineDbOwnedText(err, t) ?? engineDbInvalidText(err, t) ?? t('driveView.toast.pasteFailed'))
    } finally {
      isPastingRef.current = false
    }
  }, [clipboard, currentFolderId, effectiveFolderId, files, refresh, toast, t, undo, resolveConflict, moveFile, consumeCut, onOperationEvent, confirm, tenant, isOrg, tenantName, askForkPointers, gateMove, recordMoveUndo, recordDeleteUndo])

  // Delete selected files
  const handleDeleteSelected = useCallback(async () => {
    if (selectedFiles.length === 0) return

    // #801: preview every selected folder in parallel. When any of them holds
    // branches the caller can't change (or can't be deleted at all — a 403 preview),
    // ONE confirm shows a section per such folder; those refused whole are left out
    // of the act, the rest carry their previewed set. Otherwise the plain confirm.
    const gated = await partitionGate.gateDeleteMany(
      selectedFiles.map(f => (isPointerRow(f) ? { ...f, isFolder: false } : f)),
      id => filesApi.protectedBranches(id, 'delete'),
      count => t('driveView.confirm.deleteMultiple', { count }),
    )
    if (!gated) return
    if (!gated.confirmed) {
      const confirmed = await confirm({
        title: t('driveView.confirm.deleteTitle'),
        message: t('driveView.confirm.deleteMultiple', { count: selectedFiles.length }),
        type: 'danger',
        confirmText: t('driveView.menu.delete'),
      })
      if (!confirmed) return
    }
    const expectOf = new Map(gated.act.map(a => [a.item.id, a.expectRemovable]))
    const toDelete = selectedFiles.filter(f => expectOf.has(f.id))

    // Every selected row dims at once. The requests go out one at a time and
    // big folders then run as background jobs, side by side (#781); one toast
    // counts the items as they finish. A folder confirmed as a split (#801)
    // carries its previewed set.
    const selectedIdList = toDelete.map((f) => f.id)
    markDeleting(selectedIdList, true)
    try {
      await deleteItems(toDelete.map((f) => {
        const expectRemovable = expectOf.get(f.id)
        return { id: f.id, name: f.name, ...(expectRemovable ? { expectRemovable } : {}) }
      }))
      setSelectedIds(new Set())
    } finally {
      markDeleting(selectedIdList, false)
    }
  }, [selectedFiles, t, confirm, deleteItems, markDeleting, partitionGate])

  const handleRename = (file: FileItem) => {
    // A revoked/broken `.shared` is a real row in the user's own drive — renaming it
    // is fine (a local relabel; the dead pointer stays dead). A dead `.chain` stays
    // inert (Delete only) — deliberate, guarded by e2e/chain-primitive.spec.ts.
    if (file.chainBroken) return
    setRenamingFileId(file.id)
  }

  const handleRenameSubmit = async (file: FileItem, newName: string) => {
    setRenamingFileId(null)

    // Project promotion intercept (§3.3): a plain folder gaining a `.project`
    // extension gets the access dialog instead of a silent rename. The dialog
    // performs the gaining rename WITH the access decision (restrict in place,
    // never moves — D6). Personal tenants skip the dialog (handled in the
    // provider). Re-promotion (already a project) falls through to a normal
    // rename — the marker on the server makes the chokepoint a no-op anyway.
    if (file.isFolder && !isProjectFolder(file.name) && isProjectFolder(newName)) {
      // Promotion is a governance change — mint-admin (D1) requires MANAGE on the
      // folder, not just write (the server enforces this too). Pre-check so we
      // don't open the dialog only to fail on confirm; toast + cancel otherwise.
      try {
        const { permission } = await request<{ permission: string }>(`/api/files/${file.id}/permission`)
        if (permission !== 'admin' && permission !== 'owner') {
          toast.error(t('driveView.toast.promoteNeedsManage', 'You need manage permission to make this a project'))
          return
        }
      } catch { /* fall through — the server re-checks and 403s if needed */ }
      newProject.open({
        variant: 'promote',
        promoteFolderId: file.id,
        promoteNewName: newName,
        onDone: () => { refresh(); toast.success(t('driveView.toast.renamed', { oldName: file.name, newName })) },
      })
      return
    }

    const fileType = (file as { fileType?: string | null }).fileType
    // Renaming a live `.shared` OFF its suffix DETACHES it, which silently promotes a
    // demoted "Conflict" copy to the live share (issue #157 reconcile-on-free) and can
    // change who has access. Warn — but only when a copy will actually promote (server
    // preview), and in place of the generic extension warning (this is the real one
    // for a share pointer).
    if (pointerKindOf({ fileType }) === 'shared' && !isSharedPointer(newName)) {
      let preview: { willPromote?: boolean; promote?: { name?: string; path?: string[] } }
      try {
        preview = await filesApi.promotePreview(file.id)
      } catch {
        preview = { willPromote: true } // fail safe — warn rather than rename silently
      }
      if (preview.willPromote) {
        const here = file.name.replace(/\.shared$/i, '')
        const promoteName = preview.promote?.name?.replace(/\.shared$/i, '')
        const path = preview.promote?.path
        const proceed = await confirm({
          title: t('driveView.rename.shareDetachTitle'),
          message: (
            <>
              {promoteName
                ? t('driveView.rename.shareDetachMessage', { name: here, promoteName })
                : t('driveView.rename.shareDetachGeneric', { name: here })}
              {path && path.length > 0 && (
                <span className="mt-3 block rounded-md themed-bg-tertiary px-3 py-2">
                  <span className="mb-1 block text-xs themed-text-muted">{t('driveView.rename.shareDetachWillActivate')}</span>
                  <span className="block break-all font-mono text-xs themed-text">{[driveTitleRef.current, ...path].join('  /  ')}</span>
                </span>
              )}
            </>
          ),
          type: 'warning',
          confirmText: t('driveView.rename.shareDetachConfirm'),
        })
        if (!proceed) return
      }
    } else if (!file.isFolder) {
      // Warn on text ↔ binary extension change (non-pointer files).
      const oldExt = file.name.split('.').pop()?.toLowerCase() || ''
      const newExt = newName.split('.').pop()?.toLowerCase() || ''
      if (oldExt !== newExt && !areExtensionsCompatible(oldExt, newExt)) {
        const proceed = await confirm({
          title: t('driveView.rename.incompatibleTitle'),
          message: t('driveView.rename.incompatibleMessage', { oldExt, newExt }),
          type: 'warning',
          confirmText: t('driveView.rename.renameAnyway'),
        })
        if (!proceed) return
      }
    }

    try {
      const at = captureUndoContext()   // D134: before the rename's request
      const result = await moveFile(file.id, { name: newName })
      undo.recordRename(at, file.id, file.name, newName)
      refresh()
      const warning = (result as { chainTargetWarning?: string } | undefined)?.chainTargetWarning
      if (warning) {
        // The rename to `.chain` went through, but the drafted target was invalid
        // and NOT accepted (the chain keeps its previous target). Warn, not error.
        toast.warning(warning)
      } else {
        toast.success(t('driveView.toast.renamed', { oldName: file.name, newName }))
      }
    } catch (err) {
      // A hard failure (not the soft chain-target warning) — surface the reason.
      toast.error(folderBeingDeletedText(err, t) ?? reservedNameText(err, t) ?? ((err as Error)?.message || t('driveView.toast.renameFailed')))
    }
  }

  const handleRenameCancel = () => {
    setRenamingFileId(null)
  }

  // Keyboard shortcuts
  useEffect(() => {
    // Keep-alive decks: the drive stays mounted (display:none) under every other
    // deck, and this listener is on `document`. Without this gate a Ctrl+V /
    // Delete pressed in a workspace acted on the HIDDEN drive — including the
    // cross-tenant paste confirm (#752), accepted into a folder not on screen.
    if (!deckActive) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.target instanceof HTMLElement && e.target.isContentEditable) return

      // True when the user has highlighted text inside a file row (a filename).
      // When so, the file-mutating shortcuts below are suppressed so they act on
      // the text, not the file: Ctrl+C copies the highlighted text natively;
      // Ctrl+X / Delete / Backspace are skipped (read-only DOM text can't be cut
      // or deleted — clear the selection to act on the file). Both endpoints are
      // checked so it counts regardless of drag direction, and it's scoped via
      // the data-file-id ancestor so a selection elsewhere on the page (another
      // pane, breadcrumb, a stale leftover) can't suppress file actions.
      const textSelectedInRow = () => {
        const sel = window.getSelection()
        if (!sel || sel.isCollapsed || !sel.toString()) return false
        const inRow = (node: Node | null) => {
          const el = node instanceof Element ? node : node?.parentElement
          return !!el?.closest('[data-file-id]')
        }
        return inRow(sel.anchorNode) || inRow(sel.focusNode)
      }

      // True when ANY non-empty text is highlighted anywhere on the page (a
      // dialog, breadcrumb, header, a file row, …). The keydown listener lives
      // on `document`, so it intercepts every Ctrl+C while the drive is mounted;
      // copy is non-destructive, so whenever the user has real text selected we
      // defer to the browser's native copy instead of file-copy. (Cut/Delete
      // stay row-scoped via textSelectedInRow — they're destructive and should
      // act on the file unless the selection is clearly the filename text.)
      const hasTextSelection = () => {
        const sel = window.getSelection()
        return !!sel && !sel.isCollapsed && !!sel.toString()
      }

      if (e.key === 'Escape' && contextMenu) {
        e.preventDefault()
        setContextMenu(null)
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (hasTextSelection()) return   // let the browser copy any highlighted text (rows, dialogs, anywhere)
        e.preventDefault()
        handleCopy()
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        if (textSelectedInRow()) return   // skip — don't cut the file while its name is selected
        e.preventDefault()
        handleCut()
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault()
        // A HELD Ctrl+V (key repeat) pastes once — each repeat would paste again, or flood "still running"
        // notices while a big paste runs.
        if (e.repeat) return
        handlePaste()
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        setSelectedIds(new Set(files.map(f => f.id)))
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (textSelectedInRow()) return   // skip — don't delete the file while its name is selected
        if (selectedFiles.length > 0) {
          e.preventDefault()
          handleDeleteSelected()
        }
      } else if (e.key === 'F2') {
        if (selectedFiles.length === 1) {
          e.preventDefault()
          handleRename(selectedFiles[0])
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleCopy, handleCut, handlePaste, handleDeleteSelected, files, selectedFiles, contextMenu, deckActive])

  // Placement, follow-on-scroll, viewport clamp and anchor-exit for the
  // right-click menu — the same convention the row ⋯ menu uses, so the two
  // cannot drift. The zero-size rect is the click POINT carried inside the
  // anchor, which is what keeps the menu at the pointer while it tracks a row
  // that is moving. Replaces a hand-rolled clamp that wrote `el.style` and
  // never followed or dismissed.
  const ctxAnchorRect = useCallback((el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    const x = r.left + (contextMenu?.offX ?? 0)
    const y = r.top + (contextMenu?.offY ?? 0)
    return { top: y, left: x, right: x, bottom: y, width: 0, height: 0 }
  }, [contextMenu?.offX, contextMenu?.offY])
  const { pos: ctxPos, exiting: ctxExiting, origin: ctxOrigin } = useAnchoredPosition({
    open: !!contextMenu,
    anchorRef: ctxAnchorRef,
    floatingRef: contextMenuRef,
    onDismiss: useCallback(() => setContextMenu(null), []),
    anchorRect: ctxAnchorRect,
    gap: 0,
    margin: 4,
    exitTransitionMs: 160,
    // A background right-click is pinned to a POINT, not to a row: its anchor
    // is the scroll container, which never exits, so without this the menu
    // would sit there through any amount of scrolling. A row-anchored one
    // follows instead, and exits with its row.
    exitOnScroll: !contextMenu?.file,
  })

  // Close context menu on outside mousedown
  useEffect(() => {
    if (!contextMenu) return
    const handleMouseDown = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [contextMenu])

  // File row drag handlers
  const handleRowDragStart = useCallback((_e: React.DragEvent, file: FileItem) => {
    setIsDragActive(true)
    const filesToDrag = selectedIds.has(file.id)
      ? files.filter(f => selectedIds.has(f.id))
      : [file]
    setDraggedIds(new Set(filesToDrag.map(f => f.id)))
    // Store folder IDs in module scope so other components can check during dragOver
    const folderIds = filesToDrag.filter(f => f.isFolder).map(f => f.id)
    setActiveDragFolderIds(folderIds)
  }, [selectedIds, files])

  const handleRowDragOver = useCallback((e: React.DragEvent, file: FileItem) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragActive) return
    if (!file.isFolder) return
    if (draggedIds.has(file.id)) return
    // Centralized circular check (works cross-tab via the drag store)
    const dragFolderIds = peekPapanDragFolderIds(e.dataTransfer)
    if (dragFolderIds.size > 0 && isCircularMove(dragFolderIds, file.id, currentFolderAncestors)) {
      e.dataTransfer.dropEffect = 'none'
      setForbiddenDropId(file.id)
      setDropTargetId(null)
      return
    }
    setForbiddenDropId(null)
    setDropTargetId(file.id)
    e.dataTransfer.dropEffect = 'move'
  }, [draggedIds, isDragActive])

  const handleRowDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDropTargetId(null)
    setForbiddenDropId(null)
  }, [])

  // Container drag handlers (for external files and current folder drop)
  const handleContainerDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current++
    if (e.dataTransfer.types.includes('Files') || isPapanDrag(e.dataTransfer)) {
      setIsDragActive(true)
      setShowDropOverlay(true)
    }
  }, [])

  const handleContainerDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setShowDropOverlay(false)
      setDropTargetId(null)
    }
  }, [])

  const handleContainerDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const handleContainerDragEnd = useCallback(() => {
    setIsDragActive(false)
    setDraggedIds(new Set())
    setDropTargetId(null)
    setDropTargetCrumb(null)
    setShowDropOverlay(false)
    setForbiddenDropId(null)
    clearActiveDrag()
    dragCounterRef.current = 0
  }, [])

  // Breadcrumb drop handlers
  const handleCrumbDragOver = useCallback((e: React.DragEvent, crumbId: string) => {
    if (!isDragActive) return
    if (isPapanDrag(e.dataTransfer)) {
      e.preventDefault()
      e.stopPropagation()
      setDropTargetCrumb(crumbId)
      e.dataTransfer.dropEffect = 'move'
    }
  }, [isDragActive])

  const handleCrumbDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDropTargetCrumb(null)
  }, [])

  // A drag from another drive's tab names that drive; moving across drives isn't
  // possible, so every drop surface here says so (a folder row used to say nothing).
  // A drag naming no drive (an editor tab) isn't a drive item at all — ignored.
  const refuseCrossDrive = useCallback(() => {
    toast.error(t('driveView.toast.crossTenantMove', { defaultValue: 'Items can’t be moved from one of your drives to another. Copy them instead — moving into or out of a shared folder still works.' }))
  }, [toast, t])

  const handleCrumbDrop = useCallback(async (e: React.DragEvent, crumbId: string) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    setDropTargetCrumb(null)
    setDraggedIds(new Set())
    setShowDropOverlay(false)
    dragCounterRef.current = 0

    const targetFolderId = crumbId === DROP_ROOT ? null : crumbId

    const transfer = readPapanDrag(e.dataTransfer)
    if (!transfer) return
    if (transfer.tenantId !== tenant) {
      if (transfer.tenantId) refuseCrossDrive()
      return
    }
    try {
      await moveFilesToFolder(transfer.files, targetFolderId)
    } catch {
      // The move reports its own failures
    }
  }, [tenant, moveFilesToFolder, refuseCrossDrive])

  const handleRowDrop = useCallback(async (e: React.DragEvent, targetFile: FileItem) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    setDropTargetId(null)
    setDraggedIds(new Set())
    setShowDropOverlay(false)
    dragCounterRef.current = 0

    if (!targetFile.isFolder) return

    // Internal Papan move
    if (isPapanDrag(e.dataTransfer)) {
      const transfer = readPapanDrag(e.dataTransfer)
      if (!transfer) return
      if (transfer.tenantId !== tenant) {
        if (transfer.tenantId) refuseCrossDrive()
        return
      }
      try {
        await moveFilesToFolder(transfer.files.filter(f => f.id !== targetFile.id), targetFile.id)
      } catch (err) {
        console.error('[DriveView Drop] error:', err)
      }
      return
    }

    // External file upload into this folder
    await upload.handleExternalDrop(e, targetFile.id, resolveConflict)
  }, [tenant, moveFilesToFolder, upload, resolveConflict, refuseCrossDrive])

  const handleContainerDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragActive(false)
    setShowDropOverlay(false)
    setDraggedIds(new Set())
    setDropTargetId(null)
    dragCounterRef.current = 0

    // Papan transfer (cross-tab or same-tab drop on current folder)
    if (isPapanDrag(e.dataTransfer)) {
      const transfer = readPapanDrag(e.dataTransfer)
      if (!transfer) return
      if (transfer.tenantId !== tenant) {
        if (transfer.tenantId) refuseCrossDrive()
        return
      }
      // Filter out files already in current folder
      const filesToMove = transfer.files.filter(f => !files.some(ef => ef.id === f.id))
      if (filesToMove.length > 0) {
        try { await moveFilesToFolder(filesToMove, currentFolderId) } catch { /* the move reports its own failures */ }
      }
      return
    }

    // Handle external file upload
    await upload.handleExternalDrop(e, currentFolderId, resolveConflict)
  }, [currentFolderId, files, tenant, upload, resolveConflict, moveFilesToFolder, refuseCrossDrive])

  // D17 / I114 — a `.chain` that Needs approval doesn't open: the shared gate shows whoever may
  // approve it the Approve dialog, anyone else the reason. `block` true = handled.
  // Owner 2026-10-27 — approve a code file the app (or a visitor) wrote: the person reads the warning and vouches for it.
  // the ONE approval flow (`useApproveCode`), shared with the workspace explorer
  const approveCode = useApproveCode(useCallback(() => { refresh() }, [refresh]))
  const handleApproveCode = useCallback((file: FileItem) => { void approveCode(file) }, [approveCode])

  const { block: blockPendingPointer, openApprove: setApprovingPointer, dialogs: pendingPointerDialogs } = usePendingPointerGate({
    urlPrefix, rootLabel: driveTitle, onNavigate: handleNavigate, onApproved: () => refresh(),
  })

  // A project / app folder the person reaches only as a HALLWAY (No access on it, something
  // below it granted — #1036) has no project or app to open: it browses as the plain folder the
  // drive shows. A hallway row under a hidden parent carries no level (the listing leaves it
  // undefined), so ask once.
  const packageIsHallwayShell = useCallback((file: FileItem, opts?: { asProject?: boolean }) => isHallwayPackage(file, opts), [])

  const handleFileOpen = useCallback(async (file: FileItem) => {
    // A revoked/broken .shared is a dead pointer — don't open it.
    if (file.shareBroken) {
      toast.info(file.shareNoAccess
        ? "You don't have access to this shared folder. You can remove it from your drive."
        : 'This shared item is no longer available — access was revoked. You can remove it from your drive.')
      return
    }
    // A `.chain` whose target was deleted is a dead pointer too.
    if (file.chainBroken) {
      toast.info("This chain's target no longer exists. You can remove it from your drive.")
      return
    }
    if (blockPendingPointer(file)) return
    // The project shell (its config, the assistant) and the app launcher both need read on the
    // folder itself — a hallway package opens as its folder instead (#1036).
    // #1406: the check asks the server — an answer that lands after the person moved on (another page) is dropped
    const startedAt = window.location.pathname
    if ((file.type === 'workpapan' || presentsAsProject(file.name, file.fileType, file.isFolder) || presentsAsMiniapp(file.name, file.fileType, file.isFolder))
      && await packageIsHallwayShell(file)) {
      if (window.location.pathname !== startedAt) return
      handleNavigate(file.id)
      return
    }
    if (window.location.pathname !== startedAt) return
    if (file.type === 'workpapan') {
      routerNavigate(`${urlPrefix}/workspace/${file.id}`)
      return
    }
    // Packages open as what they ARE (macOS-bundle semantics): a `.miniapp`
    // double-click launches the app; the folder view is the explicit
    // "Browse files" verb (hover button / context menu) — never a prompt.
    // (#327) `presentsAsMiniapp` also covers a `.shared` mount of someone
    // else's app, so a recipient gets the BEHAVIOR, not just the contents.
    if (presentsAsMiniapp(file.name, file.fileType, file.isFolder)) {
      routerNavigate(`${urlPrefix}/app/${file.id}`)
      return
    }
    // (#327 Track B) A `.project` mount opens as a workspace rooted at the mount —
    // the same verb its native counterpart gets. `file.type === 'workpapan'` above
    // only catches native projects; a pointer row carries type `'shared'`.
    // NOTE: `handleOpenWorkspace` is declared BELOW this callback and is deliberately
    // absent from the dep array. The closure is only ever invoked on a click, long
    // after both bindings initialise, so it resolves fine — but adding it to the deps
    // would evaluate the identifier during render, before its `const`, and throw on
    // the temporal dead zone. Leave it out.
    if (presentsAsProject(file.name, file.fileType, file.isFolder)) {
      void handleOpenWorkspace(file)
      return
    }
    if (file.isFolder) {
      handleNavigate(file.id)
      return
    }

    // A plain file opens in the single-file editor.
    //
    // It used to run `find-for-file` and land in a WORKSPACE — wrapping the
    // file's parent folder as a project if none existed. That made the heavy
    // surface (explorer, tabs, AI panel) the default answer to "show me this
    // file", and created a workspace as a side effect of looking at something.
    // Opening the file is now just opening the file; the editor carries an
    // "Open as AI Project" action for anyone who wants the other thing, and
    // that action still runs the same find-or-wrap flow.
    routerNavigate(`${urlPrefix}/file/${file.id}`)
  }, [urlPrefix, routerNavigate, handleNavigate, toast, blockPendingPointer, packageIsHallwayShell])

  // Jump from a `.chain`'s "→ <target>" label to the real target: navigate
  // into it if it's a folder, otherwise open the single-file viewer. The
  // target is a same-tenant id resolved server-side, so no extra fetch.
  const handleJumpToChainTarget = useCallback((targetId: string, targetIsFolder: boolean) => {
    if (targetIsFolder) handleNavigate(targetId)
    else routerNavigate(`${urlPrefix}/file/${targetId}`)
  }, [handleNavigate, routerNavigate, urlPrefix])

  const handleOpenInNewTab = useCallback(async (file: FileItem) => {
    // A revoked/broken .shared is a dead pointer — don't open it.
    if (file.shareBroken) {
      toast.info(file.shareNoAccess
        ? "You don't have access to this shared folder. You can remove it from your drive."
        : 'This shared item is no longer available — access was revoked. You can remove it from your drive.')
      return
    }
    if (blockPendingPointer(file)) return
    if (file.isFolder) {
      window.open(`${urlPrefix}/folder/${file.id}`, '_blank')
      return
    }

    // Smart resolve for files
    try {
      const result = await request<{
        workspaceFolderId: string | null
        parentFolderId: string | null
        parentFolderName: string | null
        scope?: 'individual' | 'organization'
        viewerOnly?: boolean
      }>('/api/workspaces/find-for-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId: file.id, openWorkspaceFolderIds: getOpenWorkspaceFolderIds() }),
      })

      if (result.workspaceFolderId) {
        window.open(`${urlPrefix}/workspace/${result.workspaceFolderId}?open=${file.id}`, '_blank')
      } else if (result.viewerOnly) {
        window.open(`${urlPrefix}/file/${file.id}`, '_blank')
      } else if (result.parentFolderId) {
        const ws = await request<{ folderId: string }>('/api/workspaces/open-folder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderId: result.parentFolderId, folderName: result.parentFolderName, scope: result.scope }),
        })
        window.open(`${urlPrefix}/workspace/${ws.folderId}?open=${file.id}`, '_blank')
      } else {
        window.open(`${urlPrefix}/workspace?open=${file.id}`, '_blank')
      }
    } catch {
      window.open(`${urlPrefix}/workspace?open=${file.id}`, '_blank')
    }
  }, [urlPrefix, toast, blockPendingPointer])

  const handleOpenWorkspace = useCallback(async (file: FileItem, scope?: 'individual' | 'organization') => {
    // #1036 / #1406: opening it as a WORKSPACE (a project, or Edit PDApp) is opening it as a project.
    // The check asks the server — an answer that lands after the person moved on is dropped.
    const startedAt = window.location.pathname
    if ((file.type === 'workpapan' || (file.isFolder && isMiniappFolder(file.name))) && await packageIsHallwayShell(file, { asProject: true })) {
      if (window.location.pathname !== startedAt) return
      handleNavigate(file.id) // a package the caller can't open as a project browses as its folder
      return
    }
    if (window.location.pathname !== startedAt) return
    if (file.type === 'workpapan' || (file.isFolder && isMiniappFolder(file.name))) {
      // Open the entity folder directly as its own workspace — a .project
      // (workpapan) or a .miniapp ("Edit PDApp"). The mini-app is edited AS
      // itself, not wrapped in a linked project (that's the "Start AI Project"
      // path below, reserved for plain folders). Editing a mini-app lands on the
      // Settings face by default.
      if (file.isFolder && isMiniappFolder(file.name)) onRequestMiniappView?.(file.id, 'settings')
      routerNavigate(`${urlPrefix}/workspace/${file.id}`)
    } else if (file.isFolder) {
      // Open/create workspace for regular folder via workspace API
      try {
        const res = await request<{ folderId: string }>('/api/workspaces/open-folder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folderId: file.id, folderName: file.name, scope }),
        })
        routerNavigate(`${urlPrefix}/workspace/${res.folderId}`)
      } catch (err) {
        toast.error('Failed to open workspace')
        console.error('[DriveView] openFolder failed:', err)
      }
    }
  }, [urlPrefix, routerNavigate, toast, onRequestMiniappView, handleNavigate, packageIsHallwayShell])

  const handleOpenWorkspaceInNewTab = useCallback((file: FileItem) => {
    // Opened synchronously (the click's gesture — a popup blocker refuses a later open), then
    // pointed at the project, or at the plain folder for a hallway package (#1036).
    const tab = window.open('', '_blank')
    void packageIsHallwayShell(file, { asProject: true }).then((shell) => {
      const url = shell ? `${urlPrefix}/folder/${file.id}` : `${urlPrefix}/workspace/${file.id}`
      if (tab) tab.location.href = url
      else window.open(url, '_blank')
    })
  }, [urlPrefix, packageIsHallwayShell])

  // Run a .miniapp folder / .html file as a top-level shell app window —
  // everything flows through the URL; the shell's RouteSync resolves the
  // kind and registers the instance.
  const handleOpenAsApp = useCallback(async (file: FileItem) => {
    if (await packageIsHallwayShell(file)) { handleNavigate(file.id); return } // #1036
    routerNavigate(`${urlPrefix}/app/${file.id}`)
  }, [urlPrefix, routerNavigate, handleNavigate, packageIsHallwayShell])

  // Explore .workpapan as a folder in Drive view
  const handleExplore = useCallback((file: FileItem) => {
    routerNavigate(`${urlPrefix}/folder/${file.id}`)
  }, [urlPrefix, routerNavigate])

  const handleExploreInNewTab = useCallback((file: FileItem) => {
    window.open(`${urlPrefix}/folder/${file.id}`, '_blank')
  }, [urlPrefix])

  // The pointer JSON itself. No `containsFolder`, so the client takes the GET
  // single-file path which serves the metadata_json column. For a
  // `.shared`/`.chain` this round-trips: download → re-upload → re-align, and
  // the saved name is the BARE `dev.shared` (never `.json`) because the
  // aligner keys off that extension.
  // `PointerRowLike`, not `FileItem`: the dialog hands back its own rows, and
  // this only ever needed an id.
  const handleDownloadMetadata = useCallback(async (file: PointerRowLike) => {
    try {
      await filesApi.downloadMetadata(file.id)
    } catch (err) {
      toast.error((err as { message?: string })?.message || t('driveView.toast.downloadFailed'))
    }
  }, [toast, t])

  /** Resolves `true` when this download did not complete — the user cancelled
   *  the save dialog, or the browser refused to open one. A REFUSAL is toasted
   *  here: the user declined nothing, so saying nothing would leave them with no
   *  download and no explanation. */
  const handleDownloadContents = useCallback(async (file: FileItem): Promise<boolean> => {
    try {
      // `downloadItem` owns the pointer-vs-native routing. It lives in the
      // client layer because three surfaces need it — the drive, the mobile
      // sheet and the Shared view — and the first cut of this fix reached only
      // this one, leaving the other two still saving the pointer JSON.
      const { cancelled } = await filesApi.downloadItem({
        id: file.id,
        name: file.name,
        isFolder: file.isFolder,
        isPointer: isPointerRow(file),
      })
      return cancelled
    } catch (err) {
      // A refused picker is its own thing, and this surface can say it in the
      // user's language. Elsewhere the thrown message is the fallback — which
      // is the point of throwing: seven other call sites only have a `catch`.
      if ((err as { code?: string })?.code === 'PICKER_BLOCKED') {
        toast.error(t('driveView.toast.downloadPickerBlocked'))
        return true
      }
      toast.error((err as { message?: string })?.message || t('driveView.toast.downloadFailed'))
      return false
    }
  }, [toast, t])

  // A `.shared`/`.chain` row can yield two different files — the source content
  // it points at, and the pointer JSON — so Download asks which. A DEAD pointer
  // has no content to offer, so it skips the dialog and saves the JSON, which
  // is also the only thing that can repair the share (re-upload → re-align).
  const handleDownload = useCallback(async (file: FileItem) => {
    // ONE rule for all three surfaces (`routePointerDownload`). Derived inline
    // here and nowhere at all on mobile / the Shared page, which is how those
    // two came to always download contents -- including for a dead pointer,
    // where that is a near-empty zip instead of the JSON that repairs it.
    const route = routePointerDownload(file)
    if (route === 'metadata') return handleDownloadMetadata(file)
    if (route === 'choose') {
      setPointerDownload([file])
      return
    }
    // A NATIVE folder: ask the server whether it holds any `.shared`/`.chain`
    // before zipping. The zip carries their pointer FILES either way; the
    // dialog exists to offer what those point AT, which cannot be nested
    // inside the same archive.
    //
    // This costs a recursive walk on EVERY folder download, including the many
    // that hold no pointers at all. That is the accepted trade for the choice
    // appearing by itself rather than behind a menu item nobody would find.
    //
    // Fail-SOFT and silent: a pre-flight that errors must never block an
    // ordinary download. The zip still contains the pointer FILES, so the
    // fallback loses the choice and nothing else.
    if (file.isFolder) {
      try {
        if (await filesApi.hasLinkedPointers([file.id])) {
          setLinkedDownload({ folder: file, fileIds: [file.id], label: null })
          return
        }
      } catch (err) {
        if (isSessionEndedError(err)) return   // #1316: no download starts as the next session
        /* fall through to the ordinary download */
      }
    }
    return handleDownloadContents(file)
  }, [handleDownloadContents, handleDownloadMetadata])

  const handleDownloadSelected = useCallback(async () => {
    // A single selected pointer must behave exactly like its own row menu —
    // same row, same verb, same result. Left to the plain path below it routed
    // a FILE-target `.shared` through the GET single-file route, which serves
    // the pointer JSON for every pointer by design, so the bar saved
    // `index.html.shared` where the menu saved `index.html`.
    //
    // ONE selected row goes through the row menu's handler, whatever it is —
    // same row, same verb, same result. That covers pointers (their own dialog)
    // and now folders too (the linked-pointer pre-flight); leaving folders out
    // would recreate the bar-vs-menu split this rule already had to fix once.
    const only = selectedFiles.length === 1 ? selectedFiles[0] : null
    if (only && (only.isFolder || isPointerRow(only))) {
      return handleDownload(only)
    }
    // A selection MIXING pointers with ordinary rows can't be one request — the
    // server's pointer branch resolves ONE mount. Left to the path below, those
    // ids reach the native walk, which has no blob to read for a blob-less
    // pointer row and files them into the zip's `_SKIPPED_FILES.txt` under copy
    // written for unreadable blobs ("retry the download — the source may have
    // been mid-sync"), advice that can never work here. Preview the real set of
    // downloads instead, and let the user confirm it.
    if (selectedFiles.some(f => isPointerRow(f))) {
      setPointerDownload(selectedFiles)
      return
    }
    // FOLDERS in the selection get the same pre-flight one folder gets. Without
    // it the same content downloaded differently depending on how it was
    // reached: one folder offered the choice, two folders silently took the
    // pointer-files branch. That is the bar-vs-menu split the rule above
    // already had to fix once, reappearing between single and multi.
    const folders = selectedFiles.filter(f => f.isFolder)
    if (folders.length > 0) {
      try {
        if (await filesApi.hasLinkedPointers(folders.map(f => f.id))) {
          setLinkedDownload({
            folder: selectedFiles[0],
            fileIds: selectedFiles.map(f => f.id),
            // Assistive tech gets a count, not the first folder's name, which
            // would announce one folder for a download covering several.
            label: selectedFiles.length > 1 ? `${selectedFiles.length} items` : null,
          })
          return
        }
      } catch (err) {
        if (isSessionEndedError(err)) return   // #1316: no download starts as the next session
        /* fail-soft, exactly as the single-folder path does */
      }
    }
    try {
      await filesApi.download(
        selectedFiles.map(f => f.id),
        { containsFolder: selectedFiles.some(f => f.isFolder) },
      )
    } catch (err) {
      toast.error((err as { message?: string })?.message || t('driveView.toast.downloadFailed'))
    }
  }, [selectedFiles, handleDownload, toast, t])

  // ─── Extract zip ────────────────────────────────────────────────────
  const handleExtract = useCallback(async (file: FileItem, toSubfolder: boolean) => {
    if (!file.name.toLowerCase().endsWith('.zip')) return

    try {
      // 1. Preview zip contents
      const { files: zipEntries } = await filesApi.previewZip(file.id)
      if (zipEntries.length === 0) {
        toast.error(t('driveView.toast.extractFailed'))
        return
      }

      // 2. Determine destination parent
      const destParentId = toSubfolder ? undefined : currentFolderId

      // 3. Check conflicts
      let chosenStrategy: ConflictStrategy | undefined
      // For "Extract here" → check top-level names against current folder
      // For "Extract to folder/" → check if the folder name itself already exists
      const namesToCheck: Array<{ name: string; parentId: string | null }> = toSubfolder
        ? [{ name: file.name.replace(/\.zip$/i, ''), parentId: currentFolderId }]
        : [...new Set(zipEntries.map(e => e.split('/')[0]))].map(name => ({ name, parentId: currentFolderId }))

      const conflicts = await filesApi.checkConflicts(namesToCheck)
      // The first round's answers, by top-level name — never asked again in the per-file round.
      const topAnswers = new Map<string, ConflictResolution>()
      let applyToAll: ConflictResolution | null = null

      if (conflicts.length > 0) {

        for (const conflict of conflicts) {
          if (applyToAll) {
            chosenStrategy = applyToAll
            break
          }
          const result = await resolveConflict({
            fileName: conflict.name,
            sourcePath: conflict.name,
            destPath: conflict.name,
            // Reflect the destination item's type (now returned by
            // checkConflicts). Previously hardcoded `false`, which made
            // the dialog say "File Already Exists" even when /test was
            // actually a folder.
            isFolder: conflict.isFolder,
            operation: 'extract' as ConflictOperation,
          })
          chosenStrategy = result.resolution
          topAnswers.set(conflict.name, result.resolution)
          if (result.applyToAll) {
            applyToAll = result.resolution
          }
        }

        if (chosenStrategy === 'skip') return
      }

      // 3.5. Merge action — prompt the user per file conflict so they can
      // pick Skip / Keep Both / Replace individually before extract
      // runs. Uses the same ConflictModal as paste/copy/move, with
      // applyToAll wired the same way (decide once → apply to the
      // remainder). Only relevant for Replace / Merge, the one strategy
      // that writes over files inside the merged folder (each a new
      // version of the existing file). keepBoth pre-renames at the top level
      // (handled server-side) so it can't deeply conflict; skip
      // wouldn't reach here.
      let perPathResolutions: Record<string, ConflictStrategy> | undefined
      if (chosenStrategy === 'replace') {
        try {
          const { conflicts: fileConflicts } = await filesApi.previewExtractConflicts(file.id, destParentId)
          if (fileConflicts.length > 0) {
            // Ask once: a top-level file answered above (or an "Apply to all") is not asked again.
            perPathResolutions = await resolveExtractFileConflicts(fileConflicts, topAnswers, applyToAll, (c) => resolveConflict({
              fileName: c.zipPath.split('/').pop() || c.zipPath,
              sourcePath: c.zipPath,
              destPath: c.destPath,
              isFolder: c.existingIsFolder,
              operation: 'extract' as ConflictOperation,
            }))
          }
        } catch {
          // Preview-conflicts is a UX nicety, not a correctness gate. If
          // it fails (network blip, etc.), fall through to extract with
          // just the global chosenStrategy. The server still applies that
          // strategy to every entry — the legacy behavior.
        }
      }

      // 4. Show extracting toast with progress bar
      const operationId = `extract-${Date.now()}-${Math.random().toString(36).slice(2)}`
      const progressCtrl = toast.progress(`Extracting ${file.name}...`, zipEntries.length)
      const seenFiles = new Set<string>()

      const unsubscribe = onOperationEvent((event) => {
        if (event.operationId !== operationId) return
        if (event.stage === 'completed' && !seenFiles.has(event.fileName)) {
          seenFiles.add(event.fileName)
          progressCtrl.update(seenFiles.size, `Extracting ${event.fileName}`)
        }
      })

      const at = captureUndoContext()   // D134: before the extract's request (a worker job — minutes)
      try {
        const result = await filesApi.extractZip(
          file.id,
          destParentId,
          chosenStrategy,
          operationId,
          perPathResolutions,
        )

        // Undo deletes what the extract created AND restores each file its Replace wrote a version into.
        // #1430: bin the top-most created items (their contents go with them) — an older server sends only fileIds
        undo.recordExtract(at, result.topFileIds ?? result.fileIds, result.destinationFolderId, file.name, result.versioned)

        // "Extract here" lands in the folder on screen — named as the drive names it (#1459 review: it said "My Drive"
        // wherever you were)
        const destination = toSubfolder ? file.name.replace(/\.zip$/i, '') : (breadcrumbs[breadcrumbs.length - 1]?.name || driveTitle)
        progressCtrl.complete(t('driveView.toast.extracted', { count: result.filesExtracted, destination }))
        // owner D145: entries left out for a reserved name, said in the person's language
        if (result.skipped?.length) toast.info(t('driveView.toast.extractSkippedReserved', { count: result.skipped.length, names: result.skipped.slice(0, 3).map((k) => k.path).join(', ') }))
        refresh()
      } catch (err: any) {
        if (err?.status === 422 && err?.infections) {
          progressCtrl.fail(t('driveView.toast.extractMalware', { count: err.infections.length }))
        } else {
          // Same rule as paste: a storage refusal already names what frees
          // space, so "Extract failed" throws away the only actionable part.
          // Extract is the easiest way of all to meet the cap — one archive can
          // be worth thousands of files — so it is the last place to be vague.
          // #1459 review: a database entry that isn't SQLite says so (it used to read only "Failed to extract archive")
          progressCtrl.fail(storageRefusalText(err) ?? reservedNameText(err, t) ?? engineDbBusyText(err, t) ?? extractDbInvalidText(err, t) ?? engineDbOwnedText(err, t) ?? damagedMessageFrom(err) ?? t('driveView.toast.extractFailed'))
        }
        // #1430 (2): it stopped part-way — what it already wrote stays. Record it as an undoable extract and say what
        // a retry does: a retry that lands BESIDE what is there (Keep both, or "Extract to folder", which always makes
        // a new folder) makes a SECOND copy — undo first; a retry with Replace / Skip fills in the rest.
        const partial = err?.partial as ExtractPartial | undefined
        if (partial && (partial.fileIds.length > 0 || partial.versioned.length > 0 || partial.destinationFolderId)) {
          undo.recordExtract(at, partial.topFileIds ?? partial.fileIds, partial.destinationFolderId, file.name, partial.versioned)
          // What it wrote: the items it created (Undo removes them) plus the files it wrote a new version into (Undo
          // restores them) — both, each item once (#1459 Codex review)
          const count = new Set([...partial.fileIds, ...partial.versioned.map((v) => v.fileId)]).size
          // No Undo button on the toast: the stack's undo() takes whatever is on top, which a later action could be —
          // the hint points at the drive's own Undo, where this extract now sits like a finished one.
          // Keep both anywhere — the top-level answer or a per-file one — lands a retry beside what is there
          const keptBoth = chosenStrategy === 'keepBoth' || Object.values(perPathResolutions ?? {}).includes('keepBoth')
          const key = count === 0
            ? 'driveView.toast.extractPartialFolderOnly'   // only the folder it made for the archive, still empty
            : toSubfolder || keptBoth ? 'driveView.toast.extractPartialKeepBoth' : 'driveView.toast.extractPartialRetry'
          toast.warning(t(key, { count }))
          refresh()
        }
      } finally {
        setTimeout(() => unsubscribe(), 1000)
      }
    } catch (err) {
      toast.error(storageRefusalText(err) ?? t('driveView.toast.extractFailed'))
    }
  }, [currentFolderId, toast, t, undo, refresh, resolveConflict, onOperationEvent, breadcrumbs, driveTitle])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = e.target.files
    if (!inputFiles || inputFiles.length === 0) return
    await upload.handlePickedFiles(inputFiles, currentFolderId, resolveConflict)
    e.target.value = ''
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return
    try {
      const name = newFolderName.trim()
      const at = captureUndoContext()   // D134: before the create's request
      const result = await createFolder(name)
      undo.recordCreateFolder(at, result.folder.id, name)
      toast.success(t('driveView.toast.folderCreated', { name }))
      setNewFolderName('')
      setIsCreatingFolder(false)
    } catch (err: any) {
      // A refused create is the moment the banner is most wanted, and the state may
      // have changed since the last poll (or since the server's own 60 s cache
      // expired). Re-read so the explanation lands WITH the refusal rather than up
      // to a poll interval afterwards.
      if (err?.code === TENANT_WRITE_FROZEN_CODE || err?.status === 402) refreshWriteStatus()
      toast.error(folderBeingDeletedText(err, t) ?? reservedNameText(err, t) ?? t('driveView.toast.folderCreateFailed'))
    }
  }

  const handleChainTargetPicked = async (targetFileId: string, _isFolder: boolean) => {
    setIsPickingChainTarget(false)
    // Refuse the self-case HERE, where we can name it. The server refuses it too
    // ("Chain target cannot be its parent folder"), but the picker OPENS AT THE
    // DRIVE ROOT, so at the root the folder you are standing in is the first
    // thing offered and selecting it is one click. Compare against
    // `effectiveFolderId`, not `currentFolderId`: at the root the latter is null
    // while the picker hands back the real root id.
    //
    // ROOT ONLY, deliberately narrow. Inside a normal folder the server's own
    // sentence ("Chain target cannot be its parent folder") is accurate and now
    // reaches the user, so a client guard buys nothing there — and inside a
    // MOUNT it actively misleads: standing in a `.shared`, `currentFolderId` is
    // the pointer row, the picker lists that row as a folder, and selecting it
    // would fire this guard's "pick a different folder" when NO folder works —
    // the placement is a cross-tenant share and the server refuses every target
    // with a different reason entirely. Sending someone to retry N folders that
    // all fail identically is worse than the refusal they would have got.
    //
    // At the root there is no such row and the server's phrasing is weakest (the
    // drive root does not read as "a folder"), which is the case worth naming.
    if (currentFolderId === null && targetFileId === effectiveFolderId) {
      toast.error(t('driveView.toast.chainSelfTarget'))
      return
    }
    try {
      const { chain } = await filesApi.createChain(currentFolderId, targetFileId)
      toast.success(t('driveView.toast.chainCreated', { name: chain.name }))
      // file:created realtime event refreshes the list — no manual refresh needed.
    } catch (err) {
      // SURFACE THE SERVER'S REASON. This was a bare `catch {}` under a generic
      // "Failed to create chain", so every refusal looked identical: a cycle, a
      // permission denial, a name clash and a write freeze all rendered as one
      // opaque toast, and the message that would have explained it was thrown
      // away. `request()` puts the server's text on `err.message`.
      //
      // …but ONLY when it is prose. `request()` builds that message from the
      // body's `error` field, and this route's Modification Safeguard refusal
      // puts a machine token there (`error: 'modification_safeguard'`) with the
      // human sentence in `message`, which `request()` drops. Surfacing it
      // unfiltered showed the user a toast reading literally
      // `modification_safeguard` — worse than the generic copy it replaced. A
      // token has no spaces; prose does.
      const raw = err instanceof Error ? err.message : ''
      const reason = raw && /\s/.test(raw) ? raw : null
      toast.error(reason ?? t('driveView.toast.chainFailed'))
    }
  }

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) return
    try {
      const cleanName = newWorkspaceName.trim()
      const folderName = isProjectFolder(cleanName) ? cleanName : projectFolderName(cleanName)
      const displayName = stripProjectFolderExt(cleanName)
      const { folder } = await filesApi.createFolder(currentFolderId, folderName)
      await request('/api/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: folder.id, name: displayName }),
      })
      toast.success(t('driveView.toast.projectCreated', { name: displayName }))
      setNewWorkspaceName('')
      setIsCreatingWorkspace(false)
      refresh()
    } catch {
      toast.error(t('driveView.toast.projectCreateFailed'))
    }
  }

  const handleDelete = async (file: FileItem) => {
    // #801: a folder holding branches the caller can't change is previewed FIRST —
    // refused whole (an app/project root, nothing removable) with an info modal, or
    // confirmed with the preview (which then replaces the plain confirm below) and
    // deleted with the previewed set. Nothing protected: the plain flow, unchanged.
    let expectRemovable: string[] | undefined
    const pointerRow = isPointerRow(file)
    if (file.isFolder && !pointerRow) {
      const g = await partitionGate.gate(file, 'delete', () => filesApi.protectedBranches(file.id, 'delete'))
      if (!g.go) return
      if (g.confirmed) expectRemovable = g.expectRemovable
    }
    // Deleting a live `.shared` frees its target slot → silently promotes a demoted
    // "Conflict" copy to the live share (issue #157 reconcile-on-free), possibly
    // changing access. Fold that into the confirm when it will actually happen.
    let detachNote = ''
    // By KIND: a `.shared`-kind row holds its share slot whatever its name (I110).
    if (pointerKindOf(file as { fileType?: string | null }) === 'shared') {
      try {
        const preview = await filesApi.promotePreview(file.id)
        if (preview.willPromote && preview.promote?.name) {
          detachNote = ' ' + t('driveView.confirm.deleteSharePromote', { promoteName: preview.promote.name.replace(/\.shared$/i, '') })
        }
      } catch { /* advisory — never block the delete on the preview */ }
    }
    const confirmed = expectRemovable !== undefined || await confirm({
      title: t('driveView.confirm.deleteTitle'),
      message: t('driveView.confirm.deleteFile', { name: file.name }) + detachNote,
      type: 'danger',
      confirmText: t('driveView.menu.delete'),
    })
    if (confirmed) {
      // Feedback STARTS with the click, not with the answer (#732). A big
      // folder is queued as a background job and the toast becomes a real
      // bar as it reports its phases (#781); the row's "Deleting…" then comes
      // from the server's marker, for every tab and teammate.
      markDeleting([file.id], true)
      try {
        await deleteItems([{ id: file.id, name: file.name, ...(expectRemovable ? { expectRemovable } : {}) }])
      } finally {
        markDeleting([file.id], false)
      }
    }
  }

  // ─── Contextual toolbar state (2026-07-10 redesign) ────────────────
  // "New project" appears only inside the tenant's Projects container
  // (top-level folder named `Projects` — the PROJECT_REVAMP hierarchy
  // root); project creation otherwise lives on the Projects page. The
  // toolbar-level "New Workspace" button this replaces was unconditional.
  const inProjectsContainer = breadcrumbs.length === 1 && breadcrumbs[0].name.toLowerCase() === 'projects'
  const inMiniAppsContainer = breadcrumbs.length === 1 && isPdAppContainerName(breadcrumbs[0].name)
  // Browsing inside a package (project / mini-app folder)? The toolbar
  // offers the inverse escape hatch — launch the package you're standing
  // in — and the breadcrumb marks where you crossed into package contents.
  // (#327) Match on the INNER name: browsing inside a `.shared` mount, the
  // package crumb is the mount root named `MyApp.miniapp.shared`, so a raw
  // suffix test finds nothing and the "Open App" escape hatch silently
  // disappears exactly where a recipient needs it most.
  const packageCrumb = [...breadcrumbs].reverse().find(cr => {
    const inner = stripPointerSuffix(cr.name)
    return isProjectFolder(inner) || isMiniappFolder(inner)
  }) ?? null
  const packageCrumbIsApp = !!packageCrumb && isMiniappFolder(stripPointerSuffix(packageCrumb.name))
  // A package the person can't open as a project / app — a HALLWAY (#1036), or a project whose
  // config they can't read (#1406) — has nothing to open: they browse it as a plain folder. Asked once
  // per package through the ONE check the open paths use (`packageIsHallwayShell`; the listing's
  // `parentPermission` reports a hallway parent as readable). The RAW crumb name: a `.shared` project
  // mount opens through open-folder's mount path, so it keeps the folder-level rule.
  const [packageCrumbHidden, setPackageCrumbHidden] = useState(false)
  useEffect(() => {
    if (!packageCrumb?.id) { setPackageCrumbHidden(false); return }
    let live = true
    isHallwayPackage({ id: packageCrumb.id, name: packageCrumb.name, isFolder: true })
      .then((shell) => { if (live) setPackageCrumbHidden(shell) })
      .catch(() => { if (live) setPackageCrumbHidden(false) })
    return () => { live = false }
  }, [packageCrumb?.id, packageCrumb?.name])

  return (
    <div
      className="flex-1 min-h-0 themed-bg-primary flex flex-col relative drive-cq"
      onDragEnter={handleContainerDragEnter}
      onDragLeave={handleContainerDragLeave}
      onDragOver={handleContainerDragOver}
      onDrop={handleContainerDrop}
      onDragEnd={handleContainerDragEnd}
    >
      {/* Drop overlay */}
      {canWrite && showDropOverlay && !dropTargetId && !dropTargetCrumb && draggedIds.size === 0 && (
        <div className="absolute inset-0 bg-blue-600/20 border-2 border-dashed border-blue-500 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-zinc-900 px-6 py-4 rounded-lg text-center">
            <Upload size={32} className="mx-auto mb-2 text-blue-400" />
            <p className="text-zinc-200 font-medium">{t('driveView.folder.dropHere')}</p>
          </div>
        </div>
      )}

      {/* Header block — the breadcrumb row and the access line below it share
          ONE bottom border (no divider between them), so a long path never
          crowds the access summary and we don't stack extra border lines. */}
      <div className="border-b themed-border">
      <div className="h-12 flex items-center justify-between px-4">
        <DriveBreadcrumb
          breadcrumbs={breadcrumbs}
          rootLabel={driveTitle}
          onNavigate={handleNavigate}
          dropTargetCrumb={dropTargetCrumb}
          rootDropId={DROP_ROOT}
          onCrumbDragOver={handleCrumbDragOver}
          onCrumbDragLeave={handleCrumbDragLeave}
          onCrumbDrop={handleCrumbDrop}
        />
        <div className="flex items-center gap-2 shrink-0">
          {realtimeStatus === 'error' ? (
            // Give-up is terminal — offer a user-driven reload (fresh provider =
            // fresh reconnect) rather than silently staying dead.
            <button
              onClick={() => window.location.reload()}
              className="p-2 rounded-md text-red-500 hover:text-red-400 cursor-pointer"
              title={t('driveView.realtime.errorAction')}
            >
              <WifiOff size={16} />
            </button>
          ) : (
            <div
              className={`p-2 rounded-md ${
                realtimeStatus === 'connected'
                  ? 'text-green-500'
                  : realtimeStatus === 'connecting'
                  ? 'text-yellow-500'
                  : 'text-zinc-500'
              }`}
              title={t(`driveView.realtime.${realtimeStatus}`)}
            >
              {realtimeStatus === 'connected' ? <Wifi size={16} /> : <WifiOff size={16} />}
            </div>
          )}
          {!searchOpen && (
            <button
              onClick={openSearch}
              className="p-2 rounded-md transition-colors themed-icon hover:themed-icon-active themed-row-hover"
              title={t('driveView.search.open')}
              data-testid="drive-folder-search-toggle"
            >
              <Search size={16} />
            </button>
          )}
          {!searchOpen && clipboard && (
            <button
              onClick={handlePaste}
              className="flex items-center gap-2 px-3 py-1.5 themed-btn-secondary text-sm rounded-md transition-colors"
              title={t('driveView.actions.paste')}
            >
              <Clipboard size={16} />
              {clipboard.tenantId && clipboard.tenantId !== tenant
                ? t('driveView.actions.pasteCopy')
                : clipboard.isCut ? t('driveView.actions.pasteMove') : t('driveView.actions.pasteCopy')}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
          {/* Folder picker — same handler, browser supplies webkitRelativePath
              for each file so processPickedFiles can mkdir-p server-side. */}
          <input
            ref={folderInputRef}
            type="file"
            multiple
            // @ts-expect-error non-standard but widely supported
            webkitdirectory=""
            directory=""
            onChange={handleUpload}
            className="hidden"
          />
          {/* Toolbar (2026-07-10 redesign): New folder → contextual New
              project (Projects container only) → contextual Open app/project
              (inside a package) → Upload LAST as the accent primary. "Chain
              here" and the unconditional "New Workspace" moved off the
              toolbar (empty-area context menu / Projects page). Hidden in
              search mode — the row below owns the search field + Cancel. */}
          {!searchOpen && (<>
          <button
            disabled={!canCreate}
            onClick={() => setIsCreatingFolder(true)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''} themed-btn-secondary`}
          >
            <FolderPlus size={16} />
            {t('driveView.actions.newFolder')}
          </button>
          {inProjectsContainer && (
            <button
              disabled={!canCreate}
              data-testid="drive-action-new-project"
              onClick={() => newProject.open({ onDone: () => refresh() })}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''} themed-btn-secondary`}
            >
              <LayoutGrid size={16} className={!canCreate ? 'opacity-40' : 'text-purple-500'} />
              {t('driveView.actions.newProject')}
            </button>
          )}
          {inMiniAppsContainer && (
            <button
              disabled={!canCreate}
              data-testid="drive-action-new-miniapp"
              onClick={() => newProject.open({ entityKind: 'miniapp', onDone: () => refresh() })}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''} themed-btn-secondary`}
            >
              <PDAppIcon size={16} className={!canCreate ? 'opacity-40' : 'text-fuchsia-500'} />
              {t('driveView.actions.newMiniapp', 'New PDApp')}
            </button>
          )}
          {packageCrumb && !packageCrumbHidden && (
            <button
              data-testid="drive-action-open-package"
              onClick={() => routerNavigate(packageCrumbIsApp
                ? `${urlPrefix}/app/${packageCrumb.id}`
                : `${urlPrefix}/workspace/${packageCrumb.id}`)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors themed-btn-secondary"
              title={packageCrumbIsApp ? t('driveView.actions.openApp') : t('driveView.actions.openProject')}
            >
              {packageCrumbIsApp
                ? <AppWindowIcon size={16} className="text-teal-400" />
                : <LayoutGrid size={16} className="text-purple-500" />}
              {packageCrumbIsApp ? t('driveView.actions.openApp') : t('driveView.actions.openProject')}
            </button>
          )}
          <div ref={uploadMenuRef} className="relative">
            <button
              disabled={!canCreate}
              onClick={() => setShowUploadMenu((v) => !v)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${!canCreate ? 'opacity-50 cursor-not-allowed themed-btn-secondary' : 'themed-btn-primary'}`}
            >
              <Upload size={16} />
              {t('driveView.actions.upload')}
              <ChevronDown size={14} />
            </button>
            {showUploadMenu && (
              <div className="absolute right-0 top-full mt-1 ws-menu z-10 min-w-[180px]">
                <button
                  type="button"
                  onClick={() => { setShowUploadMenu(false); fileInputRef.current?.click() }}
                  className="ws-menu-item"
                >
                  <Upload size={14} />
                  Upload files
                </button>
                <button
                  type="button"
                  onClick={() => { setShowUploadMenu(false); folderInputRef.current?.click() }}
                  className="ws-menu-item"
                >
                  <FolderUp size={14} />
                  Upload folder
                </button>
              </div>
            )}
          </div>
          </>
          )}
        </div>
      </div>
      {/* Manual billing's write freeze, said to the people it affects (§12).
          Until this existed the console could say "Uploads paused" and the
          customer's own app could not — so their first notice was a 402 on an
          upload they had already chosen to make, and the four disabled toolbar
          buttons above had no explanation.

          Below the toolbar rather than above the whole view: it explains those
          buttons, and it must not push the breadcrumb around on every drive a
          frozen tenant opens. Not dismissible — it describes a state that is
          still true after you close it. */}
      {uploadsPaused && (
        <div
          role="status"
          data-testid="drive-uploads-paused"
          className="mx-4 mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2"
        >
          <div className="text-amber-300 text-xs font-medium">
            {t('driveView.frozen.title', { defaultValue: 'New uploads are paused' })}
          </div>
          <p className="themed-text-secondary text-xs mt-0.5">
            {/* Rendered from the CODE, not the server's sentence: this surface has
                i18n and the server's copy is unavoidably English. The wording
                deliberately matches the 402's three facts — what stopped, what
                still works, what fixes it. */}
            {t('driveView.frozen.body', {
              defaultValue:
                'Everything here stays available and you can still edit it — only new files and folders are blocked. An administrator can lift this once payment is settled.',
            })}
          </p>
        </div>
      )}
      {/* In-folder search row — replaces the access line while searching. The
          breadcrumb stays put above; the field is scoped to the current folder
          (widen from the results' scope chip). Cancel (or Esc) exits. */}
      {searchOpen && (
        <div className="h-12 flex items-center gap-2 px-4">
          <Search size={15} className="themed-text-muted shrink-0" />
          <input
            ref={searchInputRef}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') closeSearch() }}
            placeholder={searchScoped
              ? t('driveView.search.placeholder', { name: currentFolderName })
              : t('driveView.search.placeholderEverywhere')}
            data-testid="drive-folder-search-input"
            autoFocus
            className="flex-1 min-w-0 bg-transparent outline-none text-sm themed-text placeholder:themed-text-muted"
          />
          <button
            onClick={closeSearch}
            className="px-3 py-1.5 text-sm rounded-md themed-btn-secondary transition-colors cursor-pointer"
            data-testid="drive-folder-search-cancel"
          >
            {t('common:cancel')}
          </button>
        </div>
      )}
      {/* Access line — its own row under the breadcrumb (so a long path never
          crowds it); borderless — the block wrapper above owns the single
          bottom border. Renders null on personal tenants. Hidden while
          searching — the search row takes this slot. */}
      {effectiveFolderId && !searchOpen && (
        <AccessBar
          folderId={effectiveFolderId}
          isOrg={isOrg}
          onManage={() => setWhoHasAccessFile({ id: effectiveFolderId, name: breadcrumbs[breadcrumbs.length - 1]?.name || driveTitle, isFolder: true } as FileItem)}
          t={t}
        />
      )}
      </div>

      {/* File list header — Name | Access | Modified | Size | sort menu.
          The Type column is gone (icon + muted extension already carry it;
          its sort lives in the SortMenu); Access is the merged owner+people
          facepile. Column visibility keys off the LIST's own width via the
          drive-cq container tiers, not viewport breakpoints. Hidden in search
          mode — the results carry their own columns (location · size · date). */}
      {!searchOpen && (
      <div className="h-10 border-b themed-border flex items-center px-4 text-xs themed-text-muted uppercase tracking-wide select-none">
        <SortHeader field="name" label={t('driveView.header.name')} sortBy={sortBy} sortDir={sortDir} toggleSort={toggleSort} className="flex-1" />
        {/* normal-case: the sort-header BUTTONS get text-transform:none from
            the button reset, so the container's `uppercase` never visually
            applies to them — match, or this one header shouts. */}
        <div className="w-32 drive-hide-t3 flex justify-end shrink-0 normal-case">{t('driveView.header.access')}</div>
        <SortHeader field="modified" label={t('driveView.header.dateModified')} sortBy={sortBy} sortDir={sortDir} toggleSort={toggleSort} className="w-28 justify-end drive-hide-t4" />
        <SortHeader field="size" label={t('driveView.header.size')} sortBy={sortBy} sortDir={sortDir} toggleSort={toggleSort} className="w-20 justify-end drive-hide-t2" />
        <SortMenu sortBy={sortBy} sortDir={sortDir} toggleSort={toggleSort} t={t} />
      </div>
      )}

      {/* New folder input */}
      {isCreatingFolder && (
        <div className="h-12 flex items-center px-4 border-b themed-border themed-bg-secondary">
          <div className="flex items-center gap-3 flex-1">
            <Folder className="text-yellow-500" size={20} />
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder()
                if (e.key === 'Escape') setIsCreatingFolder(false)
              }}
              placeholder={t('driveView.folder.placeholder')}
              className="flex-1 bg-transparent themed-text outline-none"
              autoFocus
            />
          </div>
          <button
            onClick={handleCreateFolder}
            className="px-3 py-1 text-sm themed-btn-primary rounded"
          >
            {t('common:create')}
          </button>
          <button
            onClick={() => setIsCreatingFolder(false)}
            className="px-3 py-1 text-sm themed-text-secondary hover:themed-text"
          >
            {t('common:cancel')}
          </button>
        </div>
      )}

      {/* New project input (inline create row — opened by the contextual
          "New project" button / empty-area menu inside the Projects
          container; creates `<name>.project` + its workspace). */}
      {isCreatingWorkspace && (
        <div className="h-12 flex items-center px-4 border-b themed-border themed-bg-secondary">
          <div className="flex items-center gap-3 flex-1">
            <LayoutGrid className="text-purple-500" size={20} />
            <input
              type="text"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateWorkspace()
                if (e.key === 'Escape') { setIsCreatingWorkspace(false); setNewWorkspaceName('') }
              }}
              placeholder={t('driveView.project.namePlaceholder')}
              className="flex-1 bg-transparent themed-text outline-none"
              autoFocus
            />
          </div>
          <button
            onClick={handleCreateWorkspace}
            className="px-3 py-1 text-sm themed-btn-primary rounded"
          >
            {t('common:create')}
          </button>
          <button
            onClick={() => { setIsCreatingWorkspace(false); setNewWorkspaceName('') }}
            className="px-3 py-1 text-sm themed-text-secondary hover:themed-text"
          >
            {t('common:cancel')}
          </button>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 text-red-400 text-sm">
          {t('common:error')}: {error}
        </div>
      )}

      {/* File list */}
      <div
        ref={fileListRef}
        data-testid="drive-file-list"
        className="flex-1 overflow-auto"
        onContextMenu={(e) => {
          // Only trigger on the container itself, not on file rows
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-empty-area]')) {
            e.preventDefault()
            setSelectedIds(new Set())
            const el = e.currentTarget as HTMLElement
            const r = el.getBoundingClientRect()
            setContextMenu({ x: e.clientX, y: e.clientY, anchorEl: el, offX: e.clientX - r.left, offY: e.clientY - r.top })
          }
        }}
      >
        {searchOpen ? (
          <div className="px-4 py-4">
            <SearchResults
              q={debouncedSearch}
              rootId={searchScoped ? effectiveFolderId : null}
              scope={{ folderName: currentFolderName, scoped: searchScoped, onToggle: () => setSearchScoped((s) => !s) }}
              onOpen={openSearchResult}
            />
          </div>
        ) : loading && files.length === 0 ? (
          <div data-empty-area className="p-4 text-zinc-500 text-sm">{t('driveView.status.loading')}</div>
        ) : files.length === 0 && parentNeedsApproval ? (
          // D24: this folder is a `.chain` that Needs approval (a deep link / bookmark to it) —
          // nobody follows it, so its empty listing is not an empty folder. Say why.
          <div data-empty-area data-testid="drive-chain-needs-approval" className="p-6 flex items-start gap-3 max-w-lg">
            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-400" />
            <div className="min-w-0">
              <div className="text-sm font-semibold themed-text">{t('driveView.chain.needsApprovalTitle')}</div>
              <p className="mt-1 text-xs leading-relaxed themed-text-muted">{t('driveView.chain.needsApprovalBody')}</p>
            </div>
          </div>
        ) : files.length === 0 ? (
          <div data-empty-area className="p-4 text-zinc-500 text-sm">{t('driveView.status.empty')}</div>
        ) : (
          <>
            {files.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                structural={rowStructural(file)}
                deleting={deletingIds.has(file.id) || file.deleting === true}
                selected={selectedIds.has(file.id)}
                selectedFiles={selectedFiles}
                onSelect={handleSelect}
                onOpen={handleFileOpen}
                onOpenInNewTab={handleOpenInNewTab}
                onJumpToTarget={handleJumpToChainTarget}
                onApprovePointer={setApprovingPointer}
                onApproveCode={handleApproveCode}
                onOpenWorkspace={handleOpenWorkspace}
                onOpenWorkspaceInNewTab={handleOpenWorkspaceInNewTab}
                onOpenOrgWorkspace={isOrg ? (f) => handleOpenWorkspace(f, 'organization') : undefined}
                onExplore={handleExplore}
                onExploreInNewTab={handleExploreInNewTab}
                onOpenAsApp={handleOpenAsApp}
                isOrg={isOrg}
                onCopy={handleCopy}
                onCut={handleCut}
                onWhoHasAccess={rowCanManageAccess(file) ? setWhoHasAccessFile : undefined}
                // Internal grants are a manage-access action — same gate as
                // Who-has-access; org drives only (no roles/teams in personal).
                onShareInternally={isOrg && rowCanManageAccess(file) ? setShareInternallyFile : undefined}
                onShare={rowCanShare(file) && !structuralFolderBlocksShare(rowStructural(file)) ? handleShareFile : undefined}
                onShareLink={rowCanShareLink(file) ? handleShareLink : undefined}
                onShareMiniApp={miniAppShare.openMiniAppShare}
                folderBreadcrumbs={breadcrumbs}
                onDownload={handleDownload}
                onExtract={handleExtract}
                onDelete={handleDelete}
                onShowInfo={setInfoFile}
                onDebugLog={setDebugLogFile}
                onEditIcon={setIconEditorFile}
                onRename={handleRename}
                onRenameSubmit={handleRenameSubmit}
                onRenameCancel={handleRenameCancel}
                isRenaming={renamingFileId === file.id}
                isDropTarget={dropTargetId === file.id}
                isForbidden={forbiddenDropId === file.id}
                isDragging={draggedIds.has(file.id)}
                isCut={clipboard?.isCut && clipboard.items?.some(item => item.id === file.id) || false}
                onDragStart={handleRowDragStart}
                onDragOver={handleRowDragOver}
                onDragLeave={handleRowDragLeave}
                onDrop={handleRowDrop}
                onContextMenu={handleContextMenu}
                canWrite={canWrite}
                canCopy={canCopy}
                wsAccess={wsAccess}
                tenantId={tenant}
                t={t}
              />
            ))}
            {hasMore && (
              <div className="flex items-center justify-center py-3 border-t themed-border">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-4 py-1.5 text-sm themed-text-muted hover:themed-text transition-colors"
                >
                  {loadingMore ? (
                    <><Loader2 size={14} className="animate-spin" /> Loading...</>
                  ) : (
                    <>Load more ({files.length} of {total})</>
                  )}
                </button>
              </div>
            )}
            {/* (#754) A trailing background strip, so the empty-area menu
                (New Folder, Paste, Chain here…) stays reachable once the rows
                fill the scroll area and no bare container is left to click. */}
            <div data-empty-area data-testid="drive-list-trailing-space" className="h-16" aria-hidden="true" />
          </>
        )}
      </div>

      {/* Right-click context menu — portaled to <body> so the .drive-cq
          container (container-type: inline-size makes it the containing block
          for position:fixed) and the file-list scroll overflow can't clip it. */}
      {contextMenu && createPortal((
        <div
          ref={contextMenuRef}
          data-testid="file-context-menu"
          // Same reason as the row ⋯ menu: the hook measures this element, so
          // gating the render on `ctxPos` would mean it never mounts.
          style={ctxPos
            ? {
                position: 'fixed', left: ctxPos.left, top: ctxPos.top,
                ...(ctxOrigin ? { transformOrigin: `${ctxOrigin.x}px ${ctxOrigin.y}px` } : {}),
              }
            : { position: 'fixed', left: contextMenu.x, top: contextMenu.y, visibility: 'hidden' }}
          className={`${contextMenuThemeClass} ws-menu min-w-[160px] z-[100]${ctxExiting ? ' anchor-exit' : ''}`}
        >
          {contextMenu.file ? (
            <FileContextMenu
              file={contextMenu.file}
              onClose={() => setContextMenu(null)}
              onOpenInNewTab={handleOpenInNewTab}
              onOpenWorkspace={handleOpenWorkspace}
              onOpenWorkspaceInNewTab={handleOpenWorkspaceInNewTab}
              onOpenOrgWorkspace={isOrg ? (f) => handleOpenWorkspace(f, 'organization') : undefined}
              onExplore={handleExplore}
              onExploreInNewTab={handleExploreInNewTab}
              onOpenAsApp={handleOpenAsApp}
              onDownload={handleDownload}
              onExtract={handleExtract}
              onCopy={handleCopy}
              onCut={handleCut}
              onWhoHasAccess={rowCanManageAccess(contextMenu.file) ? setWhoHasAccessFile : undefined}
              onShareInternally={isOrg && rowCanManageAccess(contextMenu.file) ? setShareInternallyFile : undefined}
              onShare={rowCanShare(contextMenu.file) && !structuralFolderBlocksShare(rowStructural(contextMenu.file)) ? handleShareFile : undefined}
              onShareLink={rowCanShareLink(contextMenu.file) ? handleShareLink : undefined}
              onShareMiniApp={miniAppShare.openMiniAppShare}
              folderBreadcrumbs={breadcrumbs}
              onRename={handleRename}
              onShowInfo={setInfoFile}
              onDebugLog={setDebugLogFile}
              onEditIcon={setIconEditorFile}
              onDelete={handleDelete}
              isOrg={isOrg}
              wsAccess={wsAccess}
              canWrite={canWrite}
              canCopy={canCopy}
              urlPrefix={urlPrefix}
              t={t}
            />
          ) : (
            <EmptyAreaContextMenu
              hasClipboard={!!clipboard?.items?.length}
              onClose={() => setContextMenu(null)}
              onOpenWorkspace={() => {
                const folderName = !urlFolderId
                  ? rootFolderName
                  : (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : '')
                handleOpenWorkspace({ id: effectiveFolderId!, name: folderName, isFolder: true } as FileItem)
              }}
              onOpenOrgWorkspace={isOrg ? () => {
                const folderName = !urlFolderId
                  ? rootFolderName
                  : (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : '')
                handleOpenWorkspace({ id: effectiveFolderId!, name: folderName, isFolder: true } as FileItem, 'organization')
              } : undefined}
              onNewFolder={() => setIsCreatingFolder(true)}
              onNewProject={inProjectsContainer ? () => newProject.open({ onDone: () => refresh() }) : undefined}
              onChainHere={FilePickerDialog && canWrite ? () => setIsPickingChainTarget(true) : undefined}
              onPaste={handlePaste}
              onDownload={effectiveFolderId ? () => handleDownload({ id: effectiveFolderId, name: '', isFolder: true } as FileItem) : undefined}
              onOpenInNewTab={effectiveFolderId ? () => {
                const prefix = !isOrg ? '/~' : `/t/${tenant}`
                window.open(`${prefix}/folder/${effectiveFolderId}`, '_blank')
              } : undefined}
              // Not org-gated: on a personal drive Who-has-access is where outside
              // drives, PDApp access and share links show — same as the row menu.
              // Share internally stays org-only (no roles/teams on a personal drive).
              // `canManageAccess` is unconditionally true on a personal drive, so
              // there the gate is the folder you stand in, from the listing —
              // the same rule `rowCanManageAccess` applies to that folder's row.
              // Inside a received `.shared` the mount listing reports the capped
              // grant (≤ readwrite), so the verb hides there as it does on the row.
              onWhoHasAccess={(isOrg ? canManageAccess : canManageAccessFor(parentPermission ?? 'owner')) && effectiveFolderId ? () => setWhoHasAccessFile(currentFolderItem()) : undefined}
              onShareInternally={canManageAccess && isOrg && effectiveFolderId ? () => setShareInternallyFile(currentFolderItem()) : undefined}
              onShare={effectiveFolderId ? () => setShareFile(currentFolderItem()) : undefined}
              onShareLink={effectiveFolderId ? () => handleShareLink(currentFolderItem()) : undefined}
              reshareBlocked={parentReshareBlocked}
              isAppEligible={effectiveFolderId ? shareKindForFile({ name: breadcrumbs[breadcrumbs.length - 1]?.name || driveTitle, isFolder: true }) === 'app' : false}
              isOrg={isOrg}
              canWrite={canWrite}
              canCopy={canCopy}
              wsAccess={wsAccess}
              onShowInfo={effectiveFolderId ? () => setInfoFile({ id: effectiveFolderId, name: breadcrumbs[breadcrumbs.length - 1]?.name || rootFolderName, isFolder: true } as FileItem) : undefined}
              t={t}
            />
          )}
        </div>
      ), document.body)}

      {/* Status bar */}
      <div className="h-8 border-t themed-border flex items-center px-4 text-xs themed-text-muted">
        <span>{t('driveView.status.items', { count: files.length })}</span>
        {clipboard?.items && (
          <>
            <span className="mx-2">•</span>
            <span className="text-yellow-400">
              {clipboard.isCut
                ? t('driveView.status.cutCount', { count: clipboard.items.length })
                : t('driveView.status.copied', { count: clipboard.items.length })}
            </span>
            <button
              onClick={() => setClipboard(null)}
              className="ml-1 themed-text-muted hover:themed-text"
            >
              ✕
            </button>
          </>
        )}
        {selectedIds.size > 0 && (
          <>
            <span className="mx-2">•</span>
            <span className="text-blue-400">{t('driveView.status.selectedCount', { count: selectedIds.size })}</span>
            <button
              data-testid="drive-bulk-download"
              onClick={handleDownloadSelected}
              className="ml-3 text-blue-400 hover:text-blue-300"
            >
              {/* A single pointer can produce either a zip or the file
                  itself, so it takes the bare label for the same reason the
                  context-menu item does — the bar must not promise a shape the
                  dialog then contradicts. */}
              {selectedIds.size === 1 && isPointerRow(selectedFiles[0])
                ? t('driveView.menu.download')
                : selectedIds.size > 1 || selectedFiles.some(f => f.isFolder)
                  ? t('driveView.status.downloadAsZip')
                  : t('driveView.menu.download')}
            </button>
            <button
              onClick={handleDeleteSelected}
              className="ml-3 text-red-400 hover:text-red-300"
            >
              {t('common:delete')}
            </button>
          </>
        )}
      </div>

      {/* Share — cross-tenant share flow (step 9d). Files and folders.
          Rendered BEFORE Who-has-access so that, when Share opens it, the
          Who-has-access dialog layers on top (same z-index, later in DOM)
          and Share stays open behind it. */}
      {shareFile && (
        <ShareDialog
          folderId={shareFile.id}
          folderName={shareFile.name}
          isFolder={shareFile.isFolder}
          folderPath={folderPathFor(shareFile)}
          // POLICY: resharing a `.shared` (inbound content the user doesn't own)
          // can only grant READ-ONLY, regardless of the level they hold — only the
          // source owner sets read-write. So cap the picker at readcopy for a
          // `.shared` row (matches the server's reshare clamp). A native folder
          // share keeps the owner's real ceiling.
          // By KIND, as the server's reshare clamp (I110).
          myPermission={pointerKindOf(shareFile) === 'shared' ? 'readcopy' : shareFile.effectivePermission}
          canManage={rowCanManageAccess(shareFile)}
          onOpenWhoHasAccess={() => setWhoHasAccessFile(shareFile)}
          refreshKey={shareReloadKey}
          onClose={() => setShareFile(null)}
        />
      )}
      {/* Reverse-share (step 7) — same ShareDialog, pre-targeted at the owner
          org and operating on the dragged item's source content. The dialog's
          own `initiate` call (silent re-share, since the orgs are already
          connected) materializes the canonical `.shared` on the owner's side. */}
      {reverseShare && (
        <ShareDialog
          folderId={reverseShare.sourceFileId}
          folderName={reverseShare.sourceName}
          isFolder={reverseShare.sourceIsFolder}
          initialRecipientTenantId={reverseShare.recipientTenantId}
          onClose={() => { setReverseShare(null); refresh() }}
        />
      )}
      {/* Chain-escape prompt (owner-side) — moving a `.chain` (or a folder of
          them) into a shared folder where the target escapes the share. Honest:
          the chain stays greyed for recipients either way. Both options apply the
          move (also confirming the access change — moving into a shared folder
          changes access). "Share the source folders too" then OPENS the Share
          dialog pre-loaded with every distinct target + the recipient, so the
          owner reviews and shares; "Move without access" just applies the move. */}
      {linkedCopyAsk && (
        <FolderLinkedDownloadDialog
          folderName={linkedCopyAsk.label}
          themeClass={contextMenuThemeClass}
          t={t}
          action="copy"
          onCancel={() => { linkedCopyAsk.resolve(null); setLinkedCopyAsk(null) }}
          onConfirm={(choice) => { linkedCopyAsk.resolve(choice); setLinkedCopyAsk(null) }}
        />
      )}
      {linkedDownload && (
        <FolderLinkedDownloadDialog
          folderName={linkedDownload.label ?? linkedDownload.folder.name}
          folderCount={linkedDownload.fileIds.length}
          themeClass={contextMenuThemeClass}
          t={t}
          onCancel={() => setLinkedDownload(null)}
          onConfirm={async (choice) => {
            const { folder, fileIds } = linkedDownload
            setLinkedDownload(null)
            const single = fileIds.length === 1
            // ONE archive either way. The CHOICE decides what the pointers
            // inside it become: their own `.shared`/`.chain` files (small,
            // re-uploadable, still pointers), or ordinary folders holding what
            // they point at. It used to be one download PER pointer, which made
            // the save picker open N times and put the folder's own contents in
            // a different file from the contents it links to.
            try {
              // The server names a multi-id archive `files (N items).zip`, so
              // only the single-folder case can promise a name here — offering
              // the first folder's for a selection would preview a file the
              // user never receives.
              await filesApi.download(fileIds, {
                containsFolder: true,
                suggestedName: single ? `${folder.name}.zip` : undefined,
                expandPointers: choice === 'contents',
              })
            } catch (err) {
              toast.error((err as { message?: string })?.message || t('driveView.toast.downloadFailed'))
            }
          }}
        />
      )}
      {pointerDownload && (
        <PointerDownloadDialog
          files={pointerDownload}
          themeClass={contextMenuThemeClass}
          t={t}
          onCancel={() => setPointerDownload(null)}
          onConfirm={async (choice) => {
            const files = pointerDownload
            const plan = planPointerDownload(files)
            setPointerDownload(null)
            // A single pointer asked for its own file is the one case that is
            // not a zip at all: `metadata_json` under the pointer's own name,
            // which is what re-uploads and re-aligns.
            if (files.length === 1 && choice === 'pointer' && plan.live.length === 1) {
              await handleDownloadMetadata(plan.live[0])
              return
            }
            // Everything else is ONE request. The server zips the selection and
            // `expandPointers` decides whether a pointer inside it rides as its
            // own file or as an ordinary folder holding the source.
            try {
              await filesApi.download(files.map((f) => f.id), {
                containsFolder: true,
                // The name the dialog PREVIEWED. Omitted, the picker offers
                // `download-<ts>.zip` while the dialog promised something else
                // — the preview/actual divergence the shared plan exists to
                // prevent. A single row is named by the server instead.
                suggestedName: plan.archiveName ?? plan.producesFor(files[0]),
                expandPointers: choice === 'contents',
              })
            } catch (err) {
              toast.error((err as { message?: string })?.message || t('driveView.toast.downloadFailed'))
            }
          }}
        />
      )}
      {chainEscape && (
        <ChainEscapeDialog
          escape={chainEscape.escape}
          onCancel={() => setChainEscape(null)}
          onMoveAnyway={async () => {
            const m = chainEscape
            // A big folder is queued (202): the dialog closes then and the progress toast carries the
            // move — never a dialog held busy for the job's minutes. Only THIS dialog is closed.
            const close = () => setChainEscape((cur) => (cur === m ? null : cur))
            try {
              // confirmedAccessChange too: moving into a shared folder also trips
              // the access-diff guard. The user already saw "Shared with …" in this
              // dialog, so the access change is implied — auto-confirm it.
              const at = captureUndoContext()
              const r = await withDialogMoveProgress(toast, t('driveView.toast.moving', { name: m.name }), close, (hooks) => filesApi.moveFile(m.fileId, chainEscapeRecallUpdate(m), hooks))
              afterChainEscapeMove(at, m, r)
            } catch (err: any) {
              toast.error(moveErrorText(err, t, m.name))
            }
            close()
            refresh()
          }}
          onShareTarget={async () => {
            const m = chainEscape
            const close = () => setChainEscape((cur) => (cur === m ? null : cur))
            // Queued: the dialog closed and the person carried on, so the Share dialog is OFFERED when the move is
            // done (a toast action), never popped up minutes later over whatever they are doing now.
            let queued = false
            const closeQueued = () => { queued = true; close() }
            const { recipients, escapingChains } = m.escape
            // Dedup the chains' targets by folder id — share each once.
            const targets = Array.from(new Map(escapingChains.map((c) => [c.targetId, c])).values())
            try {
              const at = captureUndoContext()
              const r = await withDialogMoveProgress(toast, t('driveView.toast.moving', { name: m.name }), closeQueued, (hooks) => filesApi.moveFile(m.fileId, chainEscapeRecallUpdate(m), hooks))
              afterChainEscapeMove(at, m, r)
            } catch (err: any) {
              toast.error(moveErrorText(err, t, m.name))
              close(); refresh(); return
            }
            // Open the Share dialog pre-loaded with every distinct target (the
            // first as primary, the rest via alsoShare) + the recipient seeded, so
            // the owner reviews recipients/access and clicks Share. The move above
            // already applied.
            const first = targets[0]
            if (first && recipients[0]) {
              const target = {
                primaryId: first.targetId,
                primaryName: first.targetName,
                primaryIsFolder: first.targetIsFolder,
                alsoShare: targets.slice(1).map((tg) => ({ id: tg.targetId, name: tg.targetName, isFolder: tg.targetIsFolder })),
                recipientTenantId: recipients[0].tenantId,
              }
              if (queued) {
                // never replace a Share dialog the person opened meanwhile
                toast.success(t('driveView.toast.movedShareTargetReady', { name: m.name }), undefined, {
                  label: t('driveView.toast.shareTargetAction'),
                  onClick: () => setShareTarget((cur) => cur ?? target),
                })
              } else {
                setShareTarget(target)
              }
            }
            close()
            refresh()
          }}
        />
      )}
      {shareTarget && (
        <ShareDialog
          folderId={shareTarget.primaryId}
          folderName={shareTarget.primaryName}
          isFolder={shareTarget.primaryIsFolder}
          alsoShare={shareTarget.alsoShare}
          initialRecipientTenantId={shareTarget.recipientTenantId}
          onClose={() => { setShareTarget(null); refresh() }}
        />
      )}
      {/* ppn.gg share-link management dialog — unified per Decisions 5 + 2:
          mode (App vs Public) and live/snapshot are picked inside. App
          eligibility is computed from the file's name + isFolder so the
          dialog can disable the App pick for ineligible files. */}
      {shareLinksDialog && (
        <ShareLinksDialog
          fileId={shareLinksDialog.file.id}
          fileName={shareLinksDialog.file.name}
          filePath={`/papan/${breadcrumbs.map(b => b.name).join('/')}${breadcrumbs.length ? '/' : ''}${shareLinksDialog.file.name}`}
          isFolder={shareLinksDialog.file.isFolder}
          isAppEligible={shareKindForFile({ name: shareLinksDialog.file.name, isFolder: shareLinksDialog.file.isFolder }) === 'app'}
          onClose={clearShareLinksDialog}
        />
      )}
      {/* "Share PDApp" picker + folder-targeted share dialog (owned by the hook). */}
      {miniAppShare.node}
      {/* Who has access — unified dialog (issue #22). Org-only for v1. */}
      {shareInternallyFile && (
        <ShareInternallyDialog
          fileId={shareInternallyFile.id}
          fileName={shareInternallyFile.name}
          tenantId={tenant}
          isOrg={isOrg}
          isFolder={shareInternallyFile.isFolder}
          myPermission={shareInternallyFile.effectivePermission}
          onClose={() => { clearShareInternally(); refresh() }}
        />
      )}

      {whoHasAccessFile && (
        <WhoHasAccessDialog
          folderId={whoHasAccessFile.id}
          folderName={whoHasAccessFile.name}
          folderPath={folderPathFor(whoHasAccessFile)}
          tenantId={tenant}
          isFolder={whoHasAccessFile.isFolder}
          myPermission={whoHasAccessFile.effectivePermission}
          // Personal drives have no roles/teams — hide that tab and default to
          // Outsiders, matching the project cards, the Shared view, and the
          // app-shell / workspace-tab menus (#437). Without this the row menu
          // was the one surface still showing an empty People & teams tier on a
          // personal drive.
          hidePeopleTeams={!isOrg}
          // True for both the mount artifact (.shared) AND any file
          // inside it — the source cap (e.g. readcopy) is the
          // permission ceiling for the whole subtree, so the warning
          // belongs on every dialog inside the mount, not just the
          // root. Server sets `sharedFromName` everywhere inside.
          isReceivedShare={isSharedPointer(whoHasAccessFile.name) || !!whoHasAccessFile.sharedFromName}
          // Any row inside a cross-tenant share context needs the
          // Manage & Edit tier exposed, regardless of folder/file:
          //   - the mount artifact itself (folder-target or
          //     file-target `.shared`)
          //   - any descendant file inside the mount (TAP admins can
          //     set per-child overlay grants up to admin)
          // Folders inside the mount already get admin via
          // `isFolder=true`; this widening only matters for files.
          inShareContext={pointerKindOf(whoHasAccessFile) === 'shared' || !!whoHasAccessFile.sharedFromName}
          // Reload the (still-open) Share dialog so a re-sharing / permission
          // change made here reflects instantly when this dialog closes.
          onClose={() => { setWhoHasAccessFile(null); setShareReloadKey(k => k + 1) }}
        />
      )}
      {infoFile && (
        <FileInfoDialog file={infoFile} onClose={() => setInfoFile(null)} />
      )}
      {pendingPointerDialogs}
      {debugLogFile && (
        <DebugLogViewer file={debugLogFile} onClose={() => setDebugLogFile(null)} />
      )}
      {iconEditorFile && (
        <MiniappIconEditorDialog file={iconEditorFile} tenantId={tenant} onClose={() => setIconEditorFile(null)} />
      )}
      {/* Chain-target picker. Mounted only while open so the file-or-folder
          drill-down resets on each invocation. */}
      {isPickingChainTarget && FilePickerDialog && (
        <FilePickerDialog
          tenantId={tenant}
          onSelect={handleChainTargetPicked}
          onClose={() => setIsPickingChainTarget(false)}
          title={t('driveView.chain.pickTitle')}
        />
      )}
    </div>
  )
}

// FileInfoDialog ("Details") lives in ./FileInfoDialog — shared with the
// Projects/MiniApps card context menu. Imported at the top of this file.
